/* INTEZ TELEPHONY EVENT FIX + DIAGNOSTICS POLISH V15.6.3 */
/* INTEZ TELEPHONY ADMIN DASHBOARD V15.6.2 */
import {
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  UIEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  Archive,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Bell,
  BookOpen,
  Building2,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  CircleDollarSign,
  Download,
  ExternalLink,
  Clock3,
  Columns3,
  FileText,
  Filter,
  GripVertical,
  HardDrive,
  Headphones,
  Inbox,
  Link2,
  List,
  ListChecks,
  LockKeyhole,
  LogOut,
  Mail,
  LayoutDashboard,
  MessageSquare,
  MoreHorizontal,
  Moon,
  Paperclip,
  PencilLine,
  Phone,
  Plus,
  QrCode,
  Search,
  RefreshCw,
  RotateCw,
  Smile,
  Save,
  Unlock,
  Ban,
  KeyRound,
  Network,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  Tag,
  Ticket as TicketIcon,
  Timer,
  Trash2,
  UserRound,
  UserPlus,
  Users,
  X,
  Zap,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { api, formatBytes, uploadFormData } from './api';
import { useAuth } from './auth';
import { useTheme } from './theme';
import { CallCard, SipPhoneProvider, TicketCallTimelineCard, type BrowserCall } from './telephony';
import type { UiShellVersion } from './app/runtime-config';
import { PortalRoutes, type PortalScreens } from './app/routes';
import { getVisibleNavigation, type PortalRole } from './app/navigation';
import { QueueRoutingAdmin } from './features/administration/QueueRoutingAdmin';
import { primaryUserRole, userHasAnyRole, userHasRole, userRoles } from './roles';
import type {
  ActiveDirectorySettings,
  Assignee,
  Checklist,
  ChecklistItem,
  Company,
  CompanyContact,
  CompanyStats,
  ContactChannel,
  ContactStats,
  EmailDeliveryAdmin,
  EmailNotificationPreferences,
  EmailNotificationSettings,
  FileRow,
  GlobalSearchItem,
  GlobalSearchResponse,
  GlobalSearchType,
  MailClientSettings,
  MailStatus,
  MaxIntegrationSettings,
  NotificationRow,
  PlannedWork,
  PlannedWorkChecklistDraft,
  PlannedWorkChecklistItem,
  PlannedWorkEvent,
  PlannedWorkStats,
  Queue,
  RecurringRule,
  SecurityOverview,
  TelegramIntegrationSettings,
  TelephonyCall,
  TelephonyDialTarget,
  TelephonyStatus,
  Template,
  Ticket,
  TimelineEvent,
  UserRow,
} from './app/legacy-types';
import { PageHeading } from './components/ui/PageHeading';
import { AppShell } from './layout/AppShell';
import { Sidebar } from './layout/Sidebar';
import { BackupAdminPanel } from './BackupAdminPanel';
import { ClientInteractionHistory } from './client-history/ClientInteractionHistory';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { ContactCenterPage } from './features/contact-center/ContactCenterPage';
import { DirectorySection } from './features/directories/DirectorySection';
import { CompanyActivityPanel, type CompanyActivityTab } from './features/directories/CompanyActivityPanel';
import { CompanyDetailWorkspace } from './features/directories/CompanyDetailWorkspace';
import { ContactDetailWorkspace } from './features/directories/ContactDetailWorkspace';
import { TemplateDetailWorkspace } from './features/templates/TemplateDetailWorkspace';
import { CallsPage } from './features/calls/CallsPage';
import { TelephonyAdministration } from './features/administration/TelephonyAdministration';
import { Bitrix24Administration } from './features/administration/Bitrix24Administration';
import { MonitoringPage } from './features/monitoring/MonitoringPage';
import { WorklogsPanel } from './features/worklogs/WorklogsPanel';
import { defaultWorkCategory, type WorkCategory } from './features/worklogs/worklog-model';
import { BillingPage } from './features/billing/BillingPage';
import { FieldVisitsPage } from './features/visits/FieldVisitsPage';
import { DirectoryAvatar, DirectorySyncBadge } from './features/directories/DirectorySyncBadge';
import { paginateTicketList, type TicketPagination } from './features/tickets/ticket-pagination';
import { TicketColumnResizeHandle, TicketColumnSettingsPanel } from './features/tickets/TicketTableControls';
import { NewTicket as SharedNewTicket } from './features/tickets/NewTicket';
import { ClosedTicketControls, canChangeClosedTicketStatus } from './features/tickets/ClosedTicketControls';
import {
  DEFAULT_TICKET_TABLE_SETTINGS,
  TICKET_TABLE_COLUMNS,
  TICKET_TABLE_SETTINGS_STORAGE_KEY,
  normalizeTicketTableSettings,
  setTicketColumnVisibility,
  setTicketColumnWidth,
  ticketGridTemplate,
  type TicketColumnId,
  type TicketTableSettings,
} from './features/tickets/ticket-table-settings';
import { DirectoryChangeHistory, DirectoryCustomFields, DirectoryDuplicates } from './features/directories/DirectoryEntityDetails';
import { EMPLOYEE_ROLES, UserRolesEditor } from './features/administration/UserRolesEditor';
import { ProfileDialog, UserAvatar } from './features/profile/ProfileDialog';

const APP_VERSION = import.meta.env.VITE_APP_VERSION || '0.8.0-beta.9';

const STATUS_OPTIONS = [
  { value: 'NEW', label: 'Новая' },
  { value: 'DISTRIBUTION', label: 'На распределении' },
  { value: 'IN_PROGRESS', label: 'В работе' },
  { value: 'WAITING_CLIENT', label: 'Ожидает клиента' },
  { value: 'WAITING_APPROVAL', label: 'Ожидает согласования' },
  { value: 'SUSPENDED', label: 'Приостановлена' },
  { value: 'RESOLVED', label: 'Решена' },
  { value: 'CLOSED', label: 'Закрыта' },
] as const;

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Низкий' },
  { value: 'NORMAL', label: 'Обычный' },
  { value: 'HIGH', label: 'Высокий' },
  { value: 'CRITICAL', label: 'Критический' },
] as const;

const KANBAN_COLUMN_BATCH_SIZE = 10;

function statusName(value: string) {
  return STATUS_OPTIONS.find(item => item.value === value)?.label ?? value;
}

function priorityName(value: string) {
  return PRIORITY_OPTIONS.find(item => item.value === value)?.label ?? value;
}

function channelLabel(value?: string | null) {
  const normalized = String(value || '').trim().toUpperCase();
  if (normalized === 'TELEGRAM' || normalized.includes('TELEGRAM')) return 'Telegram';
  if (normalized === 'MAX' || normalized.includes('MAX')) return 'MAX';
  if (normalized === 'EMAIL' || normalized.includes('MAIL')) return 'Email';
  if (normalized === 'PHONE' || normalized.includes('TELEPHONY')) return 'Телефония';
  if (normalized === 'PORTAL' || !normalized) return 'Портал';
  return String(value || 'Портал');
}

function roleName(value: string) {
  return EMPLOYEE_ROLES.find(item => item.value === value)?.label ?? (value === 'CLIENT' ? 'Клиент' : value);
}


function authSourceName(value: string) {
  return ({ LOCAL: 'Локальный', ACTIVE_DIRECTORY: 'Active Directory (LDAP)', HYBRID: 'Локальный + LDAP' } as Record<string, string>)[value] ?? value;
}

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatShort(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelativeTime(value?: string | null, now = Date.now()) {
  if (!value) return '—';
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return '—';
  const elapsed = Math.max(0, now - timestamp);
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return 'сейчас';
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;

  const date = new Date(timestamp);
  const current = new Date(now);
  const yesterday = new Date(current.getFullYear(), current.getMonth(), current.getDate() - 1);
  if (date >= yesterday && date < new Date(current.getFullYear(), current.getMonth(), current.getDate())) {
    return `вчера, ${date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
  }
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() !== current.getFullYear() ? { year: 'numeric' as const } : {}),
    hour: '2-digit',
    minute: '2-digit',
  });
}

function initials(name?: string | null) {
  if (!name) return 'IG';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
}

function highlightSearchText(text: string, query: string) {
  const cleaned = query
    .replace(/(^|\s)[a-zа-я_]+:(?:"[^"]+"|\S+)/giu, ' ')
    .replace(/(^|\s)-\S+/g, ' ')
    .replace(/["']/g, ' ')
    .trim();
  const terms = Array.from(new Set(cleaned.split(/\s+/).filter(term => term.length >= 2))).slice(0, 8);
  if (!terms.length) return text;
  const escaped = terms.map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'giu');
  const termSet = new Set(terms.map(term => term.toLocaleLowerCase('ru-RU')));
  return text.split(pattern).map((part, index) => termSet.has(part.toLocaleLowerCase('ru-RU'))
    ? <mark key={`${part}-${index}`}>{part}</mark>
    : part);
}

function timeUntil(value?: string | null) {
  if (!value) return { label: 'Не задано', tone: 'neutral', percent: 0 };
  const ms = new Date(value).getTime() - Date.now();
  if (ms <= 0) return { label: 'Срок нарушен', tone: 'danger', percent: 100 };
  const hours = Math.ceil(ms / 3600_000);
  if (hours <= 4) return { label: `${hours} ч`, tone: 'warning', percent: 88 };
  if (hours < 48) return { label: `${hours} ч`, tone: 'ok', percent: 55 };
  return { label: `${Math.ceil(hours / 24)} дн`, tone: 'ok', percent: 28 };
}

function eventName(value: string) {
  return ({
    TICKET_CREATED: 'Заявка создана',
    STATUS_CHANGED: 'Изменён статус',
    PUBLIC_COMMENT: 'Ответ клиенту',
    INTERNAL_COMMENT: 'Внутренняя заметка',
    TICKET_UPDATED: 'Обновлены параметры',
    ASSIGNEE_ADDED: 'Назначен исполнитель',
    ASSIGNEE_REMOVED: 'Изменены исполнители',
    SUBTASK_CREATED: 'Создана подзадача',
    SUBTASK_UPDATED: 'Обновлена подзадача',
    CHECKLIST_CREATED: 'Добавлен чек-лист',
    CHECKLIST_ITEM_CREATED: 'Добавлен пункт чек-листа',
    CHECKLIST_ITEM_UPDATED: 'Изменён чек-лист',
    WORKLOG_ADDED: 'Добавлено время',
    WORKLOG_DELETED: 'Удалена запись времени',
  } as Record<string, string>)[value] ?? value;
}

function money(value: number) {
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(value || 0);
}

function minutesLabel(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} мин`;
  return `${hours} ч${rest ? ` ${rest} мин` : ''}`;
}

function Login() {
  const { user, login, loading, notice } = useAuth();
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  if (loading) return <div className="screen-loader"><span /></div>;
  if (user) return <Navigate to="/" replace />;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError('');
    const form = new FormData(event.currentTarget);
    const identity = String(form.get('identity') ?? '').trim();
    const password = String(form.get('password') ?? '');
    try {
      await login(identity, password);
      navigate('/');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось войти');
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="login-v077-page">
      <section className="login-v077-visual" aria-label="Каналы технической поддержки">
        <nav className="login-v077-contact-hotspots" aria-label="Каналы технической поддержки">
          <a className="login-v077-hotspot login-v077-hotspot-email" href="mailto:help@intezgroup.ru" aria-label="Email: help@intezgroup.ru" />
          <a className="login-v077-hotspot login-v077-hotspot-telegram" href="https://t.me/intezgroup_help" target="_blank" rel="noopener noreferrer" aria-label="Telegram: @intezgroup_help" />
          <a className="login-v077-hotspot login-v077-hotspot-max" href="tel:+79660757594" aria-label="MAX: +7 966 075-75-94" />
          <a className="login-v077-hotspot login-v077-hotspot-phone" href="tel:+74954144585" aria-label="Телефония: +7 (495) 414-45-85" />
        </nav>
      </section>

      <section className="login-v077-auth-panel" aria-label="Вход в систему">
        <article className="login-v077-card">
          <header className="login-v077-header">
            <img className="login-v077-logo" src="/auth-ui/intezgroup-logo.png" alt="Intez Group — IT Solutions" />
            <h1>Вход в систему</h1>
            <p>Введите ваши учетные данные<br />для доступа к порталу поддержки.</p>
          </header>

          <form className="login-v077-form" onSubmit={submit} aria-label="Универсальная форма входа" aria-busy={pending}>
            <label className="login-v077-field">
              <svg className="login-v077-field-icon" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="8" r="3.25" />
                <path d="M5.5 20c.5-4 2.7-6 6.5-6s6 2 6.5 6" />
              </svg>
              <span className="login-v077-sr-only">Логин или email</span>
              <input
                type="text"
                name="identity"
                autoComplete="username"
                placeholder="Логин или email"
                required
                disabled={pending}
              />
            </label>

            <label className="login-v077-field">
              <svg className="login-v077-field-icon" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="5" y="10" width="14" height="10" rx="2" />
                <path d="M8 10V7.25a4 4 0 0 1 8 0V10" />
              </svg>
              <span className="login-v077-sr-only">Пароль</span>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                placeholder="Введите пароль"
                required
                disabled={pending}
              />
              <button
                className={`login-v077-password-toggle ${showPassword ? 'is-active' : ''}`}
                type="button"
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                aria-pressed={showPassword}
                onClick={() => setShowPassword(value => !value)}
                disabled={pending}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m3 3 18 18M10.6 7.15A10.8 10.8 0 0 1 12 7c5.95 0 9.5 5 9.5 5a15.8 15.8 0 0 1-2.2 2.55M6.3 6.3C3.9 8 2.5 12 2.5 12s3.55 5 9.5 5c1.42 0 2.7-.28 3.82-.73M9.88 9.88a3 3 0 0 0 4.24 4.24" /></svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12s3.55-5 9.5-5 9.5 5 9.5 5-3.55 5-9.5 5-9.5-5-9.5-5Z" /><circle cx="12" cy="12" r="2.75" /></svg>
                )}
              </button>
            </label>

            <div className="login-v077-options">
              <label className="login-v077-remember">
                <input type="checkbox" name="remember" disabled={pending} />
                <span className="login-v077-checkbox" aria-hidden="true">
                  <svg viewBox="0 0 16 16"><path d="m3.2 8.3 2.6 2.7 6-6.2" /></svg>
                </span>
                <span>Запомнить меня</span>
              </label>
              <a className="login-v077-forgot" href="mailto:help@intezgroup.ru?subject=Восстановление доступа к порталу">Забыли пароль?</a>
            </div>

            <button className="login-v077-submit" type="submit" disabled={pending}>
              {pending ? 'Выполняется вход…' : 'Войти'}
            </button>

            {(error || notice) && <div className="login-v077-error" role="alert">{error || notice}</div>}
          </form>
        </article>

        <div className="login-v077-security-note">
          <div className="login-v077-security-title">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3 5.5 5.6v5.25c0 4.45 2.72 8.3 6.5 10.15 3.78-1.85 6.5-5.7 6.5-10.15V5.6L12 3Z" />
              <path d="m9.3 12 1.8 1.8 3.7-4" />
            </svg>
            <strong>Безопасность и конфиденциальность</strong>
          </div>
          <p>Ваши данные защищены и не передаются третьим лицам.<br />Используется шифрование TLS 1.2+.</p>
        </div>
        <span className="login-v077-version" aria-hidden="true">Intez Helpdesk v{APP_VERSION}</span>
      </section>
    </main>
  );
}

function Layout({ uiShellVersion }: { uiShellVersion: UiShellVersion }) {
  const { user, logout, refresh } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [globalSearchType, setGlobalSearchType] = useState<'all' | GlobalSearchType>('all');
  const [globalSearchFiltersOpen, setGlobalSearchFiltersOpen] = useState(false);
  const [globalSearchFilters, setGlobalSearchFilters] = useState({ status: '', priority: '', channel: '', companyId: '', assigneeId: '', dateFrom: '', dateTo: '', hasFiles: false, fileType: '', archived: false });
  const [debouncedGlobalSearch, setDebouncedGlobalSearch] = useState('');
  const [globalSearchHistory, setGlobalSearchHistory] = useState<string[]>(() => JSON.parse(localStorage.getItem('ihd-global-search-history') ?? '[]') as string[]);
  const globalSearchRef = useRef<HTMLInputElement | null>(null);
  const globalSearchBoxRef = useRef<HTMLDivElement | null>(null);
  const notificationBoxRef = useRef<HTMLDivElement | null>(null);
  const hoverReadNotificationIds = useRef<Set<string>>(new Set());
  const [pushEnabled, setPushEnabled] = useState(() => localStorage.getItem('ihd-push-enabled') !== 'false');
  const [pushToasts, setPushToasts] = useState<NotificationRow[]>([]);
  const seenToastIds = useRef<Set<string>>(new Set(JSON.parse(localStorage.getItem('ihd-push-seen') ?? '[]') as string[]));
  const toastBootstrapped = useRef(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const employee = user?.userType === 'EMPLOYEE';
  const normalizedGlobalSearch = globalSearch.trim();
  const hasGlobalSearchFilters = globalSearchType !== 'all' || Object.values(globalSearchFilters).some(value => value !== '' && value !== false);
  const globalSearchReady = debouncedGlobalSearch.length >= 2 || hasGlobalSearchFilters;

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedGlobalSearch(normalizedGlobalSearch), 280);
    return () => window.clearTimeout(timer);
  }, [normalizedGlobalSearch]);

  useEffect(() => {
    const handler = (event: globalThis.MouseEvent) => {
      if (!globalSearchOpen) return;
      const target = event.target as Node | null;
      if (target && !globalSearchBoxRef.current?.contains(target)) setGlobalSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [globalSearchOpen]);

  useEffect(() => {
    const handler = (event: globalThis.MouseEvent) => {
      if (!notificationsOpen) return;
      const target = event.target as Node | null;
      if (target && !notificationBoxRef.current?.contains(target)) setNotificationsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notificationsOpen]);

  const globalSearchUrl = useMemo(() => {
    const params = new URLSearchParams({ q: debouncedGlobalSearch, limit: globalSearchType === 'all' ? '5' : '20' });
    if (globalSearchType !== 'all') params.set('types', globalSearchType);
    Object.entries(globalSearchFilters).forEach(([key, value]) => {
      if (value === '' || value === false) return;
      params.set(key, String(value));
    });
    return `/search?${params.toString()}`;
  }, [debouncedGlobalSearch, globalSearchType, globalSearchFilters]);

  const { data: globalSearchResults, isFetching: globalSearchLoading, error: globalSearchError } = useQuery({
    queryKey: ['global-search', globalSearchUrl],
    queryFn: () => api<GlobalSearchResponse>(globalSearchUrl),
    enabled: globalSearchOpen && globalSearchReady,
    staleTime: 15_000,
  });
  const { data: globalSearchCompanies = [] } = useQuery({ queryKey: ['global-search-companies'], queryFn: () => api<Company[]>('/companies'), enabled: employee && globalSearchFiltersOpen, staleTime: 60_000 });
  const { data: globalSearchAssignees = [] } = useQuery({ queryKey: ['global-search-assignees'], queryFn: () => api<Assignee[]>('/users/assignees'), enabled: employee && globalSearchFiltersOpen, staleTime: 60_000 });
  const firstGlobalSearchItem = useMemo(() => {
    if (!globalSearchResults) return null;
    const types: GlobalSearchType[] = globalSearchType === 'all'
      ? ['ticket', 'chat', 'contact', 'company', 'file', 'user']
      : [globalSearchType];
    for (const type of types) {
      const item = globalSearchResults.groups[type]?.[0];
      if (item) return item;
    }
    return null;
  }, [globalSearchResults, globalSearchType]);
  const { data: notificationCount } = useQuery({
    queryKey: ['notification-count'],
    queryFn: () => api<{ unread: number }>('/notifications/count'),
    refetchInterval: 10_000,
  });
  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<NotificationRow[]>('/notifications'),
    enabled: notificationsOpen,
  });
  const { data: unreadNotifications = [] } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api<NotificationRow[]>('/notifications?unreadOnly=true'),
    refetchInterval: 10_000,
  });
  const markRead = useMutation({
    mutationFn: (id: string) => api(`/notifications/${id}/read`, { method: 'PATCH' }),
    onMutate: (id: string) => {
      hoverReadNotificationIds.current.add(id);
      const markRow = (rows?: NotificationRow[]) => rows?.map(item => item.id === id ? { ...item, is_read: true } : item);
      queryClient.setQueryData<NotificationRow[]>(['notifications'], markRow);
      queryClient.setQueryData<NotificationRow[]>(['notifications', 'page'], markRow);
      queryClient.setQueryData<NotificationRow[]>(['notifications', 'unread'], rows => rows?.filter(item => item.id !== id));
      queryClient.setQueryData<{ unread: number }>(['notification-count'], current => current ? { unread: Math.max(0, current.unread - 1) } : current);
    },
    onError: (_error, id) => {
      hoverReadNotificationIds.current.delete(id);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread'] });
      queryClient.invalidateQueries({ queryKey: ['notification-count'] });
    },
  });
  const markAllRead = useMutation({
    mutationFn: () => api('/notifications/read-all', { method: 'POST' }),
    onSuccess: () => {
      setPushToasts([]);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread'] });
      queryClient.invalidateQueries({ queryKey: ['notification-count'] });
    },
  });

  useEffect(() => {
    localStorage.setItem('ihd-push-enabled', pushEnabled ? 'true' : 'false');
  }, [pushEnabled]);

  useEffect(() => {
    const handler = (event: globalThis.KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setGlobalSearchOpen(true);
        window.setTimeout(() => globalSearchRef.current?.focus(), 0);
      }
      if (event.key === 'Escape') {
        setGlobalSearchOpen(false);
        setAccountOpen(false);
        setNotificationsOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    setAccountOpen(false);
    setGlobalSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const activeConversationId = new URLSearchParams(location.search).get('conversation');
    const incoming = unreadNotifications.filter(item => !seenToastIds.current.has(item.id));
    if (!toastBootstrapped.current) {
      unreadNotifications.forEach(item => seenToastIds.current.add(item.id));
      localStorage.setItem('ihd-push-seen', JSON.stringify([...seenToastIds.current].slice(-300)));
      toastBootstrapped.current = true;
      return;
    }
    if (!incoming.length) return;
    const activeChatItems = incoming.filter(item => item.entity_type === 'CONTACT_CONVERSATION' && item.entity_id && location.pathname === '/contact-center' && item.entity_id === activeConversationId);
    const visibleIncoming = incoming.filter(item => !activeChatItems.some(active => active.id === item.id));
    incoming.forEach(item => seenToastIds.current.add(item.id));
    activeChatItems.forEach(item => markRead.mutate(item.id));
    localStorage.setItem('ihd-push-seen', JSON.stringify([...seenToastIds.current].slice(-300)));
    if (!visibleIncoming.length) return;
    if (visibleIncoming.some(item => item.notification_type === 'CONTACT_MESSAGE')) playIncomingChatSound();
    if (pushEnabled) setPushToasts(current => [...visibleIncoming.slice(0, 4).reverse(), ...current].slice(0, 5));
  }, [unreadNotifications, pushEnabled, location.pathname, location.search]);

  useEffect(() => {
    setPushToasts([]);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!pushToasts.length) return;
    const timer = window.setTimeout(() => setPushToasts([]), 8000);
    return () => window.clearTimeout(timer);
  }, [pushToasts.length]);

  function markNotificationReadOnHover(item: NotificationRow) {
    if (item.is_read || hoverReadNotificationIds.current.has(item.id)) return;
    hoverReadNotificationIds.current.add(item.id);
    markRead.mutate(item.id);
  }

  function openNotification(item: NotificationRow) {
    if (!item.is_read && !hoverReadNotificationIds.current.has(item.id)) markRead.mutate(item.id);
    setPushToasts(current => current.filter(toast => toast.id !== item.id));
    setNotificationsOpen(false);
    if (item.entity_type === 'TICKET' && item.entity_id) {
      const discussion = /COMMENT|REPLY/.test(item.notification_type) ? '?tab=discussion' : '';
      navigate(`/tickets/${item.entity_id}${discussion}`);
      return;
    }
    if (item.entity_type === 'CONTACT_CONVERSATION' && item.entity_id) {
      navigate(`/contact-center?conversation=${item.entity_id}`);
      return;
    }
    if (location.pathname !== '/') navigate('/');
  }

  function globalSearchIcon(type: GlobalSearchType) {
    if (type === 'ticket') return <TicketIcon />;
    if (type === 'chat') return <MessageSquare />;
    if (type === 'contact') return <UserPlus />;
    if (type === 'company') return <Building2 />;
    if (type === 'file') return <FileText />;
    return <Users />;
  }

  function openGlobalSearchItem(item: GlobalSearchItem) {
    const nextHistory = [normalizedGlobalSearch, ...globalSearchHistory.filter(value => value !== normalizedGlobalSearch)].filter(Boolean).slice(0, 8);
    setGlobalSearchHistory(nextHistory);
    localStorage.setItem('ihd-global-search-history', JSON.stringify(nextHistory));
    setGlobalSearchOpen(false);
    navigate(item.path);
  }

  function clearGlobalSearchFilters() {
    setGlobalSearchFilters({ status: '', priority: '', channel: '', companyId: '', assigneeId: '', dateFrom: '', dateTo: '', hasFiles: false, fileType: '', archived: false });
  }

  async function doLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <AppShell
      version={uiShellVersion}
      sidebar={<Sidebar
        version={uiShellVersion}
        groups={getVisibleNavigation(user!)}
        pathname={location.pathname}
        theme={theme}
        collapsed={false}
        mobileOpen={menuOpen}
        onNavigate={() => setMenuOpen(false)}
        appVersion={APP_VERSION}
      />}
      header={<header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMenuOpen(true)} aria-label="Открыть боковое меню" title="Разделы портала"><LayoutDashboard /></button>
          <button className="icon-button mobile-global-search" type="button" onClick={() => { setGlobalSearchOpen(true); window.setTimeout(() => globalSearchRef.current?.focus(), 0); }} aria-label="Открыть глобальный поиск" title="Поиск"><Search /></button>
          <div ref={globalSearchBoxRef} className={`global-search ${globalSearchOpen ? 'focused' : ''}`}>
            <Search />
            <input
              ref={globalSearchRef}
              value={globalSearch}
              onChange={event => { setGlobalSearch(event.target.value); setGlobalSearchOpen(true); }}
              onFocus={() => setGlobalSearchOpen(true)}
              onKeyDown={event => {
                if (event.key === 'Enter' && firstGlobalSearchItem) openGlobalSearchItem(firstGlobalSearchItem);
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  globalSearchBoxRef.current?.querySelector<HTMLButtonElement>('.global-search-group>button')?.focus();
                }
              }}
              placeholder="Поиск по заявкам, чатам, клиентам, компаниям, файлам и пользователям…"
              aria-label="Глобальный поиск"
            />
            {globalSearch && <button className="global-search-clear" type="button" onClick={() => setGlobalSearch('')} aria-label="Очистить поиск"><X /></button>}
            {globalSearchOpen && <div className="global-search-results global-search-panel">
              <div className="global-search-panel-head">
                <div className="global-search-tabs">
                  {([['all','Все'],['ticket','Заявки'],['chat','Чаты'],['contact','Клиенты'],['company','Компании'],['file','Файлы'],['user','Пользователи']] as Array<['all' | GlobalSearchType,string]>).filter(([type]) => (employee || ['all','ticket','file'].includes(type)) && (type !== 'user' || userHasAnyRole(user, ['ADMIN','MANAGER','DISPATCHER']))).map(([type,label]) => <button key={type} className={globalSearchType === type ? 'active' : ''} onClick={() => setGlobalSearchType(type)}>{label}{type !== 'all' && globalSearchResults?.facets.type[type] ? <em>{globalSearchResults.facets.type[type]}</em> : null}</button>)}
                </div>
                <div className="global-search-panel-actions"><button className={`global-search-filter-button ${globalSearchFiltersOpen ? 'active' : ''}`} type="button" onClick={() => setGlobalSearchFiltersOpen(value => !value)}><Filter /> Фильтры</button><button className="global-search-mobile-close" type="button" onClick={() => setGlobalSearchOpen(false)} aria-label="Закрыть поиск"><X /></button></div>
              </div>
              {globalSearchFiltersOpen && <div className="global-search-filters">
                <select value={globalSearchFilters.status} onChange={event => setGlobalSearchFilters(value => ({ ...value, status: event.target.value }))}><option value="">Все статусы</option>{STATUS_OPTIONS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}<option value="OPEN">Чат открыт</option><option value="CLOSED">Архив</option></select>
                <select value={globalSearchFilters.priority} onChange={event => setGlobalSearchFilters(value => ({ ...value, priority: event.target.value }))}><option value="">Все приоритеты</option>{PRIORITY_OPTIONS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
                <select value={globalSearchFilters.channel} onChange={event => setGlobalSearchFilters(value => ({ ...value, channel: event.target.value }))}><option value="">Все каналы</option><option value="PORTAL">Портал</option><option value="TELEGRAM">Telegram</option><option value="MAX">MAX</option><option value="EMAIL">Email</option><option value="PHONE">Телефония</option></select>
                {employee && <select value={globalSearchFilters.companyId} onChange={event => setGlobalSearchFilters(value => ({ ...value, companyId: event.target.value }))}><option value="">Все компании</option>{globalSearchCompanies.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select>}
                {employee && <select value={globalSearchFilters.assigneeId} onChange={event => setGlobalSearchFilters(value => ({ ...value, assigneeId: event.target.value }))}><option value="">Все исполнители</option><option value="me">Назначено мне</option>{globalSearchAssignees.map(item => <option key={item.id} value={item.id}>{item.full_name}</option>)}</select>}
                <input type="date" value={globalSearchFilters.dateFrom} onChange={event => setGlobalSearchFilters(value => ({ ...value, dateFrom: event.target.value }))} title="Дата с" />
                <input type="date" value={globalSearchFilters.dateTo} onChange={event => setGlobalSearchFilters(value => ({ ...value, dateTo: event.target.value }))} title="Дата по" />
                <select value={globalSearchFilters.fileType} onChange={event => setGlobalSearchFilters(value => ({ ...value, fileType: event.target.value }))}><option value="">Все файлы</option><option value="pdf">PDF</option><option value="doc">Word</option><option value="xls">Excel</option><option value="image">Изображения</option><option value="zip">Архивы</option></select>
                <label className="checkbox-label"><input type="checkbox" checked={globalSearchFilters.hasFiles} onChange={event => setGlobalSearchFilters(value => ({ ...value, hasFiles: event.target.checked }))} /> Есть файлы</label>
                <label className="checkbox-label"><input type="checkbox" checked={globalSearchFilters.archived} onChange={event => setGlobalSearchFilters(value => ({ ...value, archived: event.target.checked }))} /> Включая архив</label>
                <button type="button" onClick={clearGlobalSearchFilters}>Сбросить</button>
              </div>}
              {!globalSearchReady ? <div className="global-search-start">
                <b>Введите минимум 2 символа</b><small>Можно искать номер заявки, текст сообщений, клиента, компанию, email, телефон или имя файла.</small>
                {globalSearchHistory.length > 0 && <div><strong>Недавние запросы</strong>{globalSearchHistory.map(value => <button key={value} onClick={() => setGlobalSearch(value)}><Clock3 />{value}</button>)}</div>}
              </div> : <div className="global-search-body">
                {globalSearchLoading && <span className="global-search-state">Ищем по всему порталу…</span>}
                {globalSearchError && <span className="global-search-state danger">Не удалось выполнить поиск</span>}
                {!globalSearchLoading && globalSearchResults && globalSearchResults.total === 0 && <span className="global-search-state">Ничего не найдено</span>}
                {!globalSearchLoading && globalSearchResults && (['ticket','chat','contact','company','file','user'] as GlobalSearchType[]).filter(type => globalSearchType === 'all' || globalSearchType === type).map(type => {
                  const rows = globalSearchResults.groups[type] || [];
                  if (!rows.length) return null;
                  const labels: Record<GlobalSearchType,string> = { ticket:'Заявки', chat:'Чаты', contact:'Клиенты и контакты', company:'Компании', file:'Файлы', user:'Пользователи' };
                  return <section key={type} className="global-search-group"><header><b>{labels[type]}</b><span>{rows.length}</span></header>{rows.map(item => <button key={`${type}-${item.id}`} onClick={() => openGlobalSearchItem(item)}><span className={`global-search-result-icon ${type}`}>{globalSearchIcon(type)}</span><span className="global-search-result-copy"><b>{highlightSearchText(item.title, normalizedGlobalSearch)}</b><small>{highlightSearchText(item.subtitle, normalizedGlobalSearch)}</small>{item.snippet && <p>{highlightSearchText(item.snippet, normalizedGlobalSearch)}</p>}</span><ChevronRight /></button>)}</section>;
                })}
              </div>}
              <div className="global-search-hint"><span><kbd>Enter</kbd> открыть</span><span><kbd>Esc</kbd> закрыть</span><span>Поддерживаются запросы: <code>status:open</code>, <code>assignee:me</code>, <code>type:file</code></span></div>
            </div>}
          </div>
          <div className="topbar-actions">
            <div className="notification-wrap" ref={notificationBoxRef}>
            <button
              className="icon-button bell-button"
              onClick={() => setNotificationsOpen(value => !value)}
              aria-label={notificationsOpen ? 'Закрыть уведомления' : 'Открыть уведомления'}
              aria-expanded={notificationsOpen}
              aria-controls="portal-notifications-panel"
            >
              <Bell />{Boolean(notificationCount?.unread) && <em>{notificationCount?.unread}</em>}
            </button>
            {notificationsOpen && (
              <div className="notification-panel" id="portal-notifications-panel">
                <div className="panel-heading"><div><b>Уведомления</b><small>{notificationCount?.unread ?? 0} непрочитанных</small></div><div className="panel-actions"><button className="text-button tiny" onClick={() => { navigate('/notifications'); setNotificationsOpen(false); }}>Все уведомления</button><button className="text-button tiny" onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending}>Прочитать все</button><button className="icon-button" onClick={() => setNotificationsOpen(false)} aria-label="Закрыть уведомления"><X /></button></div></div>
                <label className="push-setting"><span><b>Всплывающие push</b><small>Показывать новые уведомления поверх интерфейса</small></span><input type="checkbox" checked={pushEnabled} onChange={event => setPushEnabled(event.target.checked)} /></label>
                <div className="notification-list">
                  {!notifications.length && <EmptyState icon={<Bell />} title="Пока тихо" text="Новые уведомления появятся здесь" compact />}
                  {compactNotificationRows(notifications).map(item => (
                    <button key={item.id} className={`${item.is_read ? '' : 'unread'} ${notificationTone(item.notification_type, item.title)}`} onMouseEnter={() => markNotificationReadOnHover(item)} onFocus={() => markNotificationReadOnHover(item)} onClick={() => openNotification(item)}>
                      <NotificationIcon item={item} />
                      <span><b>{item.title}</b><small>{item.body}</small><time>{formatShort(item.created_at)}</time></span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            </div>
            <div className="account-wrap">
            <button className={`topbar-user ${accountOpen ? 'open' : ''}`} onClick={() => { setAccountOpen(value => !value); setNotificationsOpen(false); setGlobalSearchOpen(false); }} aria-expanded={accountOpen}>
              <UserAvatar user={user!} /><span><b>{user?.fullName}</b><small>{userRoles(user).map(roleName).join(' · ')}</small></span><ChevronDown />
            </button>
            {accountOpen && <div className="account-menu">
              <div className="account-menu-head"><UserAvatar user={user!} /><div><b>{user?.fullName}</b><small>{user?.email}</small><small>{userRoles(user).map(roleName).join(' · ')}</small></div></div>
              <button onClick={() => { setProfileOpen(true); setAccountOpen(false); }}><UserRound /> Мой профиль</button>
              <button onClick={() => { navigate('/notification-settings'); setAccountOpen(false); }}><Mail /> Уведомления</button>
              <button className="account-menu-theme" onClick={() => { toggleTheme(); setAccountOpen(false); }}>{theme === 'dark' ? <Sun /> : <Moon />} {theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}</button>
              <button className="danger" onClick={doLogout}><LogOut /> Выйти</button>
            </div>}
            </div>
          </div>
          {uiShellVersion === 'legacy' && <Link className="topbar-brand" to="/" aria-label="Перейти на главную страницу" title="Intez Group — IT Solutions">
            <img src={theme === 'dark' ? '/intez-logo-dark.png' : '/intez-logo-light.png'} alt="Intez Group — IT Solutions" />
          </Link>}
        </header>}
      toasts={pushToasts.length > 0 && <div className="toast-stack" aria-live="polite">{pushToasts.map(item => <div key={item.id} className={`toast-card ${notificationTone(item.notification_type, item.title)}`} onClick={() => openNotification(item)} role="button" tabIndex={0} onKeyDown={event => { if (event.key === 'Enter') openNotification(item); }}><NotificationIcon item={item} /><span><b>{item.title}</b>{item.body && <small>{item.body}</small>}<time>{formatShort(item.created_at)}</time></span><button className="toast-close" onClick={event => { event.stopPropagation(); setPushToasts(current => current.filter(toast => toast.id !== item.id)); }} aria-label="Скрыть уведомление"><X /></button></div>)}</div>}
      phone={employee && <CallCard />}
    >
      <PortalRoutes user={user!} screens={{
          dashboard: <DashboardPage />,
          contactCenter: <ContactCenterPage />,
          calls: <CallsWorkspacePage />,
          tickets: <Tickets />,
          ticketView: <TicketView />,
          companies: <DirectorySection active="companies"><Companies /></DirectorySection>,
          contacts: <DirectorySection active="contacts"><Contacts /></DirectorySection>,
          templates: <DirectorySection active="templates"><Templates /></DirectorySection>,
          plannedWorks: <PlannedWorksPage />,
          visits: <FieldVisitsPage canPlan={userHasAnyRole(user, ['ADMIN', 'MANAGER', 'DISPATCHER'])} canDelete={userHasAnyRole(user, ['ADMIN'])} />,
          notifications: <NotificationsPage />,
          billing: <BillingPage role={primaryUserRole(user) as PortalRole} />,
          notificationSettings: <NotificationSettings />,
          monitoring: <MonitoringPage />,
          admin: <Admin />,
      } satisfies PortalScreens} />
      <ProfileDialog user={user!} open={profileOpen} onClose={() => setProfileOpen(false)} onSaved={refresh} />
    </AppShell>
  );
}

function notificationTone(type: string, title = '') {
  if (/RECOVERED/.test(type)) return 'success';
  if (/OVERDUE|SECURITY|BAN|UNASSIGNED|CANCELLED|TELEPHONY_HEALTH_ALERT/.test(type) || /критическ/i.test(title)) return 'danger';
  if (/COMMENT|REPLY|CONTACT_MESSAGE/.test(type)) return 'message';
  if (/ASSIGNED|COMPLETED/.test(type)) return 'success';
  return 'warning';
}

function compactNotificationRows(rows: NotificationRow[]) {
  const seenIncidents = new Set<string>();
  return rows.filter(item => {
    if (!/^(?:TELEPHONY_HEALTH|SYSTEM_BACKUP)_(?:ALERT|RECOVERED)$/.test(item.notification_type)) return true;
    if (!item.entity_type || !item.entity_id) return true;
    const incidentKey = `${item.entity_type}:${item.entity_id}`;
    if (seenIncidents.has(incidentKey)) return false;
    seenIncidents.add(incidentKey);
    return true;
  }).slice(0, 12);
}

function NotificationIcon({ item }: { item: NotificationRow }) {
  const tone = notificationTone(item.notification_type, item.title);
  const label = tone === 'success'
    ? 'Восстановлено'
    : tone === 'danger'
      ? 'Критическая ошибка'
      : tone === 'message'
        ? 'Новое сообщение'
        : 'Предупреждение';
  return <span className={`notification-icon ${tone}`} aria-label={label} title={label}>
    {tone === 'success' ? <CheckCircle2 /> : tone === 'message' ? <MessageSquare /> : tone === 'danger' ? <AlertTriangle /> : <Bell />}
  </span>;
}

type ImageLightboxItem = {
  id: string;
  src: string;
  downloadUrl: string;
  name: string;
  meta?: string;
  thumbnailUrl?: string;
};

function isImageMimeOrName(mimeType?: string | null, name?: string | null) {
  return String(mimeType || '').toLowerCase().startsWith('image/')
    || /\.(?:avif|bmp|gif|heic|heif|jpe?g|png|svg|webp)$/i.test(String(name || ''));
}

function ImageLightbox({ items, initialIndex, onClose }: { items: ImageLightboxItem[]; initialIndex: number; onClose: () => void }) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [copied, setCopied] = useState(false);
  const item = items[activeIndex] ?? items[0];

  useEffect(() => {
    setActiveIndex(Math.min(Math.max(initialIndex, 0), Math.max(0, items.length - 1)));
  }, [initialIndex, items.length]);

  useEffect(() => {
    setScale(1);
    setRotation(0);
    setCopied(false);
  }, [activeIndex]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft' && items.length > 1) setActiveIndex(index => (index - 1 + items.length) % items.length);
      if (event.key === 'ArrowRight' && items.length > 1) setActiveIndex(index => (index + 1) % items.length);
      if (event.key === '+' || event.key === '=') setScale(value => Math.min(4, value + .25));
      if (event.key === '-') setScale(value => Math.max(.35, value - .25));
      if (event.key.toLowerCase() === 'r') setRotation(value => value + 90);
      if (event.key === '0') { setScale(1); setRotation(0); }
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [items.length, onClose]);

  if (!item) return null;

  async function copyLink() {
    const url = new URL(item.src, window.location.origin).toString();
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const input = document.createElement('textarea');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }

  function previous() {
    setActiveIndex(index => (index - 1 + items.length) % items.length);
  }

  function next() {
    setActiveIndex(index => (index + 1) % items.length);
  }

  return <div className="image-lightbox" role="dialog" aria-modal="true" aria-label={`Просмотр изображения ${item.name}`} onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="image-lightbox-top">
      <div className="image-lightbox-title"><b title={item.name}>{item.name}</b>{item.meta && <small>{item.meta}</small>}</div>
      <div className="image-lightbox-tools">
        <button type="button" onClick={() => setScale(value => Math.max(.35, value - .25))} title="Уменьшить"><ZoomOut /></button>
        <span>{Math.round(scale * 100)}%</span>
        <button type="button" onClick={() => setScale(value => Math.min(4, value + .25))} title="Увеличить"><ZoomIn /></button>
        <button type="button" onClick={() => { setScale(1); setRotation(0); }} title="Исходный размер">1:1</button>
        <button type="button" onClick={() => setRotation(value => value + 90)} title="Повернуть"><RotateCw /></button>
        <button type="button" onClick={copyLink} title="Скопировать ссылку"><Link2 /></button>
        <a href={item.src} target="_blank" rel="noreferrer" title="Открыть оригинал в новой вкладке"><ExternalLink /></a>
        <a href={item.downloadUrl} title="Скачать"><Download /></a>
        <button className="image-lightbox-close" type="button" onClick={onClose} title="Закрыть"><X /></button>
      </div>
    </div>
    {copied && <div className="image-lightbox-toast">Ссылка скопирована</div>}
    <div className="image-lightbox-stage" onWheel={event => { event.preventDefault(); setScale(value => Math.min(4, Math.max(.35, value + (event.deltaY < 0 ? .15 : -.15)))); }}>
      {items.length > 1 && <button className="image-lightbox-nav previous" type="button" onClick={previous} aria-label="Предыдущее изображение"><ChevronLeft /></button>}
      <img src={item.src} alt={item.name} draggable={false} style={{ transform: `scale(${scale}) rotate(${rotation}deg)` }} />
      {items.length > 1 && <button className="image-lightbox-nav next" type="button" onClick={next} aria-label="Следующее изображение"><ChevronRight /></button>}
    </div>
    <div className="image-lightbox-bottom">
      <span>{activeIndex + 1} / {items.length}</span>
      {items.length > 1 && <div className="image-lightbox-thumbs">{items.map((entry, index) => <button type="button" key={entry.id} className={index === activeIndex ? 'active' : ''} onClick={() => setActiveIndex(index)} title={entry.name}><img src={entry.thumbnailUrl || entry.src} alt="" loading="lazy" /></button>)}</div>}
      <small>Колесо мыши — масштаб · ← → — переход · R — поворот · Esc — закрыть</small>
    </div>
  </div>;
}

function playIncomingChatSound() {
  try {
    const AudioContextClass = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.08, now + 0.012);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);
    master.connect(ctx.destination);

    [1174.66, 1567.98].forEach((frequency, index) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, now + index * 0.035);
      oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.985, now + 0.34);
      gain.gain.setValueAtTime(0.0001, now + index * 0.035);
      gain.gain.exponentialRampToValueAtTime(index ? 0.055 : 0.075, now + 0.025 + index * 0.035);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.38 + index * 0.035);
      oscillator.connect(gain);
      gain.connect(master);
      oscillator.start(now + index * 0.035);
      oscillator.stop(now + 0.44 + index * 0.035);
    });
  } catch {
    // ignore browser audio restrictions
  }
}

function MetricCard({ icon, label, value, note, tone }: { icon: ReactNode; label: string; value: number | string; note: string; tone: string }) {
  return <div className={`metric-card tone-${tone}`}><span className={`metric-icon ${tone}`}>{icon}</span><div><small>{label}</small><b>{value}</b><em>{note}</em></div><span className="metric-sparkline"><i/><i/><i/><i/><i/></span></div>;
}

function Tickets() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const employee = user?.userType === 'EMPLOYEE';
  const view = employee && searchParams.get('view') === 'kanban' ? 'kanban' : 'list';
  const createOpen = searchParams.get('create') === '1';
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [queueId, setQueueId] = useState('');
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [assigneeId, setAssigneeId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [ticketPage, setTicketPage] = useState(1);
  const ticketListRef = useRef<HTMLElement>(null);
  const columnSettingsRef = useRef<HTMLDivElement>(null);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const ticketSettingsStorageKey = `${TICKET_TABLE_SETTINGS_STORAGE_KEY}:${user?.id || 'anonymous'}`;
  const [ticketTableSettings, setTicketTableSettings] = useState<TicketTableSettings>(() => {
    try {
      const stored = localStorage.getItem(ticketSettingsStorageKey);
      return normalizeTicketTableSettings(stored ? JSON.parse(stored) : null);
    } catch {
      return normalizeTicketTableSettings(null);
    }
  });
  const { data: queues = [] } = useQuery({ queryKey: ['queues'], queryFn: () => api<Queue[]>('/queues'), enabled: employee });
  const { data: assignees = [] } = useQuery({ queryKey: ['assignees'], queryFn: () => api<Assignee[]>('/users/assignees'), enabled: employee });
  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ['tickets', search, status, priority, queueId, assigneeId],
    queryFn: () => api<Ticket[]>(`/tickets?${new URLSearchParams({ ...(search && { search }), ...(status && { status }), ...(priority && { priority }), ...(queueId && { queueId }), ...(assigneeId && { assigneeId }) })}`),
    enabled: view === 'list',
  });
  const { data: summaryTickets = [] } = useQuery({ queryKey: ['tickets', 'summary'], queryFn: () => api<Ticket[]>('/tickets') });
  const { data: archivedTickets = [] } = useQuery({ queryKey: ['tickets', 'archive-summary'], queryFn: () => api<Ticket[]>('/tickets?status=CLOSED') });
  const visibleTickets = useMemo(() => tickets.filter(ticket => {
    const created = new Date(ticket.created_at).getTime();
    if (dateFrom && created < new Date(`${dateFrom}T00:00:00`).getTime()) return false;
    if (dateTo && created > new Date(`${dateTo}T23:59:59`).getTime()) return false;
    return true;
  }), [tickets, dateFrom, dateTo]);
  const ticketPagination = useMemo(() => paginateTicketList(visibleTickets, ticketPage, 25), [visibleTickets, ticketPage]);
  const activeAdvancedFilters = [assigneeId, dateFrom, dateTo].filter(Boolean).length;
  const counts = useMemo(() => ({
    all: summaryTickets.length,
    new: summaryTickets.filter(x => x.status === 'NEW').length,
    active: summaryTickets.filter(x => ['DISTRIBUTION', 'IN_PROGRESS'].includes(x.status)).length,
    waiting: summaryTickets.filter(x => x.status.includes('WAITING')).length,
    archive: archivedTickets.length,
  }), [summaryTickets, archivedTickets]);

  useEffect(() => {
    setTicketPage(1);
  }, [search, status, priority, queueId, assigneeId, dateFrom, dateTo]);

  useEffect(() => {
    if (ticketPage !== ticketPagination.page) setTicketPage(ticketPagination.page);
  }, [ticketPage, ticketPagination.page]);

  useEffect(() => {
    const persistTimer = window.setTimeout(() => {
      try { localStorage.setItem(ticketSettingsStorageKey, JSON.stringify(ticketTableSettings)); } catch { /* protected browser storage */ }
    }, 160);
    return () => window.clearTimeout(persistTimer);
  }, [ticketSettingsStorageKey, ticketTableSettings]);

  useEffect(() => {
    if (!columnSettingsOpen) return;
    const closeOnOutsideClick = (event: globalThis.MouseEvent) => {
      const target = event.target as Node | null;
      if (target && !columnSettingsRef.current?.contains(target)) setColumnSettingsOpen(false);
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setColumnSettingsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [columnSettingsOpen]);

  function changeTicketPage(nextPage: number) {
    if (nextPage === ticketPagination.page) return;
    setTicketPage(nextPage);
    window.requestAnimationFrame(() => ticketListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  function setView(next: 'list' | 'kanban') {
    const params = new URLSearchParams(searchParams);
    if (next === 'kanban') params.set('view', 'kanban');
    else params.delete('view');
    setSearchParams(params, { replace: true });
  }

  function openCreateTicket() {
    const params = new URLSearchParams(searchParams);
    params.set('create', '1');
    setSearchParams(params);
  }

  function closeCreateTicket() {
    const params = new URLSearchParams(searchParams);
    ['create', 'templateId', 'mailMessage', 'telephonyCall', 'phone', 'contact', 'companyId', 'subject'].forEach(key => params.delete(key));
    setSearchParams(params, { replace: true });
  }

  return (
    <div className={`tickets-page-v2${view === 'kanban' ? ' tickets-page-v2--kanban' : ''}`}>
      <PageHeading
        eyebrow="ЗАЯВКИ"
        title="Заявки"
        description={view === 'kanban' ? 'Перетаскивайте заявки между стадиями обработки' : 'Управление обращениями клиентов и внутренними работами'}
        actions={<>
          {employee && <div className="view-switch tickets-view-switch"><button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}><List /> Список</button><button className={view === 'kanban' ? 'active' : ''} onClick={() => setView('kanban')}><Columns3 /> Канбан</button></div>}
          <button className="button primary" onClick={openCreateTicket}><Plus /> Создать заявку</button>
        </>}
      />
      {view === 'kanban' ? <KanbanPage embedded /> : <>
        <div className="summary-pills"><SummaryPill label="Все" value={counts.all} active={!status} onClick={() => setStatus('')} /><SummaryPill label="Новые" value={counts.new} active={status === 'NEW'} onClick={() => setStatus('NEW')} /><SummaryPill label="В работе" value={counts.active} active={status === 'IN_PROGRESS'} onClick={() => setStatus('IN_PROGRESS')} /><SummaryPill label="Ожидают" value={counts.waiting} active={status === 'WAITING_CLIENT'} onClick={() => setStatus('WAITING_CLIENT')} /><SummaryPill label="Архив" value={counts.archive} active={status === 'CLOSED'} onClick={() => setStatus('CLOSED')} /></div>
        <section className="surface ticket-list-card" ref={ticketListRef}>
          <div className="filters-bar">
            <div className="search-field"><Search /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Поиск по номеру, теме или компании" /></div>
            <select value={status} onChange={event => setStatus(event.target.value)}><option value="">Все статусы</option>{STATUS_OPTIONS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
            <select value={priority} onChange={event => setPriority(event.target.value)}><option value="">Все приоритеты</option>{PRIORITY_OPTIONS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
            {employee && <select value={queueId} onChange={event => setQueueId(event.target.value)}><option value="">Все очереди</option>{queues.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select>}
            <button className={`icon-button filter-trigger ${advancedFiltersOpen || activeAdvancedFilters ? 'active' : ''}`} onClick={() => setAdvancedFiltersOpen(value => !value)} aria-expanded={advancedFiltersOpen} title="Расширенные фильтры"><Filter />{activeAdvancedFilters > 0 && <em>{activeAdvancedFilters}</em>}</button>
            <div className="ticket-column-settings-wrap" ref={columnSettingsRef}>
              <button
                className={`icon-button ticket-column-settings-trigger ${columnSettingsOpen ? 'active' : ''}`}
                type="button"
                onClick={() => setColumnSettingsOpen(value => !value)}
                aria-expanded={columnSettingsOpen}
                aria-controls="ticket-column-settings"
                aria-label="Настроить столбцы таблицы"
                title="Настроить столбцы"
              ><Settings /></button>
              {columnSettingsOpen && <div id="ticket-column-settings"><TicketColumnSettingsPanel
                settings={ticketTableSettings}
                onVisibilityChange={(columnId, visible) => setTicketTableSettings(current => setTicketColumnVisibility(current, columnId, visible))}
                onReset={() => setTicketTableSettings(normalizeTicketTableSettings(DEFAULT_TICKET_TABLE_SETTINGS))}
                onClose={() => setColumnSettingsOpen(false)}
              /></div>}
            </div>
          </div>
          {advancedFiltersOpen && <div className="advanced-filters-panel">
            {employee && <label>Исполнитель<select value={assigneeId} onChange={event => setAssigneeId(event.target.value)}><option value="">Все исполнители</option>{assignees.map(item => <option key={item.id} value={item.id}>{item.full_name}</option>)}</select></label>}
            <label>Создана с<input type="date" value={dateFrom} onChange={event => setDateFrom(event.target.value)} /></label>
            <label>Создана по<input type="date" value={dateTo} onChange={event => setDateTo(event.target.value)} /></label>
            <button className="soft-button" type="button" onClick={() => { setAssigneeId(''); setDateFrom(''); setDateTo(''); }}>Сбросить</button>
          </div>}
          {isLoading ? <TableSkeleton /> : <TicketTable
            rows={ticketPagination.items}
            clientView={user?.userType === 'CLIENT'}
            settings={ticketTableSettings}
            onColumnResize={(columnId, width) => setTicketTableSettings(current => setTicketColumnWidth(current, columnId, width))}
          />}
          {!isLoading && ticketPagination.totalItems > 0 && <ListPaginationFooter pagination={ticketPagination} onPageChange={changeTicketPage} className="ticket-pagination" ariaLabel="Страницы списка заявок" />}
        </section>
      </>}
      {createOpen && <SharedNewTicket onClose={closeCreateTicket} />}
    </div>
  );
}

function ListPaginationFooter<T>({ pagination, onPageChange, onPageSizeChange, className = '', ariaLabel = 'Страницы списка' }: {
  pagination: TicketPagination<T>;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  className?: string;
  ariaLabel?: string;
}) {
  const hasMultiplePages = pagination.totalPages > 1;

  return <nav className={`directory-pagination ${className}`.trim()} aria-label={ariaLabel}>
    <span>Показано <b>{pagination.rangeStart}–{pagination.rangeEnd}</b> из <b>{pagination.totalItems}</b></span>
    {hasMultiplePages && <div>
      <button type="button" onClick={() => onPageChange(pagination.page - 1)} disabled={pagination.page === 1} aria-label="Предыдущая страница"><ChevronLeft /></button>
      {pagination.pageItems.map((item, index) => item === 'ellipsis'
        ? <span className="ticket-pagination-ellipsis" aria-hidden="true" key={`ellipsis-${index}`}>…</span>
        : <button type="button" className={item === pagination.page ? 'active' : ''} onClick={() => onPageChange(item)} aria-label={`Страница ${item}`} aria-current={item === pagination.page ? 'page' : undefined} key={item}>{item}</button>)}
      <button type="button" onClick={() => onPageChange(pagination.page + 1)} disabled={pagination.page === pagination.totalPages} aria-label="Следующая страница"><ChevronRight /></button>
    </div>}
    {onPageSizeChange && <select value={pagination.pageSize} onChange={event => onPageSizeChange(Number(event.target.value))} aria-label="Записей на странице"><option value={10}>10 на странице</option><option value={25}>25 на странице</option><option value={50}>50 на странице</option></select>}
  </nav>;
}

function SummaryPill({ label, value, active, onClick }: { label: string; value: number; active: boolean; onClick: () => void }) {
  return <button className={active ? 'active' : ''} onClick={onClick}><span>{label}</span><b>{value}</b></button>;
}

function TicketTableCell({ columnId, ticket, clientView, visibleColumns }: {
  columnId: TicketColumnId;
  ticket: Ticket;
  clientView: boolean;
  visibleColumns: Set<TicketColumnId>;
}) {
  if (columnId === 'number') return <b className="ticket-cell ticket-cell-number">{ticket.display_number}</b>;
  if (columnId === 'subject') {
    const secondary = !visibleColumns.has('queue')
      ? ticket.queue_name ?? ticket.category ?? 'Общая очередь'
      : !visibleColumns.has('category')
        ? ticket.work_category_name ?? ticket.category ?? channelLabel(ticket.channel)
        : channelLabel(ticket.channel);
    return <span className="ticket-cell ticket-subject"><strong>{ticket.subject}</strong><small>{secondary}</small></span>;
  }
  if (columnId === 'company') return <span className="ticket-cell" title={ticket.company_name || 'Компания не определена'}>{ticket.company_name || 'Компания не определена'}</span>;
  if (columnId === 'contact') {
    const contact = ticket.contact_name ?? ticket.requester_full_name ?? 'Не указан';
    const detail = ticket.contact_phone ?? ticket.requester_phone ?? ticket.contact_email ?? ticket.requester_email;
    return <span className="ticket-cell ticket-cell-stacked"><strong>{contact}</strong>{detail && <small>{detail}</small>}</span>;
  }
  if (columnId === 'queue') return <span className="ticket-cell ticket-cell-with-dot"><i style={{ background: ticket.queue_color || '#718096' }} />{ticket.queue_name ?? 'Общая очередь'}</span>;
  if (columnId === 'status') return <span className="ticket-cell"><StatusBadge status={ticket.status} clientLabel={clientView ? ticket.client_status : undefined} /></span>;
  if (columnId === 'priority') return <span className="ticket-cell"><PriorityBadge priority={ticket.priority} /></span>;
  if (columnId === 'assignee') return <span className="ticket-cell" title={ticket.primary_assignee_name ?? 'Не назначен'}>{ticket.primary_assignee_name ?? 'Не назначен'}</span>;
  if (columnId === 'channel') return <span className="ticket-cell ticket-channel-label">{channelLabel(ticket.channel)}</span>;
  if (columnId === 'category') return <span className="ticket-cell" title={ticket.work_category_name ?? ticket.category ?? 'Не указана'}>{ticket.work_category_name ?? ticket.category ?? 'Не указана'}</span>;
  if (columnId === 'created') return <time className="ticket-cell ticket-cell-date" title={formatDateTime(ticket.created_at)}>{formatShort(ticket.created_at)}</time>;
  if (columnId === 'updated') return <time className="ticket-cell ticket-cell-date" title={formatDateTime(ticket.updated_at)}>{formatShort(ticket.updated_at)}</time>;

  const overdue = Boolean(ticket.resolution_due_at)
    && new Date(ticket.resolution_due_at as string).getTime() < Date.now()
    && !['RESOLVED', 'CLOSED'].includes(ticket.status);
  return <time className={`ticket-cell ticket-cell-date ${overdue ? 'overdue' : ''}`} title={formatDateTime(ticket.resolution_due_at)}>{formatShort(ticket.resolution_due_at)}</time>;
}

function TicketTable({ rows, clientView = false, settings, onColumnResize }: {
  rows: Ticket[];
  clientView?: boolean;
  settings: TicketTableSettings;
  onColumnResize: (columnId: TicketColumnId, width: number) => void;
}) {
  const navigate = useNavigate();
  if (!rows.length) return <EmptyState icon={<Inbox />} title="Заявок не найдено" text="Попробуйте изменить фильтры или создайте новую заявку" />;
  const visible = TICKET_TABLE_COLUMNS.filter(column => settings.visible.includes(column.id));
  const visibleIds = new Set(visible.map(column => column.id));
  const minimumGridWidth = visible.reduce((total, column) => total + column.minWidth, 0) + Math.max(0, visible.length - 1) * 16 + 28;
  const gridStyle = { gridTemplateColumns: ticketGridTemplate(settings, visible), minWidth: `${minimumGridWidth}px` };

  return <div className="ticket-table ticket-table-configurable">
    <div className="ticket-table-head ticket-table-grid" style={gridStyle}>
      {visible.map(column => <span className="ticket-table-head-cell" key={column.id}>
        <span>{column.label}</span>
        <TicketColumnResizeHandle column={column} width={settings.widths[column.id]} onResize={width => onColumnResize(column.id, width)} />
      </span>)}
    </div>
    {rows.map(ticket => <button key={ticket.id} className="ticket-table-row ticket-table-grid" style={gridStyle} onClick={() => navigate(`/tickets/${ticket.id}`)}>
      {visible.map(column => <TicketTableCell columnId={column.id} ticket={ticket} clientView={clientView} visibleColumns={visibleIds} key={column.id} />)}
    </button>)}
  </div>;
}

function KanbanPage({ embedded = false }: { embedded?: boolean }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const kanbanSearchParamsRef = useRef(searchParams);
  kanbanSearchParamsRef.current = searchParams;
  const suppressCardClickRef = useRef(false);
  const filterGroupRef = useRef<HTMLDivElement>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);
  const search = searchParams.get('kanbanSearch') || '';
  const queueId = searchParams.get('kanbanQueue') || '';
  const assigneeId = searchParams.get('kanbanAssignee') || '';
  const priority = searchParams.get('kanbanPriority') || '';
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [columnLimits, setColumnLimits] = useState<Record<string, number>>({});
  const { data: tickets = [], isLoading } = useQuery({ queryKey: ['tickets-kanban'], queryFn: () => api<Ticket[]>('/tickets') });
  const { data: queues = [] } = useQuery({ queryKey: ['queues'], queryFn: () => api<Queue[]>('/queues') });
  const { data: employees = [] } = useQuery({ queryKey: ['assignees'], queryFn: () => api<Assignee[]>('/users/assignees') });
  const columns = STATUS_OPTIONS.filter(item => ['NEW', 'DISTRIBUTION', 'IN_PROGRESS', 'WAITING_CLIENT', 'WAITING_APPROVAL', 'RESOLVED'].includes(item.value));
  const visibleTickets = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return tickets.filter(ticket => {
      const matchesSearch = !needle || [ticket.display_number, ticket.subject, ticket.company_name, ticket.queue_name, ticket.primary_assignee_name].filter(Boolean).some(value => String(value).toLowerCase().includes(needle));
      const matchesQueue = !queueId || ticket.queue_id === queueId;
      const matchesAssignee = !assigneeId || ticket.primary_assignee_id === assigneeId;
      const matchesPriority = !priority || ticket.priority === priority;
      return matchesSearch && matchesQueue && matchesAssignee && matchesPriority;
    });
  }, [tickets, search, queueId, assigneeId, priority]);

  useEffect(() => {
    setColumnLimits({});
  }, [search, queueId, assigneeId, priority]);

  useEffect(() => {
    if (!filtersOpen) return;
    const closeOnOutsideClick = (event: globalThis.MouseEvent) => {
      const target = event.target as Node | null;
      if (target && !filterGroupRef.current?.contains(target)) setFiltersOpen(false);
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setFiltersOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [filtersOpen]);

  const selectFilterCount = [queueId, assigneeId, priority].filter(Boolean).length;
  const activeFilterCount = [search.trim(), queueId, assigneeId, priority].filter(Boolean).length;
  const draggedTicket = draggedId ? tickets.find(ticket => ticket.id === draggedId) : null;
  const moveTicket = useMutation({
    mutationFn: ({ ticketId, status }: { ticketId: string; status: string }) => api(`/tickets/${ticketId}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, reason: status === 'WAITING_CLIENT' ? 'Ожидаем дополнительную информацию' : undefined, resolutionSummary: status === 'RESOLVED' ? 'Заявка решена' : undefined }),
    }),
    onMutate: async ({ ticketId, status }) => {
      await queryClient.cancelQueries({ queryKey: ['tickets-kanban'] });
      const previous = queryClient.getQueryData<Ticket[]>(['tickets-kanban']);
      queryClient.setQueryData<Ticket[]>(['tickets-kanban'], current => current?.map(ticket => ticket.id === ticketId ? { ...ticket, status } : ticket) ?? []);
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(['tickets-kanban'], context.previous);
    },
    onSettled: () => {
      setDraggedId(null);
      setDragOverStatus(null);
      queryClient.invalidateQueries({ queryKey: ['tickets-kanban'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
  });

  function dropTo(status: string) {
    if (!draggedId) return;
    const current = tickets.find(ticket => ticket.id === draggedId);
    if (current && current.status !== status) moveTicket.mutate({ ticketId: draggedId, status });
    else { setDraggedId(null); setDragOverStatus(null); }
  }

  function updateKanbanFilter(key: 'kanbanSearch' | 'kanbanQueue' | 'kanbanAssignee' | 'kanbanPriority', value: string) {
    const next = new URLSearchParams(kanbanSearchParamsRef.current);
    if (value) next.set(key, value);
    else next.delete(key);
    kanbanSearchParamsRef.current = next;
    setSearchParams(next, { replace: true });
  }

  function resetKanbanFilters() {
    const next = new URLSearchParams(kanbanSearchParamsRef.current);
    ['kanbanSearch', 'kanbanQueue', 'kanbanAssignee', 'kanbanPriority'].forEach(key => next.delete(key));
    kanbanSearchParamsRef.current = next;
    setSearchParams(next, { replace: true });
  }

  return (
    <div className={`kanban-page ${embedded ? 'embedded' : ''}`}>
      {!embedded && <PageHeading eyebrow="ЗАЯВКИ" title="Заявки" description="Перетаскивайте заявки между стадиями обработки" actions={<button className="button primary" onClick={() => navigate('/tickets?create=1')}><Plus /> Добавить заявку</button>} />}
      <div className="kanban-toolbar">
        <div className="kanban-filter-group" ref={filterGroupRef}>
          <button
            type="button"
            className={`kanban-filter-label ${filtersOpen || selectFilterCount > 0 ? 'active' : ''}`}
            aria-label={filtersOpen ? 'Скрыть фильтры канбана' : 'Показать фильтры канбана'}
            aria-expanded={filtersOpen}
            aria-controls="kanban-filter-panel"
            onClick={() => setFiltersOpen(value => !value)}
          >
            <Filter /><span>Фильтры</span>{selectFilterCount > 0 && <b>{selectFilterCount}</b>}<ChevronDown className="kanban-filter-chevron" />
          </button>
          {filtersOpen && <div id="kanban-filter-panel" className="kanban-filters" role="region" aria-label="Фильтры канбана">
            <label><span>Очередь</span><select value={queueId} onChange={event => updateKanbanFilter('kanbanQueue', event.target.value)} aria-label="Фильтр по очереди"><option value="">Все очереди</option>{queues.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            <label><span>Исполнитель</span><select value={assigneeId} onChange={event => updateKanbanFilter('kanbanAssignee', event.target.value)} aria-label="Фильтр по исполнителю"><option value="">Все исполнители</option>{employees.map(item => <option key={item.id} value={item.id}>{item.full_name}</option>)}</select></label>
            <label><span>Приоритет</span><select value={priority} onChange={event => updateKanbanFilter('kanbanPriority', event.target.value)} aria-label="Фильтр по приоритету"><option value="">Все приоритеты</option>{PRIORITY_OPTIONS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
          </div>}
        </div>
        <div className="search-field kanban-board-search"><Search /><input value={search} onChange={event => updateKanbanFilter('kanbanSearch', event.target.value)} placeholder="Поиск заявок на доске" />{search && <button type="button" className="kanban-search-clear" aria-label="Очистить поиск на доске" title="Очистить поиск" onClick={() => updateKanbanFilter('kanbanSearch', '')}><X /></button>}</div>
        <div className="kanban-toolbar-meta">
          {activeFilterCount > 1 || queueId || assigneeId || priority ? <button type="button" className="kanban-reset-filters" aria-label="Сбросить фильтры канбана" onClick={resetKanbanFilters}><RotateCw /><span>Сбросить</span><b>{activeFilterCount}</b></button> : null}
          <span className="kanban-result-count" aria-live="polite"><b>{visibleTickets.length}</b><span>{' заявок'}</span></span>
          {!embedded && <div className="view-switch"><button type="button" onClick={() => navigate('/tickets')}><List /> Список</button><button type="button" className="active"><Columns3 /> Канбан</button></div>}
        </div>
      </div>
      {isLoading ? <PageSkeleton /> : <div className="kanban-board">{columns.map(column => {
        const items = visibleTickets.filter(ticket => ticket.status === column.value);
        const isDropTarget = dragOverStatus === column.value && draggedTicket?.status !== column.value;
        const visibleLimit = columnLimits[column.value] ?? KANBAN_COLUMN_BATCH_SIZE;
        const visibleItems = items.slice(0, visibleLimit);
        const remainingItems = Math.max(0, items.length - visibleItems.length);
        const overdueItems = items.filter(ticket => timeUntil(ticket.resolution_due_at).tone === 'danger').length;
        const unassignedItems = items.filter(ticket => !ticket.primary_assignee_id && !ticket.primary_assignee_name).length;
        return <section
          key={column.value}
          data-status={column.value}
          className={`kanban-column kanban-column-surface ${isDropTarget ? 'drag-over' : ''}`}
          onDragOver={event => {
            if (!draggedTicket || draggedTicket.status === column.value) {
              event.dataTransfer.dropEffect = 'none';
              if (dragOverStatus !== null) setDragOverStatus(null);
              return;
            }
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            if (dragOverStatus !== column.value) setDragOverStatus(column.value);
          }}
          onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragOverStatus(null); }}
          onDrop={event => { event.preventDefault(); dropTo(column.value); }}
        >
          <header><span><StatusBadge status={column.value} /></span><b>{items.length}</b><small>заявок</small></header>
          <div className="kanban-column-insights" aria-label={`Сводка стадии ${column.label}`}>
            {items.length ? <>
              <span className={overdueItems ? 'danger' : 'ok'}><AlertTriangle />{overdueItems ? `${overdueItems} просрочено` : 'Сроки в норме'}</span>
              <span className={unassignedItems ? 'warning' : 'ok'}><UserRound />{unassignedItems ? `${unassignedItems} без исполнителя` : 'Все назначены'}</span>
            </> : <span className="empty"><Inbox />Стадия свободна</span>}
          </div>
          <div
            className="kanban-drop-guide"
            role={isDropTarget ? 'status' : undefined}
            aria-live={isDropTarget ? 'polite' : undefined}
            aria-atomic={isDropTarget ? 'true' : undefined}
            aria-hidden={!isDropTarget}
          ><ArrowDown /><span>Переместить в «{column.label}»</span></div>
          <div className="kanban-column-scroll" role="region" aria-label={`${column.label}: заявки`} tabIndex={0}>
            <div className="kanban-card-list">{visibleItems.map(ticket => {
            const due = timeUntil(ticket.resolution_due_at);
            return <article
              className={`kanban-card ${draggedId === ticket.id ? 'dragging' : ''}`}
              key={ticket.id}
              draggable
              role="button"
              tabIndex={0}
              aria-label={`Открыть заявку ${ticket.display_number}: ${ticket.subject}`}
              onDragStart={event => { suppressCardClickRef.current = true; setDraggedId(ticket.id); event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', ticket.id); }}
              onDragEnd={() => { setDraggedId(null); setDragOverStatus(null); window.setTimeout(() => { suppressCardClickRef.current = false; }, 0); }}
              onClick={event => { if (suppressCardClickRef.current) { event.preventDefault(); suppressCardClickRef.current = false; return; } navigate(`/tickets/${ticket.id}`); }}
              onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); navigate(`/tickets/${ticket.id}`); } }}
            >
              <header>
                <span className="kanban-ticket-number">{ticket.display_number}</span>
                <span className="kanban-card-grip" title="Перетащить заявку" aria-hidden="true"><GripVertical /></span>
              </header>
              <h3>{ticket.subject}</h3>
              <p className="kanban-card-company"><Building2 /><span>{ticket.company_name || 'Компания не определена'}</span></p>
              <div className="kanban-meta"><PriorityBadge priority={ticket.priority} /><span className={`deadline ${due.tone}`}><Clock3 />{due.label}</span></div>
              <div className="kanban-tags">{ticket.category && <span>{ticket.category}</span>}{ticket.queue_name && <span>{ticket.queue_name}</span>}</div>
              <footer>
                <span className="assignee-chip"><span className="avatar tiny">{initials(ticket.primary_assignee_name)}</span><small>{ticket.primary_assignee_name ?? 'Не назначен'}</small></span>
                <span className="kanban-updated" title={`Обновлено ${formatDateTime(ticket.updated_at)}`}><RotateCw />{formatShort(ticket.updated_at)}</span>
                {Boolean(ticket.checklist_total) && <span className="kanban-checklist" title="Выполнение чек-листа"><CheckCircle2 />{ticket.checklist_done ?? 0}/{ticket.checklist_total}</span>}
              </footer>
            </article>;
          })}{!items.length && <div className="kanban-empty"><Inbox /><span>Нет заявок</span><small>Перетащите карточку в эту колонку</small></div>}</div>
            {items.length > KANBAN_COLUMN_BATCH_SIZE && <footer className="kanban-column-progress">
              <span>Показано <b>{visibleItems.length}</b> из <b>{items.length}</b></span>
              {remainingItems > 0
                ? <button type="button" onClick={() => setColumnLimits(current => ({
                    ...current,
                    [column.value]: visibleLimit + KANBAN_COLUMN_BATCH_SIZE,
                  }))}>Показать ещё {Math.min(KANBAN_COLUMN_BATCH_SIZE, remainingItems)} <ChevronDown /></button>
                : <span className="kanban-column-complete"><Check /> Все загружены</span>}
            </footer>}
          </div>
        </section>;
      })}</div>}
    </div>
  );
}

function formatPhoneInput(value: string) {
  let digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('8')) digits = `7${digits.slice(1)}`;
  if (!digits.startsWith('7')) digits = `7${digits}`;
  digits = digits.slice(0, 11);
  const local = digits.slice(1);
  let result = '+7';
  if (local.length) result += ` (${local.slice(0, 3)}`;
  if (local.length >= 3) result += ')';
  if (local.length > 3) result += ` ${local.slice(3, 6)}`;
  if (local.length > 6) result += `-${local.slice(6, 8)}`;
  if (local.length > 8) result += `-${local.slice(8, 10)}`;
  return result;
}

function handlePhoneDelete(event: KeyboardEvent<HTMLInputElement>, setValue: (value: string) => void) {
  if (event.key !== 'Backspace' && event.key !== 'Delete') return;
  const input = event.currentTarget;
  const start = input.selectionStart ?? 0;
  const end = input.selectionEnd ?? start;
  if (start !== end) return;
  const value = input.value;
  const direction = event.key === 'Backspace' ? -1 : 1;
  let index = event.key === 'Backspace' ? start - 1 : start;
  if (index < 0 || index >= value.length || /\d/.test(value[index])) return;
  while (index >= 0 && index < value.length && !/\d/.test(value[index])) index += direction;
  if (index < 0 || index >= value.length) {
    index = event.key === 'Backspace' ? start : start - 1;
    while (index >= 0 && index < value.length && !/\d/.test(value[index])) index -= direction;
  }
  if (index < 0 || index >= value.length) return;
  event.preventDefault();
  const formatted = formatPhoneInput(`${value.slice(0, index)}${value.slice(index + 1)}`);
  setValue(formatted);
  requestAnimationFrame(() => {
    const cursor = Math.min(start, formatted.length);
    input.setSelectionRange(cursor, cursor);
  });
}

function TicketView() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const employee = user?.userType === 'EMPLOYEE';
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [commentMode, setCommentMode] = useState<'public' | 'internal'>('public');
  const [comment, setComment] = useState('');
  const [commentFiles, setCommentFiles] = useState<File[]>([]);
  const [commentError, setCommentError] = useState('');
  const [activeTab, setActiveTab] = useState<'discussion' | 'history' | 'checklists' | 'files' | 'worklogs'>('discussion');
  const [statusOpen, setStatusOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [descriptionEditing, setDescriptionEditing] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (!tab) return;
    const allowed = ['discussion', 'history', 'checklists', 'files', 'worklogs'];
    if (allowed.includes(tab) && (employee || ['discussion', 'files'].includes(tab))) setActiveTab(tab as typeof activeTab);
  }, [location.search, employee]);

  const { data: ticket, isLoading } = useQuery({ queryKey: ['ticket', id], queryFn: () => api<Ticket>(`/tickets/${id}`) });
  const emailTicket = employee && String(ticket?.channel || '').toUpperCase() === 'EMAIL';
  const { data: mailStatus } = useQuery({
    queryKey: ['mail-status'],
    queryFn: () => api<MailStatus>('/mail/status'),
    enabled: Boolean(emailTicket),
  });
  const { data: timeline = [] } = useQuery({
    queryKey: ['timeline', id],
    queryFn: () => api<TimelineEvent[]>(`/tickets/${id}/timeline`),
    refetchInterval: employee ? 10_000 : false,
  });
  const { data: checklists = [] } = useQuery({ queryKey: ['checklists', id], queryFn: () => api<Checklist[]>(`/tickets/${id}/checklists`), enabled: employee });
  const { data: files = [] } = useQuery({ queryKey: ['files', id], queryFn: () => api<FileRow[]>(`/tickets/${id}/files`) });
  const { data: employees = [] } = useQuery({ queryKey: ['assignees'], queryFn: () => api<Assignee[]>('/users/assignees'), enabled: employee });
  const { data: ticketContacts = [] } = useQuery({ queryKey: ['contacts', 'ticket', ticket?.company_id], queryFn: () => api<CompanyContact[]>(`/contacts?companyId=${ticket?.company_id}&active=true`), enabled: employee && Boolean(ticket?.company_id) });
  const { data: workCategories = [] } = useQuery({ queryKey: ['work-categories'], queryFn: () => api<WorkCategory[]>('/work-categories'), enabled: employee });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    queryClient.invalidateQueries({ queryKey: ['timeline', id] });
    queryClient.invalidateQueries({ queryKey: ['tickets'] });
    queryClient.invalidateQueries({ queryKey: ['tickets-kanban'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['billing'] });
    queryClient.invalidateQueries({ queryKey: ['billing-summary'] });
  };
  const commentMutation = useMutation({
    mutationFn: async ({ body, isPublic, files: filesToSend }: { body: string; isPublic: boolean; files: File[] }) => {
      if (filesToSend.length) {
        const form = new FormData();
        form.append('body', body);
        form.append('isPublic', String(isPublic));
        filesToSend.forEach(file => form.append('files', file, file.name));
        return uploadFormData(`/tickets/${id}/comments/with-files`, form);
      }
      return api(`/tickets/${id}/comments`, { method: 'POST', body: JSON.stringify({ body, isPublic }) });
    },
    onSuccess: () => {
      setComment('');
      setCommentFiles([]);
      setCommentError('');
      queryClient.invalidateQueries({ queryKey: ['files', id] });
      invalidate();
    },
    onError: reason => setCommentError(reason instanceof Error ? reason.message : 'Сообщение не отправлено'),
  });
  const retryEmailReply = useMutation({
    mutationFn: (eventId: string) => api(`/tickets/${id}/events/${eventId}/email/retry`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['timeline', id] }),
    onError: reason => setCommentError(reason instanceof Error ? reason.message : 'Не удалось повторить отправку письма'),
  });
  const statusMutation = useMutation({
    mutationFn: (status: string) => api(`/tickets/${id}/status`, { method: 'POST', body: JSON.stringify({ status, reason: status.includes('WAITING') ? 'Ожидаем дополнительную информацию' : undefined, resolutionSummary: status === 'RESOLVED' ? 'Заявка решена' : undefined }) }),
    onSuccess: () => { setStatusOpen(false); setStatusError(''); invalidate(); },
    onError: reason => setStatusError(reason instanceof Error ? reason.message : 'Не удалось изменить статус'),
  });
  const assigneeMutation = useMutation({ mutationFn: (userId: string) => api(`/tickets/${id}/assignees`, { method: 'POST', body: JSON.stringify({ userId, isPrimary: !(ticket?.assignees?.length) }) }), onSuccess: invalidate });
  const descriptionMutation = useMutation({
    mutationFn: (description: string) => api(`/tickets/${id}`, { method: 'PATCH', body: JSON.stringify({ description }) }),
    onSuccess: () => { setDescriptionEditing(false); invalidate(); },
  });
  const ticketContactMutation = useMutation({
    mutationFn: (contactId: string | null) => api(`/tickets/${id}`, { method: 'PATCH', body: JSON.stringify({ contactId }) }),
    onSuccess: invalidate,
    onError: reason => setStatusError(reason instanceof Error ? reason.message : 'Не удалось привязать контакт'),
  });
  const workCategoryMutation = useMutation({
    mutationFn: (workCategoryId: string) => api(`/tickets/${id}`, { method: 'PATCH', body: JSON.stringify({ workCategoryId }) }),
    onSuccess: () => { setStatusError(''); invalidate(); },
    onError: reason => setStatusError(reason instanceof Error ? reason.message : 'Не удалось изменить категорию работ'),
  });

  function addCommentFiles(files: FileList | File[]) {
    const incoming = Array.from(files);
    const oversized = incoming.filter(file => file.size > 100 * 1024 * 1024);
    if (oversized.length) setCommentError(`Файл больше 100 МБ: ${oversized.map(file => file.name).join(', ')}`);
    setCommentFiles(current => {
      const keys = new Set(current.map(file => `${file.name}:${file.size}:${file.lastModified}`));
      return [...current, ...incoming.filter(file => file.size <= 100 * 1024 * 1024 && !keys.has(`${file.name}:${file.size}:${file.lastModified}`))].slice(0, 10);
    });
  }

  function submitComment() {
    if ((!comment.trim() && !commentFiles.length) || commentMutation.isPending) return;
    if (emailTicket && commentMode === 'public' && mailStatus && !mailStatus.smtpEnabled) {
      setCommentError('Исходящая почта не настроена. Откройте «Администрирование → Интеграции → Почта».');
      return;
    }
    commentMutation.mutate({ body: comment, isPublic: commentMode === 'public', files: [...commentFiles] });
  }

  async function takeIntoWork() {
    try {
      if (user?.id && !ticket?.assignees?.some(person => person.id === user.id)) {
        await api(`/tickets/${id}/assignees`, { method: 'POST', body: JSON.stringify({ userId: user.id, isPrimary: !(ticket?.assignees?.length) }) });
      }
      await api(`/tickets/${id}/status`, { method: 'POST', body: JSON.stringify({ status: 'IN_PROGRESS' }) });
      setStatusError('');
      invalidate();
    } catch (reason) {
      setStatusError(reason instanceof Error ? reason.message : 'Не удалось взять заявку в работу');
    }
  }

  if (isLoading || !ticket) return <PageSkeleton />;
  const currentRole = primaryUserRole(user) as PortalRole;
  const canChangeStatus = Boolean(employee && canChangeClosedTicketStatus(ticket.status, currentRole));
  const response = timeUntil(ticket.response_due_at);
  const resolution = timeUntil(ticket.resolution_due_at);
  const visibleMessages = timeline.filter(event => ['PUBLIC_COMMENT', 'INTERNAL_COMMENT', 'FILE_ATTACHED'].includes(event.event_type));
  const tabCounts: Record<string, number> = { discussion: visibleMessages.length, history: timeline.length, checklists: checklists.reduce((sum, list) => sum + list.items.length, 0), files: files.length };

  return (
    <div className="ticket-page ticket-page-v2">
      <div className="ticket-page-head"><button className="back-link" onClick={() => navigate('/tickets')}><ArrowLeft /> К списку заявок</button><div className="ticket-head-actions">
        <button className="soft-button ticket-reply-action" onClick={() => { setCommentMode('public'); setActiveTab('discussion'); document.getElementById('comment-box')?.focus(); }}><Send /> Ответить</button>
        {canChangeStatus && ticket.status !== 'IN_PROGRESS' && <button className="soft-button ticket-take-action" onClick={takeIntoWork}><Activity /> Взять в работу</button>}
        <button className="soft-button ticket-more-trigger" aria-label="Дополнительные действия" onClick={() => { setMoreOpen(value => !value); setStatusOpen(false); }}><MoreHorizontal /></button>
        {moreOpen && <div className="action-menu ticket-more-menu">{employee && <><button onClick={() => { setCommentMode('internal'); setActiveTab('discussion'); setMoreOpen(false); document.getElementById('comment-box')?.focus(); }}><MessageSquare /> Внутренняя заметка</button>{canChangeStatus && <button onClick={() => { setActiveTab('worklogs'); setMoreOpen(false); }}><Timer /> Добавить время</button>}</>}<button onClick={() => { navigator.clipboard?.writeText(window.location.href); setMoreOpen(false); }}>Скопировать ссылку</button><button onClick={() => { window.print(); setMoreOpen(false); }}>Печать заявки</button></div>}
        {canChangeStatus && <><button className="button primary" onClick={() => { setStatusOpen(value => !value); setMoreOpen(false); }}>Статус <ChevronDown /></button>{statusOpen && <div className="action-menu ticket-status-menu">{STATUS_OPTIONS.map(item => <button key={item.value} onClick={() => statusMutation.mutate(item.value)}>{item.label}</button>)}</div>}</>}
      </div></div>
      {statusError && <div className="ticket-inline-error">{statusError}</div>}
      <div className="ticket-title-card"><div><span className="ticket-number">{ticket.display_number}</span><StatusBadge status={ticket.status} /><PriorityBadge priority={ticket.priority} /><h1>{ticket.subject}</h1><p>Создана {formatDateTime(ticket.created_at)} · обновлена {formatDateTime(ticket.updated_at)} · канал {channelLabel(ticket.channel)}</p></div><div className="ticket-contact-pills"><button type="button" onClick={() => navigate(`/companies?company=${ticket.company_id}`)}><Building2 />{ticket.company_name}</button>{ticket.contact_id && ticket.contact_name ? <button type="button" onClick={() => navigate(`/contacts?contact=${ticket.contact_id}`)}><Users />{ticket.contact_name}</button> : ticket.requester_full_name && <span><Users />{ticket.requester_full_name}</span>}{ticket.requester_phone && <span><Phone />{ticket.requester_phone}</span>}{ticket.requester_email && <span><Mail />{ticket.requester_email}</span>}</div></div>
      <div className="ticket-workspace">
        <section className="surface ticket-main-column">
          <section className="ticket-description-panel"><div className="ticket-description-heading"><span><FileText /></span><div><h2>Описание обращения</h2><p>Исходная задача и важные детали</p></div>{employee && !descriptionEditing && <button className="text-button tiny" onClick={() => { setDescriptionDraft(ticket.description || ''); setDescriptionEditing(true); }}>Изменить</button>}</div>{descriptionEditing ? <><textarea className="description-editor" value={descriptionDraft} onChange={event => setDescriptionDraft(event.target.value)} rows={5} /><div className="description-editor-actions"><button className="button ghost compact" onClick={() => setDescriptionEditing(false)}>Отмена</button><button className="button primary compact" disabled={!descriptionDraft.trim() || descriptionMutation.isPending} onClick={() => descriptionMutation.mutate(descriptionDraft.trim())}><Save /> Сохранить</button></div></> : <p className="description-text">{ticket.description || 'Описание не указано'}</p>}</section>
          <div className="ticket-tabs">{(['discussion', 'history', 'checklists', 'files', 'worklogs'] as const).map(tab => employee || ['discussion', 'files'].includes(tab) ? <button key={tab} className={activeTab === tab ? 'active' : ''} onClick={() => setActiveTab(tab)}>{({ discussion: 'Обсуждение', history: 'История', checklists: 'Чек-листы', files: 'Файлы', worklogs: 'Трудозатраты' } as Record<string, string>)[tab]}{tab !== 'worklogs' && <em>{tabCounts[tab]}</em>}</button> : null)}</div>
          {activeTab === 'discussion' && <DiscussionPanel events={timeline} currentUserId={user?.id} employee={employee} onRetryEmail={eventId => retryEmailReply.mutate(eventId)} retryingEventId={retryEmailReply.isPending ? String(retryEmailReply.variables || '') : ''} />}
          {activeTab === 'history' && <div className="timeline-list">{timeline.map(event => <TimelineItem key={event.id} event={event} />)}</div>}
          {activeTab === 'checklists' && <ChecklistsPanel ticketId={id} rows={checklists} />}
          {activeTab === 'files' && <FilesPanel files={files} />}
          {activeTab === 'worklogs' && employee && <WorklogsPanel ticketId={id} ticketCategoryId={ticket.work_category_id} ticketStatus={ticket.status} role={currentRole} currentUserId={user?.id || ''} onChanged={invalidate} />}
          <div className="comment-composer"><div className="composer-tabs"><button className={commentMode === 'public' ? 'active' : ''} onClick={() => setCommentMode('public')}>Ответ клиенту</button>{employee && <button className={commentMode === 'internal' ? 'active' : ''} onClick={() => setCommentMode('internal')}>Внутренняя заметка</button>}</div>
            {emailTicket && commentMode === 'public' && <div className={`email-ticket-reply-banner ${mailStatus && !mailStatus.smtpEnabled ? 'error' : ''}`}><span className="email-ticket-reply-icon"><Mail /></span><div><b>{mailStatus && !mailStatus.smtpEnabled ? 'Исходящая почта не настроена' : 'Ответ будет отправлен заказчику по email'}</b><small>Кому: {ticket.requester_email || 'адрес не указан'}{mailStatus?.fromEmail ? ` · Отправитель: ${mailStatus.fromEmail}` : mailStatus?.username ? ` · Почтовый ящик: ${mailStatus.username}` : ''}</small></div></div>}
            {commentFiles.length > 0 && <div className="pending-files compact">{commentFiles.map((file, index) => <span key={`${file.name}-${file.lastModified}`}><Paperclip /><b title={file.name}>{file.name}</b><small>{formatBytes(file.size)}</small><button type="button" onClick={() => setCommentFiles(current => current.filter((_item, currentIndex) => currentIndex !== index))}><X /></button></span>)}</div>}
            <textarea id="comment-box" value={comment} onChange={event => setComment(event.target.value)} rows={4} placeholder={commentMode === 'internal' ? 'Добавьте внутреннюю заметку, клиент её не увидит…' : emailTicket ? 'Напишите ответ — он будет отправлен заказчику письмом…' : 'Напишите ответ клиенту…'} onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); submitComment(); } }} />
            {commentError && <div className="form-error composer-error">{commentError}</div>}
            <div className="composer-footer"><div><label className="attachment-button" title="Прикрепить файлы"><Paperclip /><input type="file" multiple hidden onChange={event => { if (event.target.files) addCommentFiles(event.target.files); event.currentTarget.value = ''; }} /></label><small className="composer-hint">{emailTicket && commentMode === 'public' ? 'Файлы до 18 МБ будут приложены к письму' : 'Enter — отправить, Shift+Enter — новая строка'}</small></div><button className="button primary" disabled={(!comment.trim() && !commentFiles.length) || commentMutation.isPending || Boolean(emailTicket && commentMode === 'public' && mailStatus && !mailStatus.smtpEnabled)} onClick={submitComment}><Send /> {commentMutation.isPending ? 'Отправка…' : commentMode === 'internal' ? 'Добавить заметку' : emailTicket ? 'Отправить письмо' : 'Отправить'}</button></div>
          </div>
        </section>
        <aside className="ticket-right-rail">
          <ClosedTicketControls ticketId={id} status={ticket.status} closedAt={ticket.closed_at} role={currentRole} onUpdated={invalidate} />
          <section className="surface rail-card ticket-context-card"><div className="rail-title"><h2>Маршрут и клиент</h2><span className="ticket-context-channel">{channelLabel(ticket.channel)}</span></div><InfoLine label="Очередь" value={ticket.queue_name ?? 'Общая'} /><InfoLine label="Категория заявки" value={ticket.category} />{employee && userHasAnyRole(user, ['ADMIN', 'MANAGER', 'DISPATCHER', 'EXECUTOR']) ? <label className="ticket-contact-linker"><span>Категория работ</span><select value={ticket.work_category_id || defaultWorkCategory(workCategories)} disabled={workCategoryMutation.isPending || !workCategories.length} onChange={event => workCategoryMutation.mutate(event.target.value)}>{workCategories.filter(item => item.is_active).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label> : <InfoLine label="Категория работ" value={ticket.work_category_name} />}<InfoLine label="Компания" value={ticket.company_name} />{employee ? <label className="ticket-contact-linker"><span>Контакт</span><select value={ticket.contact_id || ''} disabled={ticketContactMutation.isPending} onChange={event => ticketContactMutation.mutate(event.target.value || null)}><option value="">Не привязан</option>{ticket.contact_id && ticket.contact_name && !ticketContacts.some(contact => contact.id === ticket.contact_id) && <option value={ticket.contact_id}>{ticket.contact_name}</option>}{ticketContacts.map(contact => <option key={contact.id} value={contact.id}>{contact.full_name || contact.email || contact.phone || 'Без имени'}</option>)}</select></label> : <InfoLine label="Контакт" value={ticket.contact_name || ticket.requester_full_name} />}</section>
          <section className="surface rail-card sla-card"><div className="rail-title"><h2>SLA</h2><span className={resolution.tone === 'danger' ? 'danger' : 'ok'}>{resolution.tone === 'danger' ? 'Нарушен' : 'В норме'}</span></div><DeadlineBar label="Реакция" value={response} date={ticket.response_due_at} /><DeadlineBar label="Начало работ" value={timeUntil(ticket.start_due_at)} date={ticket.start_due_at} /><DeadlineBar label="Решение" value={resolution} date={ticket.resolution_due_at} /></section>
          <section className="surface rail-card"><div className="rail-title"><h2>Исполнители</h2></div><div className="assignee-list">{ticket.assignees?.map(person => <div key={person.id}><span className="avatar tiny">{initials(person.full_name)}</span><span><b>{person.full_name}</b><small>{person.is_primary ? 'Ответственный' : roleName(person.role)}</small></span></div>)}{!ticket.assignees?.length && <p className="muted">Не назначены</p>}</div>{employee && <select className="inline-select" defaultValue="" onChange={event => { if (event.target.value) assigneeMutation.mutate(event.target.value); event.target.value = ''; }}><option value="">+ Добавить исполнителя</option>{employees.filter(person => !ticket.assignees?.some(current => person.id === current.id)).map(person => <option key={person.id} value={person.id}>{person.full_name}</option>)}</select>}</section>
          {Boolean(ticket.checklist_total) && <section className="surface rail-card progress-card"><h2>Выполнение</h2><ProgressBlock label="Чек-лист" done={ticket.checklist_done ?? 0} total={ticket.checklist_total ?? 0} /></section>}
        </aside>
      </div>
    </div>
  );
}

function InfoLine({ label, value }: { label: string; value?: string | null }) {
  return <div className="info-line"><span>{label}</span><b>{value || '—'}</b></div>;
}

function DeadlineBar({ label, value, date }: { label: string; value: { label: string; tone: string; percent: number }; date?: string | null }) {
  return <div className="deadline-bar"><div><span>{label}</span><b className={value.tone}>{value.label}</b></div><div className="sla-track"><i className={value.tone} style={{ width: `${value.percent}%` }} /></div><small>{formatShort(date)}</small></div>;
}

function ProgressBlock({ label, done, total }: { label: string; done: number; total: number }) {
  const percent = total ? done / total * 100 : 0;
  return <div className="progress-block"><div><span>{label}</span><b>{done}/{total}</b></div><div><i style={{ width: `${percent}%` }} /></div></div>;
}

function DiscussionPanel({ events, currentUserId, employee, onRetryEmail, retryingEventId }: { events: TimelineEvent[]; currentUserId?: string; employee: boolean; onRetryEmail: (eventId: string) => void; retryingEventId: string }) {
  const [imagePreviewIndex, setImagePreviewIndex] = useState<number | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const discussionEvents = events.filter(event => ['PUBLIC_COMMENT', 'INTERNAL_COMMENT', 'FILE_ATTACHED', 'STATUS_CHANGED', 'ASSIGNEE_ADDED', 'ASSIGNEE_REMOVED', 'TICKET_CREATED'].includes(event.event_type));
  useEffect(() => {
    if (!discussionEvents.length) return;
    const timer = window.setTimeout(() => endRef.current?.scrollIntoView({ block: 'end' }), 80);
    return () => window.clearTimeout(timer);
  }, [discussionEvents.length]);
  const imageGallery = useMemo<ImageLightboxItem[]>(() => discussionEvents.filter(event => event.event_type === 'FILE_ATTACHED'
    && typeof event.payload?.fileId === 'string'
    && isImageMimeOrName(String(event.payload?.mimeType || ''), String(event.payload?.originalName || event.body || '')))
    .map(event => {
      const fileId = String(event.payload.fileId);
      const name = String(event.payload?.originalName || event.body || 'Изображение');
      return { id: event.id, src: `/api/v1/files/${fileId}/download?inline=1`, downloadUrl: `/api/v1/files/${fileId}/download`, name, meta: formatDateTime(event.occurred_at) };
    }), [discussionEvents]);
  const imageIndex = useMemo(() => new Map(imageGallery.map((item, index) => [item.id, index])), [imageGallery]);
  if (!discussionEvents.length) {
    return <div className="discussion-list"><EmptyState icon={<MessageSquare />} title="Обсуждение пока пустое" text="Напишите первый ответ клиенту или внутреннюю заметку для команды." compact /></div>;
  }
  return <><div className="discussion-list">{discussionEvents.map(event => {
    const isComment = ['PUBLIC_COMMENT', 'INTERNAL_COMMENT'].includes(event.event_type);
    const isFile = event.event_type === 'FILE_ATTACHED';
    const own = Boolean(event.author_user_id && currentUserId && event.author_user_id === currentUserId);
    if (!isComment && !isFile) {
      return <div key={event.id} className="discussion-system"><span>{eventName(event.event_type)}</span><b>{event.body || 'Событие заявки'}</b><time>{formatDateTime(event.occurred_at)}</time></div>;
    }
    const previewIndex = imageIndex.get(event.id);
    return <article key={event.id} className={`discussion-message ${own ? 'own' : ''} ${event.is_public ? 'public' : 'internal'}`}>
      <span className="avatar tiny">{initials(event.author_name ?? event.user_full_name ?? 'Система')}</span>
      <div>
        <header><b>{event.author_name ?? event.user_full_name ?? 'Система'}</b><time>{formatDateTime(event.occurred_at)}</time></header>
        <p>{isFile ? `Файл прикреплён: ${event.body ?? 'вложение'}` : event.body}</p>
        {isFile && typeof event.payload?.fileId === 'string' && event.payload.fileId && <div className="discussion-file-actions">{previewIndex === undefined ? <a href={`/api/v1/files/${event.payload.fileId}/download?inline=1`} target="_blank" rel="noreferrer">Открыть</a> : <button type="button" onClick={() => setImagePreviewIndex(previewIndex)}>Открыть</button>}<a href={`/api/v1/files/${event.payload.fileId}/download`}>Скачать</a></div>}
        <footer><span>{event.is_public ? 'Видно клиенту' : 'Внутренняя заметка'}</span>{event.email_delivery_status && <EmailReplyDeliveryStatus event={event} employee={employee} retrying={retryingEventId === event.id} onRetry={() => onRetryEmail(event.id)} />}{typeof event.payload?.mailMessageId === 'string' && event.payload.mailMessageId && <a className="email-origin-link" href={`/mail?message=${encodeURIComponent(String(event.payload.mailMessageId))}`}><Mail /> Открыть письмо</a>}</footer>
      </div>
    </article>;
  })}<div ref={endRef} className="discussion-end-anchor" /></div>{imagePreviewIndex !== null && <ImageLightbox items={imageGallery} initialIndex={imagePreviewIndex} onClose={() => setImagePreviewIndex(null)} />}</>;
}

function EmailReplyDeliveryStatus({ event, employee, retrying, onRetry }: { event: TimelineEvent; employee: boolean; retrying: boolean; onRetry: () => void }) {
  const status = String(event.email_delivery_status || '');
  const labels: Record<string, string> = {
    PENDING: 'Письмо в очереди',
    SENDING: 'Письмо отправляется',
    RETRY: 'Повторная отправка',
    SENT: 'Отправлено по email',
    FAILED: 'Ошибка отправки',
    CANCELLED: 'Отправка отменена',
  };
  const retryAllowed = employee && ['FAILED', 'RETRY', 'CANCELLED'].includes(status);
  return <span className={`email-delivery-state ${status.toLowerCase()}`} title={event.email_delivery_error || event.email_delivery_recipient || ''}><Mail /> {labels[status] || status}{status === 'SENT' && event.email_delivery_sent_at ? ` · ${formatShort(event.email_delivery_sent_at)}` : ''}{retryAllowed && <button type="button" disabled={retrying} onClick={onRetry}>{retrying ? 'Повтор…' : 'Повторить'}</button>}</span>;
}

function TimelineItem({ event }: { event: TimelineEvent }) {
  if (event.event_type === 'PHONE_CALL') {
    const call = event.payload?.call;
    if (call && typeof call === 'object' && 'id' in call && 'direction' in call) {
      return <TicketCallTimelineCard call={call as BrowserCall} occurredAt={event.occurred_at} />;
    }
  }
  const tone = event.event_type.includes('COMMENT') ? (event.is_public ? 'blue' : 'amber') : event.event_type.includes('STATUS') ? 'red' : event.event_type.includes('WORKLOG') ? 'purple' : 'gray';
  return <div className={`timeline-item ${tone}`}><span className="timeline-icon">{event.event_type.includes('COMMENT') ? <MessageSquare /> : event.event_type.includes('STATUS') ? <Activity /> : event.event_type.includes('WORKLOG') ? <Timer /> : <Circle />}</span><div><header><span><b>{eventName(event.event_type)}</b><small> · {event.author_name ?? event.user_full_name ?? 'Система'}</small></span><time>{formatDateTime(event.occurred_at)}</time></header>{event.body && <p>{event.body}</p>}{!event.is_public && <em>Внутреннее событие</em>}</div></div>;
}

function ChecklistsPanel({ ticketId, rows }: { ticketId: string; rows: Checklist[] }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(`Чек-лист ${rows.length + 1}`);
  const [itemText, setItemText] = useState<Record<string, string>>({});
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  useEffect(() => {
    if (!title.trim() || /^Чек-лист \d+$/.test(title)) setTitle(`Чек-лист ${rows.length + 1}`);
  }, [rows.length]);
  const refresh = () => { queryClient.invalidateQueries({ queryKey: ['checklists', ticketId] }); queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] }); };
  const createList = useMutation({ mutationFn: () => api(`/tickets/${ticketId}/checklists`, { method: 'POST', body: JSON.stringify({ title: title.trim() || `Чек-лист ${rows.length + 1}` }) }), onSuccess: () => { setTitle(`Чек-лист ${rows.length + 2}`); refresh(); } });
  const renameList = useMutation({ mutationFn: ({ checklistId, title: nextTitle }: { checklistId: string; title: string }) => api(`/tickets/${ticketId}/checklists/${checklistId}`, { method: 'PATCH', body: JSON.stringify({ title: nextTitle }) }), onSuccess: () => { setOpenMenuId(null); refresh(); } });
  const deleteList = useMutation({ mutationFn: (checklistId: string) => api(`/tickets/${ticketId}/checklists/${checklistId}`, { method: 'DELETE' }), onSuccess: () => { setOpenMenuId(null); refresh(); } });
  const createItem = useMutation({ mutationFn: ({ checklistId, title: item }: { checklistId: string; title: string }) => api(`/tickets/${ticketId}/checklists/${checklistId}/items`, { method: 'POST', body: JSON.stringify({ title: item }) }), onSuccess: (_data, variables) => { setItemText(current => ({ ...current, [variables.checklistId]: '' })); refresh(); } });
  const [itemError, setItemError] = useState('');
  const patchItem = useMutation({
    mutationFn: ({ id, isDone }: { id: string; isDone: boolean }) => api(`/tickets/${ticketId}/checklist-items/${id}`, { method: 'PATCH', body: JSON.stringify({ isDone }) }),
    onMutate: async ({ id, isDone }) => {
      setItemError('');
      await queryClient.cancelQueries({ queryKey: ['checklists', ticketId] });
      const previous = queryClient.getQueryData<Checklist[]>(['checklists', ticketId]);
      queryClient.setQueryData<Checklist[]>(['checklists', ticketId], current => current?.map(list => ({ ...list, items: list.items.map(item => item.id === id ? { ...item, is_done: isDone } : item) })) ?? current);
      return { previous };
    },
    onError: (reason, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(['checklists', ticketId], context.previous);
      setItemError(reason instanceof Error ? reason.message : 'Не удалось изменить пункт чек-листа');
    },
    onSettled: refresh,
  });

  function addItem(checklistId: string) {
    const value = itemText[checklistId]?.trim();
    if (value) createItem.mutate({ checklistId, title: value });
  }

  return <div className="panel-content"><div className="panel-toolbar"><div><h2>Чек-листы</h2><p>Стандартизируйте выполнение типовых работ</p></div><div className="inline-create"><input value={title} onChange={event => setTitle(event.target.value)} placeholder={`Чек-лист ${rows.length + 1}`} onKeyDown={event => { if (event.key === 'Enter' && title.trim()) { event.preventDefault(); createList.mutate(); } }} /><button className="button primary" disabled={!title.trim() || createList.isPending} onClick={() => createList.mutate()}><Plus /> Создать</button></div></div>
    <div className="checklist-grid">{rows.map(list => <section key={list.id} className="checklist-card"><header><div><b>{list.title}</b><small>{list.items.filter(item => item.is_done).length}/{list.items.length}</small></div><div className="checklist-menu-wrap"><button type="button" className="checklist-more" aria-label="Действия с чек-листом" onClick={() => setOpenMenuId(current => current === list.id ? null : list.id)}><MoreHorizontal /></button>{openMenuId === list.id && <div className="checklist-menu"><button type="button" onClick={() => { const next = window.prompt('Новое название чек-листа', list.title)?.trim(); if (next) renameList.mutate({ checklistId: list.id, title: next }); }}>Переименовать</button><button type="button" className="danger" onClick={() => { if (window.confirm(`Удалить чек-лист «${list.title}»?`)) deleteList.mutate(list.id); }}>Удалить</button></div>}</div></header>
      <div>{list.items.map(item => <div className="checklist-item-row" key={item.id}><button type="button" aria-label={item.is_done ? 'Отметить невыполненным' : 'Отметить выполненным'} aria-pressed={item.is_done} className={`checklist-toggle ${item.is_done ? 'checked' : ''}`} disabled={patchItem.isPending && patchItem.variables?.id === item.id} onClick={() => patchItem.mutate({ id: item.id, isDone: !item.is_done })}>{item.is_done ? <Check /> : <span className="checklist-toggle-circle" />}</button><span className={item.is_done ? 'done' : ''}>{item.title}</span></div>)}</div>
      <footer><input value={itemText[list.id] ?? ''} onChange={event => setItemText(current => ({ ...current, [list.id]: event.target.value }))} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); addItem(list.id); } }} placeholder="Добавить пункт" /><button type="button" className="icon-button" disabled={!itemText[list.id]?.trim()} onClick={() => addItem(list.id)}><Plus /></button></footer>
    </section>)}</div>{itemError && <div className="ticket-inline-error checklist-error">{itemError}</div>}{!rows.length && <EmptyState icon={<ClipboardIcon />} title="Чек-листов пока нет" text="Предложенное название уже заполнено — нажмите «Создать»" compact />}</div>;
}

function ClipboardIcon() { return <CheckCircle2 />; }

function FilesPanel({ files }: { files: FileRow[] }) {
  const [imagePreviewIndex, setImagePreviewIndex] = useState<number | null>(null);
  const imageGallery = useMemo<ImageLightboxItem[]>(() => files.filter(file => isImageMimeOrName(file.mime_type, file.original_name)).map(file => ({
    id: file.id,
    src: `/api/v1/files/${file.id}/download?inline=1`,
    downloadUrl: `/api/v1/files/${file.id}/download`,
    name: file.original_name,
    meta: `${formatBytes(Number(file.size_bytes))} · ${formatShort(file.created_at)}`,
  })), [files]);
  const imageIndex = useMemo(() => new Map(imageGallery.map((item, index) => [item.id, index])), [imageGallery]);
  return <div className="panel-content"><div className="panel-toolbar"><div><h2>Файлы</h2><p>Все вложения из портала и каналов связи</p></div></div><div className="file-grid">{files.map(file => {
    const openUrl = `/api/v1/files/${file.id}/download?inline=1`;
    const downloadUrl = `/api/v1/files/${file.id}/download`;
    const previewIndex = imageIndex.get(file.id);
    return <article className={`ticket-file-card ${previewIndex !== undefined ? 'has-image' : ''}`} key={file.id}>{previewIndex === undefined ? <span className="file-icon"><FileText /></span> : <button type="button" className="ticket-file-preview" onClick={() => setImagePreviewIndex(previewIndex)} aria-label={`Просмотреть ${file.original_name}`}><img src={openUrl} alt={file.original_name} loading="lazy" /></button>}<span className="ticket-file-info"><b title={file.original_name}>{file.original_name}</b><small>{formatBytes(Number(file.size_bytes))} · {formatShort(file.created_at)}</small></span><span className="ticket-file-actions">{previewIndex === undefined ? <a href={openUrl} target="_blank" rel="noreferrer">Открыть</a> : <button type="button" onClick={() => setImagePreviewIndex(previewIndex)}>Открыть</button>}<a href={downloadUrl}>Скачать</a></span></article>;
  })}</div>{!files.length && <EmptyState icon={<Paperclip />} title="Файлов пока нет" text="Добавьте вложение через форму комментария" compact />}{imagePreviewIndex !== null && <ImageLightbox items={imageGallery} initialIndex={imagePreviewIndex} onClose={() => setImagePreviewIndex(null)} />}</div>;
}

function StatusBadge({ status, clientLabel }: { status: string; clientLabel?: string }) {
  return <span className={`status-badge status-${status.toLowerCase()}`}>{clientLabel ?? statusName(status)}</span>;
}

function PriorityBadge({ priority }: { priority: string }) {
  return <span className={`priority-badge priority-${priority.toLowerCase()}`}><i />{priorityName(priority)}</span>;
}

function contactChannelLabel(type?: string | null) {
  return ({ PHONE: 'Телефон', EMAIL: 'Email', TELEGRAM: 'Telegram', MAX: 'MAX', WHATSAPP: 'WhatsApp', VIBER: 'Viber', SKYPE: 'Skype', OTHER: 'Другое' } as Record<string, string>)[String(type || '').toUpperCase()] ?? String(type || 'Канал');
}

function channelIcon(type?: string | null) {
  const normalized = String(type || '').toUpperCase();
  if (normalized === 'PHONE') return <Phone />;
  if (normalized === 'EMAIL') return <Mail />;
  if (normalized === 'TELEGRAM') return <Send />;
  return <MessageSquare />;
}

function primaryChannel(contact: CompanyContact | undefined, type: string) {
  const channels = contact?.channels ?? [];
  return channels.find(item => item.channel_type === type && item.is_active !== false && item.is_primary)
    ?? channels.find(item => item.channel_type === type && item.is_active !== false);
}

function contactPhones(contact?: CompanyContact) {
  const rows = (contact?.channels ?? [])
    .filter(item => item.channel_type === 'PHONE' && item.is_active !== false && item.value.trim())
    .map(item => ({ value: item.value.trim(), label: item.label || (item.is_primary ? 'Основной телефон' : 'Телефон') }));
  if (contact?.phone?.trim() && !rows.some(item => item.value === contact.phone?.trim())) {
    rows.unshift({ value: contact.phone.trim(), label: 'Основной телефон' });
  }
  return rows.filter((item, index, all) => all.findIndex(other => other.value.replace(/\D/g, '') === item.value.replace(/\D/g, '')) === index);
}

function contactPhone(contact?: CompanyContact) {
  return contactPhones(contact)[0]?.value || '';
}

function contactEmail(contact?: CompanyContact) {
  return primaryChannel(contact, 'EMAIL')?.value || contact?.email || '';
}

function messengerChannels(contact?: CompanyContact) {
  return (contact?.channels ?? []).filter(item => ['TELEGRAM', 'MAX', 'WHATSAPP', 'VIBER', 'SKYPE'].includes(item.channel_type) && item.is_active !== false);
}

function Companies() {
  const companyBatchSize = 50;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [openTickets, setOpenTickets] = useState<'ALL' | 'YES' | 'NO'>('ALL');
  const [responsible, setResponsible] = useState('');
  const [source, setSource] = useState<'ALL' | 'PORTAL' | 'BITRIX24'>('ALL');
  const [syncStatus, setSyncStatus] = useState<'ALL' | 'SYNCED' | 'PENDING' | 'ERROR' | 'ARCHIVED'>('ALL');
  const [sort, setSort] = useState<'NAME_ASC' | 'NAME_DESC' | 'UPDATED'>('NAME_ASC');
  const [showFilters, setShowFilters] = useState(false);
  const [visibleCompanyCount, setVisibleCompanyCount] = useState(companyBatchSize);
  const [detailMenuOpen, setDetailMenuOpen] = useState(false);
  const [companyActivityTab, setCompanyActivityTab] = useState<CompanyActivityTab>('CONTACTS');
  const [companyEditor, setCompanyEditor] = useState<Company | null | 'NEW'>(null);
  const [contactEditor, setContactEditor] = useState<CompanyContact | null | 'NEW'>(null);
  const [dialTarget, setDialTarget] = useState<TelephonyDialTarget | null>(null);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const canManage = userHasRole(user, 'ADMIN');

  const { data: companies = [], isLoading } = useQuery({ queryKey: ['companies'], queryFn: () => api<Company[]>('/companies') });
  const { data: stats } = useQuery({ queryKey: ['companies', 'stats'], queryFn: () => api<CompanyStats>('/companies/stats') });
  const { data: assignees = [] } = useQuery({ queryKey: ['assignees'], queryFn: () => api<Assignee[]>('/users/assignees'), enabled: canManage });

  const visibleCompanies = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = companies.filter(company => {
      if (status === 'ACTIVE' && !company.is_active) return false;
      if (status === 'INACTIVE' && company.is_active) return false;
      if (openTickets === 'YES' && !(Number(company.open_tickets) > 0)) return false;
      if (openTickets === 'NO' && Number(company.open_tickets) > 0) return false;
      if (responsible && company.responsible_user_id !== responsible) return false;
      if (source !== 'ALL' && (company.source_system || 'PORTAL') !== source) return false;
      if (syncStatus !== 'ALL' && company.sync_status !== syncStatus) return false;
      if (!needle) return true;
      return [company.name, company.full_name, company.inn, company.kpp, company.ogrn, company.address, company.primary_contact_name, company.primary_contact_phone]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(needle));
    });
    return [...rows].sort((left, right) => {
      if (sort === 'NAME_DESC') return right.name.localeCompare(left.name, 'ru');
      if (sort === 'UPDATED') return new Date(right.updated_at || 0).getTime() - new Date(left.updated_at || 0).getTime();
      return left.name.localeCompare(right.name, 'ru');
    });
  }, [companies, search, status, openTickets, responsible, source, syncStatus, sort]);

  useEffect(() => setVisibleCompanyCount(companyBatchSize), [search, status, openTickets, responsible, source, syncStatus, sort]);
  const pageCompanies = useMemo(() => visibleCompanies.slice(0, visibleCompanyCount), [visibleCompanies, visibleCompanyCount]);
  const hasMoreCompanies = pageCompanies.length < visibleCompanies.length;

  function loadMoreCompanies(event: UIEvent<HTMLDivElement>) {
    if (!hasMoreCompanies) return;
    const list = event.currentTarget;
    const distanceToBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
    if (distanceToBottom > 120) return;
    setVisibleCompanyCount(current => Math.min(current + companyBatchSize, visibleCompanies.length));
  }

  const selectedCompanyId = searchParams.get('company') || visibleCompanies[0]?.id || '';
  const { data: selectedCompany, isLoading: companyLoading } = useQuery({
    queryKey: ['company', selectedCompanyId],
    queryFn: () => api<Company>(`/companies/${selectedCompanyId}`),
    enabled: Boolean(selectedCompanyId),
  });

  useEffect(() => {
    if (!selectedCompanyId || searchParams.get('company')) return;
    const next = new URLSearchParams(searchParams);
    next.set('company', selectedCompanyId);
    setSearchParams(next, { replace: true });
  }, [selectedCompanyId]);

  useEffect(() => setCompanyActivityTab('CONTACTS'), [selectedCompanyId]);

  function selectCompany(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('company', id);
    next.delete('contact');
    setSearchParams(next, { replace: true });
    setCompanyActivityTab('CONTACTS');
    setDetailMenuOpen(false);
  }

  const saveCompany = useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: Record<string, unknown> }) => api<Company>(id ? `/companies/${id}` : '/companies', { method: id ? 'PATCH' : 'POST', body: JSON.stringify(payload) }),
    onSuccess: async company => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['companies'] }),
        queryClient.invalidateQueries({ queryKey: ['companies', 'stats'] }),
        queryClient.invalidateQueries({ queryKey: ['company', company.id] }),
      ]);
      setCompanyEditor(null);
      selectCompany(company.id);
      setFeedback('Карточка компании сохранена');
      setError('');
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить компанию'),
  });

  const companyLifecycle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => api<Company>(
      isActive ? `/companies/${id}/status` : `/companies/${id}`,
      isActive
        ? { method: 'PATCH', body: JSON.stringify({ isActive: true }) }
        : { method: 'DELETE' },
    ),
    onMutate: () => {
      setFeedback('');
      setError('');
    },
    onSuccess: async (company, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['companies'] }),
        queryClient.invalidateQueries({ queryKey: ['companies', 'stats'] }),
        queryClient.invalidateQueries({ queryKey: ['company', company.id] }),
      ]);
      setDetailMenuOpen(false);
      if (variables.isActive) {
        setFeedback('Обслуживание компании восстановлено');
      } else {
        const next = new URLSearchParams(searchParams);
        next.delete('company');
        setSearchParams(next, { replace: true });
        queryClient.removeQueries({ queryKey: ['company', company.id] });
        setFeedback('Компания удалена из списка');
      }
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось изменить состояние компании'),
  });

  const saveContact = useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: Record<string, unknown> }) => api<CompanyContact>(id ? `/contacts/${id}` : '/contacts', { method: id ? 'PATCH' : 'POST', body: JSON.stringify(payload) }),
    onSuccess: async contact => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['company', contact.company_id] }),
        queryClient.invalidateQueries({ queryKey: ['companies'] }),
        queryClient.invalidateQueries({ queryKey: ['companies', 'stats'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts', 'stats'] }),
      ]);
      setContactEditor(null);
      setFeedback('Контакт сохранён и связан с компанией');
      setError('');
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить контакт'),
  });

  const removeCompanyContact = useMutation({
    mutationFn: ({ id }: { id: string; companyId: string }) => api<CompanyContact>(`/contacts/${id}`, { method: 'DELETE' }),
    onMutate: () => {
      setFeedback('');
      setError('');
    },
    onSuccess: async (_contact, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['company', variables.companyId] }),
        queryClient.invalidateQueries({ queryKey: ['companies'] }),
        queryClient.invalidateQueries({ queryKey: ['companies', 'stats'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts', 'stats'] }),
      ]);
      setFeedback('Контакт удалён из компании');
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось удалить контакт из компании'),
  });

  const totalStats = stats ?? {
    total: companies.length,
    active: companies.filter(item => item.is_active).length,
    inactive: companies.filter(item => !item.is_active).length,
    without_contacts: companies.filter(item => !Number(item.contacts_count)).length,
    without_responsible: companies.filter(item => !item.responsible_user_id).length,
  };

  return <div className="directory-page companies-directory-page">
    <PageHeading eyebrow="СПРАВОЧНИКИ" title="Компании" description="Клиенты, контакты, обращения и параметры обслуживания" />
    {feedback && <div className="admin-feedback success directory-feedback">{feedback}<button className="icon-button" onClick={() => setFeedback('')}><X /></button></div>}
    {error && !companyEditor && !contactEditor && <div className="admin-feedback error directory-feedback" role="alert">{error}<button className="icon-button" onClick={() => setError('')}><X /></button></div>}
    <div className="directory-metrics company-directory-metrics">
      <MetricCard icon={<Building2 />} label="Всего компаний" value={totalStats.total} note="зарегистрировано в системе" tone="blue" />
      <MetricCard icon={<CheckCircle2 />} label="Активные" value={totalStats.active} note="доступны для обслуживания" tone="green" />
      <MetricCard icon={<AlertTriangle />} label="Без контактов" value={totalStats.without_contacts} note="нуждаются в добавлении контактов" tone="red" />
    </div>

    <section className="surface directory-workspace companies-workspace">
      <div className="contact-directory-toolbar company-directory-toolbar">
        <div className="search-field directory-search"><Search /><input aria-label="Поиск компаний" value={search} onChange={event => setSearch(event.target.value)} placeholder="Поиск по названию, ИНН или адресу" /></div>
        <select aria-label="Статус компании" value={status} onChange={event => setStatus(event.target.value as typeof status)}><option value="ALL">Все статусы</option><option value="ACTIVE">Активные</option><option value="INACTIVE">Неактивные</option></select>
        <button type="button" className={`button ghost ${showFilters ? 'active' : ''}`} aria-expanded={showFilters} aria-controls="company-advanced-filters" onClick={() => setShowFilters(value => !value)}><Filter /> Фильтры</button>
        {canManage && <button className="button primary" onClick={() => { setError(''); setCompanyEditor('NEW'); }}><Plus /> Добавить компанию</button>}
      </div>

      {showFilters && <div id="company-advanced-filters" className="contact-advanced-filters company-advanced-filters">
        <label>Открытые заявки<select value={openTickets} onChange={event => setOpenTickets(event.target.value as typeof openTickets)}><option value="ALL">Все</option><option value="YES">Есть</option><option value="NO">Нет</option></select></label>
        <label>Ответственный<select value={responsible} onChange={event => setResponsible(event.target.value)}><option value="">Все</option>{assignees.map(item => <option key={item.id} value={item.id}>{item.full_name}</option>)}</select></label>
        <label>Источник<select value={source} onChange={event => setSource(event.target.value as typeof source)}><option value="ALL">Все</option><option value="BITRIX24">Битрикс24</option><option value="PORTAL">Портал</option></select></label>
        <label>Синхронизация<select value={syncStatus} onChange={event => setSyncStatus(event.target.value as typeof syncStatus)}><option value="ALL">Все</option><option value="SYNCED">Синхронизировано</option><option value="PENDING">Ожидает</option><option value="ERROR">Ошибка</option><option value="ARCHIVED">Архив</option></select></label>
        <label>Сортировка<select value={sort} onChange={event => setSort(event.target.value as typeof sort)}><option value="NAME_ASC">Название (А–Я)</option><option value="NAME_DESC">Название (Я–А)</option><option value="UPDATED">Недавно изменённые</option></select></label>
        <button className="button ghost compact" onClick={() => { setOpenTickets('ALL'); setResponsible(''); setSource('ALL'); setSyncStatus('ALL'); setSort('NAME_ASC'); }}>Сбросить дополнительные фильтры</button>
      </div>}

      <div className="companies-master-detail">
        <div className="companies-table-wrap">
          {isLoading ? <TableSkeleton /> : <div className="companies-directory-list" onScroll={loadMoreCompanies}>
            <div className="company-directory-list-head"><strong>{visibleCompanies.length} компаний</strong><span>Выберите для просмотра</span></div>
            {pageCompanies.map(company => <button key={company.id} className={`companies-directory-row ${selectedCompanyId === company.id ? 'selected' : ''}`} onClick={() => selectCompany(company.id)}>
              <span className="company-directory-name">
                <DirectoryAvatar entity="company" id={company.id} name={company.name} hasImage={Boolean(company.logo_storage_key)} />
                <span className="company-directory-row__summary">
                  <b>{company.name}</b>
                  <span>{company.full_name || company.legal_form || 'Полное название не указано'}</span>
                  <span>{company.inn ? `ИНН ${company.inn}` : 'ИНН не указан'} · {company.responsible_name || company.primary_contact_name || 'Ответственный не назначен'}</span>
                </span>
              </span>
              <span className="company-directory-row__meta">
                <span className="company-directory-row__counts"><em title="Контакты"><Users />{Number(company.contacts_count || 0)}</em><em title="Открытые заявки"><TicketIcon />{Number(company.open_tickets || 0)}</em></span>
                <StatusDot active={company.is_active} activeLabel={Number(company.open_tickets) ? `${company.open_tickets} откр.` : 'Активна'} inactiveLabel="Неактивна" />
              </span>
              <ChevronRight />
            </button>)}
            {!pageCompanies.length && <EmptyState icon={<Building2 />} title="Компании не найдены" text="Измените фильтры или добавьте новую компанию" compact />}
            {pageCompanies.length > 0 && <div className="company-directory-load-state" role="status">
              {hasMoreCompanies ? `Показано ${pageCompanies.length} из ${visibleCompanies.length} · прокрутите ниже` : `Показаны все ${visibleCompanies.length}`}
            </div>}
          </div>}
        </div>

        <CompanyDetailWorkspace
          loading={companyLoading}
          emptyState={<EmptyState icon={<Building2 />} title="Выберите компанию" text="Здесь появятся контакты, заявки и параметры обслуживания" compact />}
          header={selectedCompany ? <>
            <div className="company-profile-hero">
              <DirectoryAvatar entity="company" id={selectedCompany.id} name={selectedCompany.name} hasImage={Boolean(selectedCompany.logo_storage_key)} className="company-profile-avatar" />
              <div className="company-profile-hero__identity">
                <h2>{selectedCompany.name}</h2>
                <p>{selectedCompany.full_name || [selectedCompany.legal_form, selectedCompany.name].filter(Boolean).join(' ')}</p>
                <DirectorySyncBadge source={selectedCompany.source_system} status={selectedCompany.sync_status} lastSyncedAt={selectedCompany.last_synced_at} compact />
              </div>
              <div className="company-profile-hero__actions">
                <StatusDot active={selectedCompany.is_active} activeLabel="Активна" inactiveLabel="Неактивна" />
                <div className="directory-detail-menu-wrap">
                  <button type="button" className="icon-button" title="Действия" aria-expanded={detailMenuOpen} aria-controls="company-detail-actions" onClick={() => setDetailMenuOpen(value => !value)}><MoreHorizontal /></button>
                  {detailMenuOpen && <div id="company-detail-actions" className="directory-detail-menu">
                    <button onClick={() => { setDetailMenuOpen(false); setCompanyActivityTab('TICKETS'); }}><TicketIcon /> Открыть заявки</button>
                    <button onClick={() => { setDetailMenuOpen(false); navigate(`/tickets?create=1&companyId=${selectedCompany.id}`); }}><Plus /> Создать заявку</button>
                    {canManage && <button onClick={() => { setDetailMenuOpen(false); setError(''); setCompanyEditor(selectedCompany); }}><PencilLine /> Редактировать</button>}
                    {canManage && <button className="danger" disabled={companyLifecycle.isPending} onClick={() => {
                      const sourceNotice = selectedCompany.source_system === 'BITRIX24'
                        ? ' Автоматическая синхронизация будет отключена; компанию можно будет снова выбрать в каталоге Битрикс24.'
                        : '';
                      if (window.confirm(`Удалить компанию из списка? Заявки и история сохранятся.${sourceNotice}`)) companyLifecycle.mutate({ id: selectedCompany.id, isActive: false });
                    }}><Trash2 /> Удалить компанию</button>}
                  </div>}
                </div>
              </div>
            </div>
            <div className="company-profile-facts">
              <div><small>Контакты</small><b>{selectedCompany.contacts?.length ?? selectedCompany.contacts_count ?? 0}</b></div>
              <div><small>Открытые заявки</small><b>{selectedCompany.open_tickets || 0}</b></div>
              <div><small>Уровень сервиса</small><b>{selectedCompany.service_level || 'STANDARD'}</b></div>
              <div><small>Обновлена</small><b>{formatShort(selectedCompany.updated_at)}</b></div>
            </div>
          </> : undefined}
          overview={selectedCompany ? <>
            <section className="company-overview-section company-overview-actions">
              <button className="button primary" onClick={() => navigate(`/tickets?create=1&companyId=${selectedCompany.id}`)}><Plus /> Создать заявку</button>
              {canManage && <button className="button ghost" onClick={() => { setError(''); setContactEditor('NEW'); }}><UserPlus /> Добавить контакт</button>}
              {selectedCompany.website && <a className="button ghost" href={selectedCompany.website.startsWith('http') ? selectedCompany.website : `https://${selectedCompany.website}`} target="_blank" rel="noreferrer"><ExternalLink /> Открыть сайт</a>}
            </section>

            <section className="company-overview-section">
              <div className="company-overview-section__head"><div><h3>Обслуживание</h3><p>Ответственный и уровень поддержки</p></div></div>
              <div className="company-service-summary">
                <div><span><UserRound /></span><small>Ответственный</small><b>{selectedCompany.responsible_name || 'Не назначен'}</b></div>
                <div><span><ShieldCheck /></span><small>Уровень сервиса</small><b>{selectedCompany.service_level || 'STANDARD'}</b></div>
              </div>
            </section>

            <section className="company-overview-section">
              <div className="company-overview-section__head"><div><h3>Реквизиты</h3><p>Юридические данные компании</p></div></div>
              <dl className="company-overview-facts-list">
                <div><dt>ИНН</dt><dd>{selectedCompany.inn || 'Не указан'}</dd></div>
                <div><dt>КПП</dt><dd>{selectedCompany.kpp || 'Не указан'}</dd></div>
                <div><dt>ОГРН</dt><dd>{selectedCompany.ogrn || 'Не указан'}</dd></div>
                <div><dt>Форма</dt><dd>{selectedCompany.legal_form || 'Не указана'}</dd></div>
              </dl>
            </section>

            <section className="company-overview-section">
              <div className="company-overview-section__head"><div><h3>Адреса</h3><p>Юридическое и фактическое местонахождение</p></div></div>
              <div className="company-address-list">
                {selectedCompany.addresses?.map(address => <div key={address.id}><Network /><span><b>{address.address_type === 'LEGAL' ? 'Юридический адрес' : address.address_type === 'POSTAL' ? 'Почтовый адрес' : address.address_type === 'ACTUAL' ? 'Фактический адрес' : 'Адрес'}</b><small>{address.full_address}</small></span></div>)}
                {!selectedCompany.addresses?.length && <>
                  <div><Network /><span><b>Юридический адрес</b><small>{selectedCompany.address || 'Не указан'}</small></span></div>
                  {selectedCompany.postal_address && <div><Mail /><span><b>Почтовый адрес</b><small>{selectedCompany.postal_address}</small></span></div>}
                </>}
              </div>
            </section>

            <section className="company-overview-section company-description-card">
              <div className="company-overview-section__head"><div><h3>О компании</h3><p>Описание и техническая среда</p></div></div>
              <p>{selectedCompany.description || 'Описание компании пока не заполнено.'}</p>
              <div><Network /><span><small>Инфраструктура</small><b>{selectedCompany.infrastructure_description || 'Техническая справка не заполнена.'}</b></span></div>
            </section>

            <details className="company-overview-details">
              <summary>Дополнительные данные</summary>
              <div className="company-overview-details__body">
                <DirectoryCustomFields entityType="COMPANY" entityId={selectedCompany.id} initialFields={selectedCompany.custom_fields} isAdmin={canManage} />
                {canManage && <DirectoryDuplicates entityType="COMPANY" entityId={selectedCompany.id} entitySource={selectedCompany.source_system} onMerged={selectCompany} />}
              </div>
            </details>

          </> : undefined}
          activity={selectedCompany ? <CompanyActivityPanel
            key={selectedCompany.id}
            activeTab={companyActivityTab}
            onActiveTabChange={setCompanyActivityTab}
            contactsCount={selectedCompany.contacts?.length ?? selectedCompany.contacts_count ?? 0}
            ticketsCount={selectedCompany.open_tickets || 0}
            contacts={<div className="company-activity-section">
              <div className="company-activity-section__head"><div><h3>Контакты компании</h3><p>Лица, телефоны, email и мессенджеры</p></div>{canManage && <button className="button primary compact" onClick={() => { setError(''); setContactEditor('NEW'); }}><Plus /> Добавить контакт</button>}</div>
              <div className="company-contact-list">{selectedCompany.contacts?.map(contact => {
                const phone = contactPhone(contact);
                const email = contactEmail(contact);
                return <article key={contact.id} className={!contact.is_active ? 'inactive' : ''}>
                  <button type="button" className="company-contact-open" onClick={() => navigate(`/contacts?contact=${contact.id}`)}>
                    <DirectoryAvatar entity="contact" id={contact.id} name={contact.full_name || 'Контакт'} hasImage={Boolean(contact.photo_storage_key)} className="contact-avatar" />
                    <div><b>{contact.full_name || 'Без имени'}{contact.is_primary && <em>Основной</em>}</b><small>{contact.position || contact.department || 'Должность не указана'}</small><p>{email || 'Email не указан'}<span><Phone />{phone || 'Телефон не указан'}</span></p></div>
                  </button>
                  <div className="contact-row-actions">
                    {email && <a className="icon-button" href={`mailto:${email}`} title="Написать"><Mail /></a>}
                    {phone && <button type="button" className="icon-button" title="Позвонить через Билайн АТС" onClick={() => setDialTarget({ phones: contactPhones(contact), contactId: contact.id, companyId: contact.company_id || selectedCompany.id, contactName: contact.full_name || 'Контакт', companyName: selectedCompany.name })}><Phone /></button>}
                    {canManage && <button className="icon-button" onClick={() => { setError(''); setContactEditor(contact); }} title="Редактировать"><PencilLine /></button>}
                    {canManage && <button className="icon-button danger" title="Удалить контакт из компании" disabled={removeCompanyContact.isPending} onClick={() => {
                      const note = contact.source_system === 'BITRIX24' ? ' Контакт не будет возвращён синхронизацией.' : ' Его можно будет восстановить в справочнике контактов.';
                      if (window.confirm(`Удалить контакт из компании? Заявки и история сохранятся.${note}`)) removeCompanyContact.mutate({ id: contact.id, companyId: selectedCompany.id });
                    }}><Trash2 /></button>}
                  </div>
                </article>;
              })}{!selectedCompany.contacts?.length && <EmptyState icon={<Users />} title="Контактов пока нет" text="Добавьте представителей компании и их каналы связи" compact />}</div>
            </div>}
            tickets={<div className="company-activity-section">
              <div className="company-activity-section__head"><div><h3>Заявки компании</h3><p>Последние обращения и текущие статусы</p></div><button className="button primary compact" onClick={() => navigate(`/tickets?create=1&companyId=${selectedCompany.id}`)}><Plus /> Создать заявку</button></div>
              <div className="directory-ticket-list company-ticket-list">{selectedCompany.recent_tickets?.map(ticket => <button key={ticket.id} onClick={() => navigate(`/tickets/${ticket.id}`)}><span><b>{ticket.display_number}</b><small>{ticket.subject}</small><PriorityBadge priority={ticket.priority} /></span><StatusBadge status={ticket.status} /><time>{formatDateTime(ticket.updated_at)}</time><ChevronRight /></button>)}{!selectedCompany.recent_tickets?.length && <EmptyState icon={<TicketIcon />} title="Заявок пока нет" text="Создайте первую заявку для этой компании" compact />}</div>
            </div>}
            history={<div className="company-history-workspace"><ClientInteractionHistory scope="company" entityId={selectedCompany.id} contacts={selectedCompany.contacts || []} /><DirectoryChangeHistory entityType="COMPANY" entityId={selectedCompany.id} initialHistory={selectedCompany.change_history} /></div>}
          /> : undefined}
        />
      </div>
    </section>

    {dialTarget && <TelephonyDialModal target={dialTarget} onClose={() => setDialTarget(null)} />}
    {companyEditor && <CompanyEditorModal company={companyEditor === 'NEW' ? undefined : companyEditor} assignees={assignees} pending={saveCompany.isPending} error={error} onClose={() => { if (!saveCompany.isPending) setCompanyEditor(null); }} onSubmit={payload => saveCompany.mutate({ id: companyEditor === 'NEW' ? undefined : companyEditor.id, payload })} />}
    {contactEditor && <ContactEditorModal contact={contactEditor === 'NEW' ? undefined : contactEditor} companies={companies} defaultCompanyId={selectedCompany?.id} pending={saveContact.isPending} error={error} onClose={() => { if (!saveContact.isPending) setContactEditor(null); }} onSubmit={payload => saveContact.mutate({ id: contactEditor === 'NEW' ? undefined : contactEditor.id, payload })} />}
  </div>;
}

function Contacts() {
  const contactBatchSize = 50;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [companyId, setCompanyId] = useState(searchParams.get('company') || '');
  const [status, setStatus] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [showFilters, setShowFilters] = useState(false);
  const [channelFilter, setChannelFilter] = useState('');
  const [preferredFilter, setPreferredFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [syncFilter, setSyncFilter] = useState('');
  const [openOnly, setOpenOnly] = useState(false);
  const [editor, setEditor] = useState<CompanyContact | null | 'NEW'>(null);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [dialTarget, setDialTarget] = useState<TelephonyDialTarget | null>(null);
  const [detailMenuOpen, setDetailMenuOpen] = useState(false);
  const [visibleContactCount, setVisibleContactCount] = useState(contactBatchSize);
  const canManage = userHasRole(user, 'ADMIN');

  const { data: companies = [] } = useQuery({ queryKey: ['companies'], queryFn: () => api<Company[]>('/companies') });
  const { data: contacts = [], isLoading } = useQuery({ queryKey: ['contacts'], queryFn: () => api<CompanyContact[]>('/contacts') });
  const { data: stats } = useQuery({ queryKey: ['contacts', 'stats'], queryFn: () => api<ContactStats>('/contacts/stats') });

  const visibleContacts = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return contacts.filter(contact => {
      if (companyId && contact.company_id !== companyId) return false;
      if (status === 'ACTIVE' && !contact.is_active) return false;
      if (status === 'INACTIVE' && contact.is_active) return false;
      if (openOnly && !Number(contact.open_tickets)) return false;
      if (sourceFilter && (contact.source_system || 'PORTAL') !== sourceFilter) return false;
      if (syncFilter && contact.sync_status !== syncFilter) return false;
      if (preferredFilter && String(contact.preferred_channel || '').toUpperCase() !== preferredFilter) return false;
      if (channelFilter) {
        const hasChannel = contact.channels.some(item => item.is_active !== false && item.channel_type === channelFilter)
          || (channelFilter === 'PHONE' && Boolean(contact.phone))
          || (channelFilter === 'EMAIL' && Boolean(contact.email));
        if (!hasChannel) return false;
      }
      if (!needle) return true;
      return [contact.full_name, contact.company_name, contact.position, contact.department, contact.phone, contact.email, ...contact.channels.map(item => item.value)]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(needle));
    });
  }, [contacts, search, companyId, status, openOnly, preferredFilter, channelFilter, sourceFilter, syncFilter]);

  useEffect(() => setVisibleContactCount(contactBatchSize), [search, companyId, status, openOnly, preferredFilter, channelFilter, sourceFilter, syncFilter]);
  const pageRows = useMemo(() => visibleContacts.slice(0, visibleContactCount), [visibleContacts, visibleContactCount]);
  const hasMoreContacts = pageRows.length < visibleContacts.length;

  function loadMoreContacts(event: UIEvent<HTMLDivElement>) {
    if (!hasMoreContacts) return;
    const list = event.currentTarget;
    const distanceToBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
    if (distanceToBottom > 120) return;
    setVisibleContactCount(current => Math.min(current + contactBatchSize, visibleContacts.length));
  }
  const selectedContactId = searchParams.get('contact') || pageRows[0]?.id || visibleContacts[0]?.id || '';
  const { data: selectedContact, isLoading: detailLoading } = useQuery({
    queryKey: ['contact', selectedContactId],
    queryFn: () => api<CompanyContact>(`/contacts/${selectedContactId}`),
    enabled: Boolean(selectedContactId),
  });

  useEffect(() => {
    if (!selectedContactId || searchParams.get('contact')) return;
    const next = new URLSearchParams(searchParams);
    next.set('contact', selectedContactId);
    setSearchParams(next, { replace: true });
  }, [selectedContactId]);

  useEffect(() => setDetailMenuOpen(false), [selectedContactId]);

  function selectContact(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('contact', id);
    setSearchParams(next, { replace: true });
    setDetailMenuOpen(false);
  }

  async function copyChannel(channel: ContactChannel) {
    try {
      await navigator.clipboard.writeText(channel.value);
      setFeedback(`${contactChannelLabel(channel.channel_type)} скопирован: ${channel.value}`);
    } catch {
      setFeedback(`${contactChannelLabel(channel.channel_type)}: ${channel.value}`);
    }
  }

  const save = useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: Record<string, unknown> }) => api<CompanyContact>(id ? `/contacts/${id}` : '/contacts', { method: id ? 'PATCH' : 'POST', body: JSON.stringify(payload) }),
    onSuccess: async contact => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts', 'stats'] }),
        queryClient.invalidateQueries({ queryKey: ['contact', contact.id] }),
        queryClient.invalidateQueries({ queryKey: ['companies'] }),
        contact.company_id ? queryClient.invalidateQueries({ queryKey: ['company', contact.company_id] }) : Promise.resolve(),
      ]);
      setEditor(null);
      selectContact(contact.id);
      setFeedback('Контакт сохранён');
      setError('');
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить контакт'),
  });

  const contactLifecycle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; companyId?: string | null; isActive: boolean; remove?: boolean }) => api<CompanyContact>(
      isActive ? `/contacts/${id}/status` : `/contacts/${id}`,
      isActive
        ? { method: 'PATCH', body: JSON.stringify({ isActive: true }) }
        : { method: 'DELETE' },
    ),
    onMutate: () => {
      setFeedback('');
      setError('');
    },
    onSuccess: async (contact, variables) => {
      setDetailMenuOpen(false);
      if (variables.remove) {
        const next = new URLSearchParams(searchParams);
        next.delete('contact');
        setSearchParams(next, { replace: true });
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts', 'stats'] }),
        variables.remove ? Promise.resolve() : queryClient.invalidateQueries({ queryKey: ['contact', contact.id] }),
        queryClient.invalidateQueries({ queryKey: ['companies'] }),
        variables.companyId ? queryClient.invalidateQueries({ queryKey: ['company', variables.companyId] }) : Promise.resolve(),
      ]);
      setFeedback(variables.isActive ? 'Контакт восстановлен' : variables.remove ? 'Контакт удалён из списка' : 'Контакт отключён');
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось изменить состояние контакта'),
  });

  const summary = stats ?? {
    total: contacts.length,
    active: contacts.filter(item => item.is_active).length,
    inactive: contacts.filter(item => !item.is_active).length,
    without_company: contacts.filter(item => !item.company_id).length,
    without_channel: contacts.filter(item => !contactPhone(item) && !contactEmail(item) && !item.channels.length).length,
  };

  const selectedPhone = contactPhone(selectedContact);
  const selectedEmail = contactEmail(selectedContact);
  const selectedCommunicationChannels = useMemo<ContactChannel[]>(() => {
    if (!selectedContact) return [];
    const rows = selectedContact.channels.filter(channel => channel.is_active !== false && channel.value.trim());
    const addFallback = (channel_type: ContactChannel['channel_type'], value?: string | null) => {
      const normalized = String(value || '').trim();
      if (!normalized || rows.some(channel => channel.channel_type === channel_type && channel.value.trim().toLowerCase() === normalized.toLowerCase())) return;
      rows.unshift({ channel_type, value: normalized, is_primary: true, is_active: true });
    };
    addFallback('EMAIL', selectedContact.email);
    addFallback('PHONE', selectedContact.phone);
    return rows
      .filter((channel, index, all) => all.findIndex(other => `${other.channel_type}:${other.value.trim().toLowerCase()}` === `${channel.channel_type}:${channel.value.trim().toLowerCase()}`) === index)
      .sort((left, right) => Number(Boolean(right.is_primary)) - Number(Boolean(left.is_primary)) || Number(left.sort_order || 0) - Number(right.sort_order || 0));
  }, [selectedContact]);

  return <div className="directory-page contacts-directory-page">
    <PageHeading eyebrow="КОНТАКТЫ" title="Контакты" description="Телефоны, email и мессенджеры представителей клиентов" />
    {feedback && <div className="admin-feedback success directory-feedback">{feedback}<button className="icon-button" onClick={() => setFeedback('')}><X /></button></div>}
    {error && !editor && <div className="admin-feedback error directory-feedback" role="alert">{error}<button className="icon-button" onClick={() => setError('')}><X /></button></div>}

    <div className="directory-metrics contacts-directory-metrics">
      <MetricCard icon={<Users />} label="Всего контактов" value={summary.total} note="зарегистрировано в портале" tone="blue" />
      <MetricCard icon={<CheckCircle2 />} label="Активные" value={summary.active} note="доступны для связи" tone="green" />
      <MetricCard icon={<UserPlus />} label="Без компании" value={summary.without_company} note="не привязаны к компании" tone="yellow" />
      <MetricCard icon={<AlertTriangle />} label="Без канала связи" value={summary.without_channel} note="нет телефона или email" tone="red" />
    </div>

    <section className="surface directory-workspace contacts-workspace">
      <div className="contact-directory-toolbar">
        <div className="search-field directory-search"><Search /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Поиск по имени, телефону, email..." /></div>
        <select value={companyId} onChange={event => setCompanyId(event.target.value)}><option value="">Все компании</option>{companies.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}</select>
        <select value={status} onChange={event => setStatus(event.target.value as typeof status)}><option value="ALL">Все статусы</option><option value="ACTIVE">Активные</option><option value="INACTIVE">Неактивные</option></select>
        <button className={`button ghost ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(value => !value)}><Filter /> Фильтры</button>
        {canManage && <button className="button primary" onClick={() => { setError(''); setEditor('NEW'); }}><Plus /> Добавить контакт</button>}
      </div>

      {showFilters && <div className="contact-advanced-filters">
        <label>Есть канал<select value={channelFilter} onChange={event => setChannelFilter(event.target.value)}><option value="">Любой</option><option value="PHONE">Телефон</option><option value="EMAIL">Email</option><option value="TELEGRAM">Telegram</option><option value="MAX">MAX</option><option value="WHATSAPP">WhatsApp</option><option value="VIBER">Viber</option><option value="SKYPE">Skype</option></select></label>
        <label>Предпочтительный<select value={preferredFilter} onChange={event => setPreferredFilter(event.target.value)}><option value="">Любой</option><option value="PHONE">Телефон</option><option value="EMAIL">Email</option><option value="TELEGRAM">Telegram</option><option value="MAX">MAX</option><option value="WHATSAPP">WhatsApp</option><option value="VIBER">Viber</option><option value="SKYPE">Skype</option></select></label>
        <label>Источник<select value={sourceFilter} onChange={event => setSourceFilter(event.target.value)}><option value="">Все</option><option value="BITRIX24">Битрикс24</option><option value="PORTAL">Портал</option></select></label>
        <label>Синхронизация<select value={syncFilter} onChange={event => setSyncFilter(event.target.value)}><option value="">Все</option><option value="SYNCED">Синхронизировано</option><option value="PENDING">Ожидает</option><option value="ERROR">Ошибка</option><option value="ARCHIVED">Архив</option></select></label>
        <label className="contact-open-filter"><input type="checkbox" checked={openOnly} onChange={event => setOpenOnly(event.target.checked)} /><span>Только с открытыми заявками</span></label>
        <button className="button ghost compact" onClick={() => { setChannelFilter(''); setPreferredFilter(''); setSourceFilter(''); setSyncFilter(''); setOpenOnly(false); }}>Сбросить дополнительные фильтры</button>
      </div>}

      <div className="contacts-master-detail">
        <div className="contacts-table-wrap">
          {isLoading ? <TableSkeleton /> : <div className="contacts-directory-table" onScroll={loadMoreContacts}>
            <div className="contact-directory-list-head"><strong>{visibleContacts.length} контактов</strong><span>Выберите для просмотра</span></div>
            {pageRows.map(contact => {
              const phone = contactPhone(contact);
              const email = contactEmail(contact);
              const messengers = messengerChannels(contact);
              return <button key={contact.id} className={`contacts-directory-row ${selectedContactId === contact.id ? 'selected' : ''}`} onClick={() => selectContact(contact.id)}>
                <span className="contact-directory-name">
                  <DirectoryAvatar entity="contact" id={contact.id} name={contact.full_name || 'Контакт'} hasImage={Boolean(contact.photo_storage_key)} />
                  <span className="contact-directory-row__summary">
                    <b>{contact.full_name || 'Без имени'}</b>
                    <span>{contact.position || contact.department || contact.company_name || 'Должность не указана'}</span>
                    <span>{contact.company_name || phone || email || 'Компания не назначена'}</span>
                  </span>
                </span>
                <span className="contact-directory-row__meta">
                  <span className="contact-directory-row__channels">
                    {phone && <em title={phone}><Phone /></em>}
                    {email && <em title={email}><Mail /></em>}
                    {messengers.slice(0, 2).map(channel => <em key={channel.id || `${channel.channel_type}:${channel.value}`} title={`${contactChannelLabel(channel.channel_type)}: ${channel.value}`}>{channelIcon(channel.channel_type)}</em>)}
                  </span>
                  <StatusDot active={contact.is_active} activeLabel={Number(contact.open_tickets) ? `${contact.open_tickets} откр.` : 'Активен'} inactiveLabel="Неактивен" />
                </span>
                <ChevronRight />
              </button>;
            })}
            {!pageRows.length && <EmptyState icon={<Users />} title="Контакты не найдены" text="Измените фильтры или добавьте новый контакт" compact />}
            {pageRows.length > 0 && <div className="contact-directory-load-state" role="status">
              {hasMoreContacts ? `Показано ${pageRows.length} из ${visibleContacts.length} · прокрутите ниже` : `Показаны все ${visibleContacts.length}`}
            </div>}
          </div>}
        </div>

        <ContactDetailWorkspace
          loading={detailLoading}
          emptyState={<EmptyState icon={<Users />} title="Выберите контакт" text="Здесь появятся каналы связи, заявки и история взаимодействий" compact />}
          header={selectedContact ? <>
            <div className="contact-profile-hero">
              <DirectoryAvatar entity="contact" id={selectedContact.id} name={selectedContact.full_name || 'Контакт'} hasImage={Boolean(selectedContact.photo_storage_key)} className="contact-profile-avatar" />
              <div className="contact-profile-hero__identity">
                <h2>{selectedContact.full_name || 'Без имени'}</h2>
                <p>{[selectedContact.position, selectedContact.department].filter(Boolean).join(' · ') || 'Должность не указана'}</p>
                <DirectorySyncBadge source={selectedContact.source_system} status={selectedContact.sync_status} lastSyncedAt={selectedContact.last_synced_at} />
              </div>
              <div className="contact-profile-hero__actions">
                <StatusDot active={selectedContact.is_active} activeLabel="Активен" inactiveLabel="Неактивен" />
                {canManage && <div className="directory-detail-menu-wrap">
                  <button type="button" className="icon-button" title="Действия с контактом" aria-expanded={detailMenuOpen} aria-controls="contact-detail-actions" onClick={() => setDetailMenuOpen(value => !value)}><MoreHorizontal /></button>
                  {detailMenuOpen && <div id="contact-detail-actions" className="directory-detail-menu">
                    <button onClick={() => { setDetailMenuOpen(false); setError(''); setEditor(selectedContact); }}><PencilLine /> Редактировать</button>
                    {selectedContact.source_system === 'BITRIX24'
                      ? <button className="danger" disabled={contactLifecycle.isPending} onClick={() => { if (window.confirm('Удалить контакт из портала? Заявки и история сохранятся, повторная синхронизация из Битрикс24 будет отключена.')) contactLifecycle.mutate({ id: selectedContact.id, companyId: selectedContact.company_id, isActive: false, remove: true }); }}><Trash2 /> Удалить контакт</button>
                      : (selectedContact.is_active
                      ? <button className="danger" disabled={contactLifecycle.isPending} onClick={() => { if (window.confirm('Отключить контакт? Заявки и история сохранятся.')) contactLifecycle.mutate({ id: selectedContact.id, companyId: selectedContact.company_id, isActive: false }); }}><Ban /> Отключить контакт</button>
                      : <button className="restore" disabled={contactLifecycle.isPending} onClick={() => contactLifecycle.mutate({ id: selectedContact.id, companyId: selectedContact.company_id, isActive: true })}><RefreshCw /> Восстановить контакт</button>)}
                  </div>}
                </div>}
              </div>
            </div>
            <div className="contact-profile-facts">
              <div><small>Компания</small><b>{selectedContact.company_name || 'Не назначена'}</b></div>
              <div><small>Открытые заявки</small><b>{selectedContact.open_tickets || 0}</b></div>
              <div><small>Каналы связи</small><b>{selectedCommunicationChannels.length}</b></div>
              <div><small>Обновлён</small><b>{formatShort(selectedContact.updated_at)}</b></div>
            </div>
          </> : undefined}
          overview={selectedContact ? <>
            <section className="contact-overview-section">
              <div className="contact-profile-actions contact-overview-actions">
                {selectedPhone && <button type="button" className="button primary" onClick={() => setDialTarget({ phones: contactPhones(selectedContact), contactId: selectedContact.id, companyId: selectedContact.company_id || undefined, contactName: selectedContact.full_name || 'Контакт', companyName: selectedContact.company_name || undefined })}><Phone /> Позвонить через АТС</button>}
                {selectedEmail && <a className="button ghost" href={`mailto:${selectedEmail}`}><Mail /> Написать</a>}
                <button className="button ghost" disabled={!selectedContact.company_id} onClick={() => navigate(`/tickets?create=1&companyId=${selectedContact.company_id || ''}&contactId=${selectedContact.id}`)}><Plus /> Создать заявку</button>
              </div>
            </section>

            <section className="contact-overview-section">
              <div className="contact-overview-section__head"><div><h3>Каналы связи</h3><p>Актуальные способы связи с контактом</p></div></div>
              <div className="contact-channel-list">
                {selectedCommunicationChannels.map(channel => <div className="contact-channel-item" key={channel.id || `${channel.channel_type}:${channel.value}`}>
                  <span className="contact-channel-item__icon">{channelIcon(channel.channel_type)}</span>
                  <span><small>{contactChannelLabel(channel.channel_type)}{channel.is_primary ? ' · основной' : ''}</small><b title={channel.value}>{channel.value}</b></span>
                  <button type="button" className="icon-button" title={`Скопировать ${channel.value}`} onClick={() => copyChannel(channel)}><Link2 /></button>
                </div>)}
                {!selectedCommunicationChannels.length && <EmptyState icon={<MessageSquare />} title="Каналы не указаны" text="Добавьте телефон, email или мессенджер" compact />}
              </div>
            </section>

            <section className="contact-overview-section">
              <div className="contact-overview-section__head"><div><h3>Данные контакта</h3><p>Организация и предпочтения связи</p></div></div>
              <button type="button" className="contact-profile-company contact-overview-company" disabled={!selectedContact.company_id} onClick={() => selectedContact.company_id && navigate(`/companies?company=${selectedContact.company_id}`)}><Building2 /><span>{selectedContact.company_name || 'Компания не назначена'}</span><ChevronRight /></button>
              <dl className="contact-overview-facts-list">
                <div><dt>Предпочтительный канал</dt><dd>{contactChannelLabel(selectedContact.preferred_channel)}</dd></div>
                <div><dt>Рабочее время</dt><dd>{selectedContact.working_hours || 'Не указано'}</dd></div>
                <div><dt>Часовой пояс</dt><dd>{selectedContact.timezone || 'Не указан'}</dd></div>
                <div><dt>Заметка</dt><dd>{selectedContact.notes || 'Нет заметок'}</dd></div>
              </dl>
            </section>

            <section className="contact-overview-section contact-recent-tickets">
              <div className="contact-overview-section__head"><div><h3>Последние заявки</h3><p>Обращения, связанные с контактом</p></div><button className="soft-button" onClick={() => navigate(`/tickets?search=${encodeURIComponent(selectedContact.full_name || '')}`)}>Все</button></div>
              {selectedContact.recent_tickets?.map(ticket => <button key={ticket.id} onClick={() => navigate(`/tickets/${ticket.id}`)}><span><small>{ticket.display_number}</small><b>{ticket.subject}</b><em>Обновлено {formatShort(ticket.updated_at)}</em></span><StatusBadge status={ticket.status} /></button>)}
              {!selectedContact.recent_tickets?.length && <EmptyState icon={<TicketIcon />} title="Связанных заявок нет" text="Создайте заявку из карточки контакта" compact />}
            </section>

            {Boolean(selectedContact.addresses?.length) && <section className="contact-overview-section contact-profile-addresses">
              <div className="contact-overview-section__head"><div><h3>Адреса</h3><p>Синхронизированы с карточкой контакта</p></div></div>
              {selectedContact.addresses?.map(address => <div key={address.id}><Network /><span><b>{address.address_type === 'WORK' ? 'Рабочий адрес' : address.address_type === 'HOME' ? 'Домашний адрес' : 'Адрес'}</b><small>{address.full_address}</small></span></div>)}
            </section>}

            <details className="contact-overview-details">
              <summary>Дополнительные данные</summary>
              <div className="contact-overview-details__body">
                <DirectoryCustomFields entityType="CONTACT" entityId={selectedContact.id} initialFields={selectedContact.custom_fields} isAdmin={canManage} />
                {canManage && <DirectoryDuplicates entityType="CONTACT" entityId={selectedContact.id} entitySource={selectedContact.source_system} onMerged={selectContact} />}
                <DirectoryChangeHistory entityType="CONTACT" entityId={selectedContact.id} initialHistory={selectedContact.change_history} />
              </div>
            </details>

          </> : undefined}
          activity={selectedContact ? <ClientInteractionHistory scope="contact" entityId={selectedContact.id} /> : undefined}
        />
      </div>
    </section>

    {dialTarget && <TelephonyDialModal target={dialTarget} onClose={() => setDialTarget(null)} />}
    {editor && <ContactEditorModal contact={editor === 'NEW' ? undefined : editor} companies={companies} defaultCompanyId={companyId || undefined} pending={save.isPending} error={error} onClose={() => { if (!save.isPending) setEditor(null); }} onSubmit={payload => save.mutate({ id: editor === 'NEW' ? undefined : editor.id, payload })} />}
  </div>;
}

function CompanyEditorModal({ company, assignees, pending, error, onClose, onSubmit }: { company?: Company; assignees: Assignee[]; pending: boolean; error: string; onClose: () => void; onSubmit: (payload: Record<string, unknown>) => void }) {
  const linked = company?.source_system === 'BITRIX24';
  useEffect(() => { const listener = (event: globalThis.KeyboardEvent) => { if (event.key === 'Escape' && !pending) onClose(); }; window.addEventListener('keydown', listener); return () => window.removeEventListener('keydown', listener); }, [pending, onClose]);
  function submit(event: FormEvent<HTMLFormElement>) { event.preventDefault(); const form = new FormData(event.currentTarget); onSubmit({ name: linked ? company?.name : String(form.get('name') || '').trim(), fullName: linked ? company?.full_name : String(form.get('fullName') || '').trim() || undefined, legalForm: linked ? company?.legal_form : String(form.get('legalForm') || '').trim() || undefined, inn: linked ? company?.inn : String(form.get('inn') || '').trim() || undefined, kpp: linked ? company?.kpp : String(form.get('kpp') || '').trim() || undefined, ogrn: linked ? company?.ogrn : String(form.get('ogrn') || '').trim() || undefined, address: linked ? company?.address : String(form.get('address') || '').trim() || undefined, postalAddress: linked ? company?.postal_address : String(form.get('postalAddress') || '').trim() || undefined, website: linked ? company?.website : String(form.get('website') || '').trim() || undefined, responsibleUserId: String(form.get('responsibleUserId') || '') || undefined, serviceLevel: String(form.get('serviceLevel') || 'STANDARD'), description: String(form.get('description') || '').trim() || undefined, infrastructureDescription: String(form.get('infrastructureDescription') || '').trim() || undefined, ...(!company ? { isActive: form.get('isActive') === 'on' } : {}) }); }
  return <div className="modal-backdrop directory-editor-backdrop" onMouseDown={event => { if (event.currentTarget === event.target && !pending) onClose(); }}><form className="modal-card directory-editor-modal" onSubmit={submit} onMouseDown={event => event.stopPropagation()}><div className="modal-head directory-editor-head"><div><span className="modal-kicker">СПРАВОЧНИК КОМПАНИЙ</span><h2>{company ? 'Редактирование компании' : 'Новая компания'}</h2><p>Реквизиты, ответственный, обслуживание и техническая справка.</p></div><button type="button" className="icon-button" onClick={onClose} disabled={pending}><X /></button></div>{linked && <div className="directory-master-notice"><LockKeyhole /> Юридические и контактные поля доступны только для чтения и обновляются из Битрикс24.</div>}<div className="directory-editor-content"><section className="directory-editor-section wide"><div className="planned-section-title"><span><Building2 /></span><div><h3>Основная информация</h3><p>Название и юридическая форма</p></div></div><div className="form-grid three"><label>Краткое название *<input name="name" required minLength={2} defaultValue={company?.name || ''} autoFocus readOnly={linked} /></label><label>Юридическая форма<input name="legalForm" defaultValue={company?.legal_form || ''} placeholder="ООО, ИП, АО" readOnly={linked} /></label><label>Полное наименование<input name="fullName" defaultValue={company?.full_name || ''} readOnly={linked} /></label></div></section><section className="directory-editor-section"><div className="planned-section-title"><span><FileText /></span><div><h3>Реквизиты</h3><p>ИНН, КПП и ОГРН</p></div></div><div className="form-grid three"><label>ИНН<input name="inn" defaultValue={company?.inn || ''} readOnly={linked} /></label><label>КПП<input name="kpp" defaultValue={company?.kpp || ''} readOnly={linked} /></label><label>ОГРН<input name="ogrn" defaultValue={company?.ogrn || ''} readOnly={linked} /></label></div><label>Описание<textarea name="description" rows={4} defaultValue={company?.description || ''} /></label></section><section className="directory-editor-section"><div className="planned-section-title"><span><Network /></span><div><h3>Адреса и связь</h3><p>Местонахождение и сайт</p></div></div><label>Юридический адрес<input name="address" defaultValue={company?.address || ''} readOnly={linked} /></label><label>Почтовый адрес<input name="postalAddress" defaultValue={company?.postal_address || ''} readOnly={linked} /></label><label>Сайт<input name="website" defaultValue={company?.website || ''} placeholder="https://company.ru" readOnly={linked} /></label></section><section className="directory-editor-section wide"><div className="planned-section-title"><span><ShieldCheck /></span><div><h3>Обслуживание</h3><p>Ответственный, SLA и инфраструктура</p></div></div><div className="form-grid two"><label>Ответственный<select name="responsibleUserId" defaultValue={company?.responsible_user_id || ''}><option value="">Не назначен</option>{assignees.map(item => <option key={item.id} value={item.id}>{item.full_name}</option>)}</select></label><label>Уровень обслуживания<select name="serviceLevel" defaultValue={company?.service_level || 'STANDARD'}><option value="STANDARD">Стандарт</option><option value="PRIORITY">Приоритет</option><option value="PREMIUM">Премиум</option><option value="CUSTOM">Индивидуальный</option></select></label></div><label>Описание инфраструктуры<textarea name="infrastructureDescription" rows={5} defaultValue={company?.infrastructure_description || ''} placeholder="Серверы, 1С, сеть, почта, VPN и ключевые сервисы" /></label><label className="company-active-switch-v2"><span><b>Компания активна</b><small>{linked ? 'Статус управляется синхронизацией Битрикс24' : 'Разрешить создание и обслуживание заявок'}</small></span><input type="checkbox" name="isActive" defaultChecked={company?.is_active ?? true} disabled={linked} /></label></section></div>{error && <div className="form-error directory-editor-error">{error}</div>}<div className="directory-editor-actions"><button type="button" className="button ghost" onClick={onClose} disabled={pending}>Отмена</button><button className="button primary" disabled={pending}><Save /> {pending ? 'Сохраняем…' : 'Сохранить компанию'}</button></div></form></div>;
}

function ContactEditorModal({ contact, companies, defaultCompanyId, pending, error, onClose, onSubmit }: { contact?: CompanyContact; companies: Company[]; defaultCompanyId?: string; pending: boolean; error: string; onClose: () => void; onSubmit: (payload: Record<string, unknown>) => void }) {
  const linked = contact?.source_system === 'BITRIX24';
  const initialChannels = useMemo(() => {
    if (contact?.channels?.length) return contact.channels.map(item => ({ channelType: item.channel_type, value: item.value, label: item.label || '', isPrimary: Boolean(item.is_primary), isActive: item.is_active !== false }));
    const rows: Array<{ channelType: string; value: string; label: string; isPrimary: boolean; isActive: boolean }> = [];
    if (contact?.phone) rows.push({ channelType: 'PHONE', value: contact.phone, label: 'Основной телефон', isPrimary: true, isActive: true });
    if (contact?.email) rows.push({ channelType: 'EMAIL', value: contact.email, label: 'Основной email', isPrimary: true, isActive: true });
    return rows.length ? rows : [{ channelType: 'PHONE', value: '', label: '', isPrimary: true, isActive: true }];
  }, [contact]);
  const [channels, setChannels] = useState(initialChannels);
  useEffect(() => { const listener = (event: globalThis.KeyboardEvent) => { if (event.key === 'Escape' && !pending) onClose(); }; window.addEventListener('keydown', listener); return () => window.removeEventListener('keydown', listener); }, [pending, onClose]);
  function updateChannel(index: number, patch: Partial<(typeof channels)[number]>) { setChannels(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item)); }
  function submit(event: FormEvent<HTMLFormElement>) { event.preventDefault(); const form = new FormData(event.currentTarget); onSubmit({ companyId: linked ? contact?.company_id || undefined : String(form.get('companyId') || '') || undefined, fullName: linked ? contact?.full_name : String(form.get('fullName') || '').trim(), position: linked ? contact?.position : String(form.get('position') || '').trim() || undefined, department: String(form.get('department') || '').trim() || undefined, contactType: String(form.get('contactType') || 'CONTACT'), preferredChannel: String(form.get('preferredChannel') || 'EMAIL'), workingHours: String(form.get('workingHours') || '').trim() || undefined, timezone: String(form.get('timezone') || '').trim() || undefined, notes: String(form.get('notes') || '').trim() || undefined, isPrimary: form.get('isPrimary') === 'on', ...(!contact ? { isActive: form.get('isActive') === 'on' } : {}), channels: channels.filter(item => item.value.trim()).map(item => ({ ...item, value: item.value.trim(), label: item.label.trim() || undefined })) }); }
  if (linked && contact) return <LinkedContactEditorModal contact={contact} pending={pending} error={error} onClose={onClose} onSubmit={onSubmit} />;
  return <div className="modal-backdrop directory-editor-backdrop" onMouseDown={event => { if (event.currentTarget === event.target && !pending) onClose(); }}><form className="modal-card directory-editor-modal contact-editor-modal" onSubmit={submit} onMouseDown={event => event.stopPropagation()}><div className="modal-head directory-editor-head"><div><span className="modal-kicker">СПРАВОЧНИК КОНТАКТОВ</span><h2>{contact ? 'Редактирование контакта' : 'Новый контакт'}</h2><p>Компания, должность, телефоны, email и мессенджеры.</p></div><button type="button" className="icon-button" onClick={onClose} disabled={pending}><X /></button></div><div className="directory-editor-content"><section className="directory-editor-section wide"><div className="planned-section-title"><span><Users /></span><div><h3>Контактное лицо</h3><p>Основные сведения и привязка к компании</p></div></div><div className="form-grid three"><label>ФИО *<input name="fullName" required minLength={2} defaultValue={contact?.full_name || ''} autoFocus /></label><label>Компания<select name="companyId" defaultValue={contact?.company_id || defaultCompanyId || ''}><option value="">Без компании</option>{companies.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}</select></label><label>Тип контакта<select name="contactType" defaultValue={contact?.contact_type || 'CONTACT'}><option value="CONTACT">Контактное лицо</option><option value="MANAGER">Руководитель</option><option value="ACCOUNTING">Бухгалтерия</option><option value="TECHNICAL">ИТ-специалист</option><option value="USER">Пользователь</option><option value="OTHER">Другое</option></select></label></div><div className="form-grid two"><label>Должность<input name="position" defaultValue={contact?.position || ''} /></label><label>Подразделение<input name="department" defaultValue={contact?.department || ''} /></label></div></section><section className="directory-editor-section wide"><div className="planned-section-title"><span><MessageSquare /></span><div><h3>Каналы связи</h3><p>Можно добавить несколько телефонов, email и мессенджеров</p></div><button type="button" className="soft-button" onClick={() => setChannels(current => [...current, { channelType: 'TELEGRAM', value: '', label: '', isPrimary: false, isActive: true }])}><Plus /> Добавить канал</button></div><div className="contact-channel-editor"><div className="contact-channel-head"><span>Тип</span><span>Значение</span><span>Метка</span><span>Основной</span><span /></div>{channels.map((channel, index) => <div className="contact-channel-row" key={index}><select value={channel.channelType} onChange={event => updateChannel(index, { channelType: event.target.value })}><option value="PHONE">Телефон</option><option value="EMAIL">Email</option><option value="TELEGRAM">Telegram</option><option value="MAX">MAX</option><option value="WHATSAPP">WhatsApp</option><option value="VIBER">Viber</option><option value="SKYPE">Skype</option><option value="OTHER">Другое</option></select><input value={channel.value} onChange={event => updateChannel(index, { value: channel.channelType === 'PHONE' ? formatPhoneInput(event.target.value) : event.target.value })} placeholder={channel.channelType === 'PHONE' ? '+7 (___) ___-__-__' : channel.channelType === 'EMAIL' ? 'name@company.ru' : '@username или ID'} /><input value={channel.label} onChange={event => updateChannel(index, { label: event.target.value })} placeholder="Рабочий, личный..." /><label className="channel-primary-check"><input type="checkbox" checked={channel.isPrimary} onChange={event => updateChannel(index, { isPrimary: event.target.checked })} /><span>Основной</span></label><button type="button" className="icon-button danger" onClick={() => setChannels(current => current.filter((_item, itemIndex) => itemIndex !== index))}><Trash2 /></button></div>)}</div></section><section className="directory-editor-section"><div className="planned-section-title"><span><Clock3 /></span><div><h3>Предпочтения</h3><p>Когда и как лучше связываться</p></div></div><label>Предпочтительный канал<select name="preferredChannel" defaultValue={contact?.preferred_channel || 'EMAIL'}><option value="EMAIL">Email</option><option value="PHONE">Телефон</option><option value="TELEGRAM">Telegram</option><option value="MAX">MAX</option><option value="WHATSAPP">WhatsApp</option><option value="VIBER">Viber</option><option value="SKYPE">Skype</option><option value="OTHER">Другое</option></select></label><label>Рабочее время<input name="workingHours" defaultValue={contact?.working_hours || ''} placeholder="Пн–Пт, 09:00–18:00" /></label><label>Часовой пояс<input name="timezone" defaultValue={contact?.timezone || ''} placeholder="Europe/Moscow" /></label></section><section className="directory-editor-section"><div className="planned-section-title"><span><FileText /></span><div><h3>Заметки и статус</h3><p>Внутренняя информация для команды</p></div></div><label>Заметки<textarea name="notes" rows={5} defaultValue={contact?.notes || ''} /></label><label className="company-active-switch-v2"><span><b>Основной контакт компании</b><small>Будет показан первым в карточке компании</small></span><input type="checkbox" name="isPrimary" defaultChecked={contact?.is_primary ?? false} /></label><label className="company-active-switch-v2"><span><b>Контакт активен</b><small>Разрешить выбор в заявках и коммуникациях</small></span><input type="checkbox" name="isActive" defaultChecked={contact?.is_active ?? true} /></label></section></div>{error && <div className="form-error directory-editor-error">{error}</div>}<div className="directory-editor-actions"><button type="button" className="button ghost" onClick={onClose} disabled={pending}>Отмена</button><button className="button primary" disabled={pending}><Save /> {pending ? 'Сохраняем…' : 'Сохранить контакт'}</button></div></form></div>;
}

function LinkedContactEditorModal({ contact, pending, error, onClose, onSubmit }: { contact: CompanyContact; pending: boolean; error: string; onClose: () => void; onSubmit: (payload: Record<string, unknown>) => void }) {
  const masterChannels = useMemo(() => (contact.channels || []).filter(item => ['PHONE', 'EMAIL'].includes(item.channel_type)), [contact.channels]);
  const [messengers, setMessengers] = useState(() => (contact.channels || []).filter(item => !['PHONE', 'EMAIL'].includes(item.channel_type)).map(item => ({ channelType: item.channel_type, value: item.value, label: item.label || '', isPrimary: Boolean(item.is_primary), isActive: item.is_active !== false })));
  useEffect(() => { const listener = (event: globalThis.KeyboardEvent) => { if (event.key === 'Escape' && !pending) onClose(); }; window.addEventListener('keydown', listener); return () => window.removeEventListener('keydown', listener); }, [pending, onClose]);
  function updateMessenger(index: number, patch: Partial<(typeof messengers)[number]>) { setMessengers(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item)); }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    onSubmit({
      companyId: contact.company_id || undefined,
      fullName: contact.full_name,
      position: contact.position || undefined,
      department: String(form.get('department') || '').trim() || undefined,
      contactType: String(form.get('contactType') || 'CONTACT'),
      preferredChannel: String(form.get('preferredChannel') || 'EMAIL'),
      workingHours: String(form.get('workingHours') || '').trim() || undefined,
      timezone: String(form.get('timezone') || '').trim() || undefined,
      notes: String(form.get('notes') || '').trim() || undefined,
      isPrimary: form.get('isPrimary') === 'on',
      isActive: contact.is_active,
      channels: [
        ...masterChannels.map(item => ({ channelType: item.channel_type, value: item.value, label: item.label || undefined, isPrimary: Boolean(item.is_primary), isActive: item.is_active !== false })),
        ...messengers.filter(item => item.value.trim()).map(item => ({ ...item, value: item.value.trim(), label: item.label.trim() || undefined })),
      ],
    });
  }
  return <div className="modal-backdrop directory-editor-backdrop" onMouseDown={event => { if (event.currentTarget === event.target && !pending) onClose(); }}>
    <form className="modal-card directory-editor-modal contact-editor-modal" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
      <div className="modal-head directory-editor-head"><div><span className="modal-kicker">КОНТАКТ ИЗ БИТРИКС24</span><h2>Редактирование контакта</h2><p>Master-поля защищены; внутренние сведения и мессенджеры управляются в портале.</p></div><button type="button" className="icon-button" onClick={onClose} disabled={pending}><X /></button></div>
      <div className="directory-master-notice"><LockKeyhole /> ФИО, компания, должность, рабочие телефоны и email обновляются только из Битрикс24.</div>
      <div className="directory-editor-content">
        <section className="directory-editor-section wide"><div className="planned-section-title"><span><LockKeyhole /></span><div><h3>Данные Битрикс24</h3><p>Только для чтения</p></div></div><div className="linked-contact-master-grid"><div><small>ФИО</small><b>{contact.full_name || 'Не указано'}</b></div><div><small>Компания</small><b>{contact.company_name || 'Не назначена'}</b></div><div><small>Должность</small><b>{contact.position || 'Не указана'}</b></div></div><div className="linked-contact-master-channels">{masterChannels.map(channel => <span key={channel.id || `${channel.channel_type}:${channel.value}`}>{channel.channel_type === 'PHONE' ? <Phone /> : <Mail />}<b>{channel.value}</b><small>{channel.label}</small></span>)}{!masterChannels.length && <p>Рабочие каналы в Битрикс24 не заполнены.</p>}</div></section>
        <section className="directory-editor-section wide"><div className="planned-section-title"><span><MessageSquare /></span><div><h3>Мессенджеры портала</h3><p>Telegram, MAX, WhatsApp и другие дополнительные каналы</p></div><button type="button" className="soft-button" onClick={() => setMessengers(current => [...current, { channelType: 'TELEGRAM', value: '', label: '', isPrimary: false, isActive: true }])}><Plus /> Добавить канал</button></div><div className="contact-channel-editor"><div className="contact-channel-head"><span>Тип</span><span>Значение</span><span>Метка</span><span>Основной</span><span /></div>{messengers.map((channel, index) => <div className="contact-channel-row" key={index}><select value={channel.channelType} onChange={event => updateMessenger(index, { channelType: event.target.value as ContactChannel['channel_type'] })}><option value="TELEGRAM">Telegram</option><option value="MAX">MAX</option><option value="WHATSAPP">WhatsApp</option><option value="VIBER">Viber</option><option value="SKYPE">Skype</option><option value="OTHER">Другое</option></select><input value={channel.value} onChange={event => updateMessenger(index, { value: event.target.value })} placeholder="@username или ID" /><input value={channel.label} onChange={event => updateMessenger(index, { label: event.target.value })} placeholder="Рабочий, личный..." /><label className="channel-primary-check"><input type="checkbox" checked={channel.isPrimary} onChange={event => updateMessenger(index, { isPrimary: event.target.checked })} /><span>Основной</span></label><button type="button" className="icon-button danger" onClick={() => setMessengers(current => current.filter((_item, itemIndex) => itemIndex !== index))}><Trash2 /></button></div>)}</div></section>
        <section className="directory-editor-section"><div className="planned-section-title"><span><Clock3 /></span><div><h3>Рабочие параметры</h3><p>Внутренние данные портала</p></div></div><label>Подразделение<input name="department" defaultValue={contact.department || ''} /></label><label>Тип контакта<select name="contactType" defaultValue={contact.contact_type || 'CONTACT'}><option value="CONTACT">Контактное лицо</option><option value="MANAGER">Руководитель</option><option value="ACCOUNTING">Бухгалтерия</option><option value="TECHNICAL">ИТ-специалист</option><option value="USER">Пользователь</option><option value="OTHER">Другое</option></select></label><label>Предпочтительный канал<select name="preferredChannel" defaultValue={contact.preferred_channel || 'EMAIL'}><option value="EMAIL">Email</option><option value="PHONE">Телефон</option><option value="TELEGRAM">Telegram</option><option value="MAX">MAX</option><option value="WHATSAPP">WhatsApp</option><option value="VIBER">Viber</option><option value="SKYPE">Skype</option><option value="OTHER">Другое</option></select></label><label>Рабочее время<input name="workingHours" defaultValue={contact.working_hours || ''} placeholder="Пн–Пт, 09:00–18:00" /></label><label>Часовой пояс<input name="timezone" defaultValue={contact.timezone || ''} placeholder="Europe/Moscow" /></label></section>
        <section className="directory-editor-section"><div className="planned-section-title"><span><FileText /></span><div><h3>Заметки</h3><p>Внутренняя информация для команды</p></div></div><label>Заметки<textarea name="notes" rows={7} defaultValue={contact.notes || ''} /></label><label className="company-active-switch-v2"><span><b>Основной контакт компании</b><small>Будет показан первым в карточке компании</small></span><input type="checkbox" name="isPrimary" defaultChecked={contact.is_primary} /></label></section>
      </div>
      {error && <div className="form-error directory-editor-error">{error}</div>}
      <div className="directory-editor-actions"><button type="button" className="button ghost" onClick={onClose} disabled={pending}>Отмена</button><button className="button primary" disabled={pending}><Save /> {pending ? 'Сохраняем…' : 'Сохранить внутренние данные'}</button></div>
    </form>
  </div>;
}


function MetricMini({ label, value, icon }: { label: string; value: number; icon: ReactNode }) { return <div><span>{icon}</span><div><b>{value}</b><small>{label}</small></div></div>; }
function StatusDot({ active, activeLabel = 'Активна', inactiveLabel = 'Неактивна' }: { active: boolean; activeLabel?: string; inactiveLabel?: string }) { return <span className={`active-dot ${active ? 'on' : ''}`}><i />{active ? activeLabel : inactiveLabel}</span>; }

function Templates() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [templateSearchParams, setTemplateSearchParams] = useSearchParams();
  const [tab, setTab] = useState<'templates' | 'recurring'>('templates');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [feedback, setFeedback] = useState('');
  const [recurringError, setRecurringError] = useState('');
  const recurringFormRef = useRef<HTMLFormElement>(null);
  const canManageTemplates = userHasRole(user, 'ADMIN');
  const { data: templates = [], isLoading: templatesLoading } = useQuery({ queryKey: ['templates'], queryFn: () => api<Template[]>('/templates') });
  const { data: recurring = [] } = useQuery({ queryKey: ['recurring'], queryFn: () => api<RecurringRule[]>('/templates/recurring') });
  const { data: companies = [] } = useQuery({ queryKey: ['companies'], queryFn: () => api<Company[]>('/companies') });
  const createRecurring = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api('/templates/recurring/rules', { method: 'POST', body: JSON.stringify(payload) }),
    onMutate: () => {
      setRecurringError('');
      setFeedback('');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring'] });
      recurringFormRef.current?.reset();
      setFeedback('Расписание создано');
    },
    onError: reason => setRecurringError(reason instanceof Error ? reason.message : 'Не удалось создать расписание'),
  });
  const editingTemplateId = templateSearchParams.get('edit');
  const editorOpen = templateSearchParams.get('create') === '1' || Boolean(editingTemplateId);
  const selectedTemplateId = templateSearchParams.get('template');

  const visibleTemplates = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return templates.filter(template => {
      if (status === 'ACTIVE' && !template.is_active) return false;
      if (status === 'INACTIVE' && template.is_active) return false;
      if (!needle) return true;
      return `${template.name} ${template.description || ''} ${template.subject_template} ${template.body_template} ${template.category || ''} ${template.queue_name || ''}`.toLowerCase().includes(needle);
    });
  }, [templates, search, status]);

  const activeCount = templates.filter(item => item.is_active).length;
  const recurringCount = recurring.filter(item => item.is_active).length;
  const selectedTemplate = visibleTemplates.find(item => item.id === selectedTemplateId) || visibleTemplates[0] || null;
  const selectedTemplateRules = selectedTemplate ? recurring.filter(rule => rule.template_id === selectedTemplate.id) : [];

  useEffect(() => {
    if (templatesLoading || !visibleTemplates.length || selectedTemplateId === selectedTemplate?.id) return;
    const next = new URLSearchParams(templateSearchParams);
    next.set('template', selectedTemplate!.id);
    setTemplateSearchParams(next, { replace: true });
  }, [selectedTemplate?.id, selectedTemplateId, setTemplateSearchParams, templateSearchParams, templatesLoading, visibleTemplates]);

  function selectTemplate(id: string) {
    const next = new URLSearchParams(templateSearchParams);
    next.set('template', id);
    setTemplateSearchParams(next);
  }

  function openCreateTemplate() {
    setFeedback('');
    const next = new URLSearchParams(templateSearchParams);
    next.delete('edit');
    next.set('create', '1');
    setTemplateSearchParams(next);
  }

  function openEditTemplate(id: string) {
    setFeedback('');
    const next = new URLSearchParams(templateSearchParams);
    next.delete('create');
    next.set('edit', id);
    setTemplateSearchParams(next);
  }

  function closeTemplateEditor() {
    const next = new URLSearchParams(templateSearchParams);
    next.delete('create');
    next.delete('edit');
    setTemplateSearchParams(next, { replace: true });
  }

  function handleTemplateSaved(action: 'created' | 'updated' | 'disabled') {
    closeTemplateEditor();
    setFeedback(action === 'created' ? 'Шаблон создан и добавлен в библиотеку' : action === 'updated' ? 'Изменения шаблона сохранены' : 'Шаблон отключён');
  }

  function recurringSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const nextRunAt = new Date(String(form.get('nextRunAt')));
    if (Number.isNaN(nextRunAt.getTime())) {
      setRecurringError('Укажите дату и время первого запуска');
      return;
    }
    createRecurring.mutate({
      name: form.get('name'),
      templateId: form.get('templateId'),
      companyId: form.get('companyId'),
      frequency: form.get('frequency'),
      intervalCount: Number(form.get('intervalCount') || 1),
      nextRunAt: nextRunAt.toISOString(),
    });
  }

  return <div className="templates-page-v2 templates-page-v4 templates-workspace">
    <PageHeading
      eyebrow="АВТОМАТИЗАЦИЯ"
      title="Шаблоны заявок"
      description="Готовые сценарии обращений, чек-листы и автоматические расписания в одной рабочей области"
      actions={tab === 'templates' && canManageTemplates ? <button className="button primary template-create-button-v4" onClick={openCreateTemplate}><Plus /> Создать шаблон</button> : undefined}
    />

    {feedback && <div className="admin-feedback success template-feedback-v3">{feedback}<button type="button" className="icon-button" onClick={() => setFeedback('')}><X /></button></div>}

    <div className="admin-tabs template-tabs-v2 template-section-tabs" role="tablist" aria-label="Разделы шаблонов">
      <button type="button" role="tab" data-tab="templates" aria-selected={tab === 'templates'} className={tab === 'templates' ? 'active' : ''} onClick={() => setTab('templates')}><BookOpen /> Шаблоны <small>{templates.length}</small></button>
      <button type="button" role="tab" data-tab="recurring" aria-selected={tab === 'recurring'} className={tab === 'recurring' ? 'active' : ''} onClick={() => setTab('recurring')}><CalendarClock /> Расписания <small>{recurring.length}</small></button>
    </div>

    <section className="template-metric-strip" aria-label="Сводка по шаблонам">
      <span><BookOpen aria-hidden="true" /><small>Всего шаблонов</small><b>{templates.length}</b></span>
      <span><CheckCircle2 aria-hidden="true" /><small>Активны</small><b>{activeCount}</b></span>
      <span><CalendarClock aria-hidden="true" /><small>Расписания</small><b>{recurringCount}</b></span>
    </section>

    {tab === 'templates' ? <section className="surface template-library-workspace" role="tabpanel">
      <div className="template-library-toolbar">
        <div>
          <h2>Библиотека</h2>
          <p>Выберите шаблон, чтобы проверить содержание и применить его к новой заявке</p>
        </div>
        <div className="template-library-controls">
          <label className="search-field"><Search aria-hidden="true" /><input aria-label="Поиск шаблонов" value={search} onChange={event => setSearch(event.target.value)} placeholder="Название, тема, очередь…" /></label>
          <select aria-label="Статус шаблона" value={status} onChange={event => setStatus(event.target.value as 'ALL' | 'ACTIVE' | 'INACTIVE')}>
            <option value="ALL">Все статусы</option>
            <option value="ACTIVE">Активные</option>
            <option value="INACTIVE">Неактивные</option>
          </select>
        </div>
      </div>

      <div className="templates-master-detail" aria-busy={templatesLoading}>
        {templatesLoading ? <>
          <aside className="template-directory-pane"><TableSkeleton /></aside>
          <div className="template-detail-loading"><TableSkeleton /></div>
        </> : visibleTemplates.length ? <>
          <aside className="template-directory-pane">
            <header><div><b>Шаблоны</b><small>{visibleTemplates.length} из {templates.length}</small></div><span>Выберите сценарий</span></header>
            <nav className="template-directory-list" aria-label="Список шаблонов" tabIndex={0}>
              {visibleTemplates.map(template => {
                const checklistItems = template.checklist_template.reduce((sum, group) => sum + group.items.length, 0);
                const selected = template.id === selectedTemplate?.id;
                return <button
                  type="button"
                  className={`template-directory-row ${selected ? 'selected' : ''} ${template.is_active ? '' : 'inactive'}`}
                  aria-current={selected ? 'true' : undefined}
                  key={template.id}
                  onClick={() => selectTemplate(template.id)}
                >
                  <span className="template-directory-row__icon"><FileText aria-hidden="true" /></span>
                  <span className="template-directory-row__copy"><b>{template.name}</b><small>{template.description || template.subject_template}</small><span><em className={`template-state-dot ${template.is_active ? 'is-active' : ''}`}>{template.is_active ? 'Активен' : 'Неактивен'}</em>{template.queue_name && <em>{template.queue_name}</em>}</span></span>
                  <span className="template-directory-row__meta"><PriorityBadge priority={template.priority} /><small><ListChecks aria-hidden="true" /> {checklistItems}<CalendarClock aria-hidden="true" /> {template.recurring_count || 0}</small></span>
                  <ChevronRight aria-hidden="true" />
                </button>;
              })}
            </nav>
          </aside>
          <TemplateDetailWorkspace
            key={selectedTemplate!.id}
            template={selectedTemplate!}
            recurringRules={selectedTemplateRules}
            canManage={canManageTemplates}
            onUse={() => navigate(`/tickets?create=1&templateId=${selectedTemplate!.id}`)}
            onEdit={() => openEditTemplate(selectedTemplate!.id)}
          />
        </> : <div className="template-workspace-empty">
          <EmptyState icon={<BookOpen />} title="Шаблоны не найдены" text={search || status !== 'ALL' ? 'Измените поисковый запрос или фильтр статуса' : 'Создайте первый шаблон, чтобы ускорить работу с заявками'} compact />
        </div>}
      </div>
    </section> : <div className={`template-schedules-workspace ${recurring.length ? '' : 'is-empty'}`} role="tabpanel">
      <section className="surface template-schedule-list-panel">
        <header><div><h2>Расписания</h2><p>Автоматическое создание регламентных заявок</p></div><span>{recurring.length} настроено</span></header>
        <div className="template-schedule-list" tabIndex={0} aria-label="Список расписаний">
          {recurring.length ? recurring.map(rule => <article className={rule.is_active ? '' : 'inactive'} key={rule.id}>
            <span className="template-schedule-list__icon"><CalendarClock aria-hidden="true" /></span>
            <div><b>{rule.name}</b><small>{rule.template_name} · {rule.company_name}</small></div>
            <span><small>Периодичность</small><b>{rule.frequency === 'DAILY' ? 'Ежедневно' : rule.frequency === 'WEEKLY' ? 'Еженедельно' : 'Ежемесячно'}{rule.interval_count > 1 ? ` · ${rule.interval_count}` : ''}</b></span>
            <span><small>Следующий запуск</small><b>{formatDateTime(rule.next_run_at)}</b></span>
            <StatusDot active={rule.is_active} />
          </article>) : <EmptyState icon={<CalendarClock />} title="Расписаний пока нет" text="Создайте первое расписание в форме справа" compact />}
        </div>
      </section>
      {canManageTemplates ? <form ref={recurringFormRef} className="surface template-schedule-form" onSubmit={recurringSubmit}>
        <div className="template-schedule-form__heading"><span><Zap aria-hidden="true" /></span><div><h2>Новое расписание</h2><p>Портал создаст заявку автоматически</p></div></div>
        <label>Название<input name="name" required placeholder="Например, ежемесячная проверка" /></label>
        <label>Шаблон<select name="templateId" required defaultValue=""><option value="">Выберите шаблон</option>{templates.filter(item => item.is_active).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label>Компания<select name="companyId" required defaultValue=""><option value="">Выберите компанию</option>{companies.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <div className="template-schedule-form__grid"><label>Частота<select name="frequency" defaultValue="DAILY"><option value="DAILY">Ежедневно</option><option value="WEEKLY">Еженедельно</option><option value="MONTHLY">Ежемесячно</option></select></label><label>Интервал<input name="intervalCount" type="number" min="1" defaultValue="1" /></label></div>
        <label>Первый запуск<input name="nextRunAt" type="datetime-local" required /></label>
        {recurringError && <div className="template-schedule-form__error" role="alert"><AlertTriangle aria-hidden="true" />{recurringError}</div>}
        <button className="button primary wide" disabled={createRecurring.isPending}><Zap /> {createRecurring.isPending ? 'Создаём…' : 'Создать расписание'}</button>
      </form> : <div className="surface template-schedule-form template-schedule-form--readonly"><CalendarClock /><h2>Управление расписаниями</h2><p>Создание и изменение расписаний доступно администратору.</p></div>}
    </div>}

    {editorOpen && canManageTemplates && <TemplateEditorModal key={editingTemplateId || 'new'} templateId={editingTemplateId || undefined} onClose={closeTemplateEditor} onSaved={handleTemplateSaved} />}
  </div>;
}

type TemplateEditorForm = {
  name: string;
  description: string;
  subjectTemplate: string;
  bodyTemplate: string;
  priority: string;
  queueId: string;
  category: string;
  isActive: boolean;
};

type TemplateChecklistDraft = { title: string; items: string[] };

const EMPTY_TEMPLATE_EDITOR_FORM: TemplateEditorForm = {
  name: '',
  description: '',
  subjectTemplate: '',
  bodyTemplate: '',
  priority: 'NORMAL',
  queueId: '',
  category: '',
  isActive: true,
};

function templateEditorSnapshot(form: TemplateEditorForm, checklists: TemplateChecklistDraft[]) {
  return JSON.stringify({ form, checklists });
}

function TemplateEditorModal({ templateId, onClose, onSaved }: { templateId?: string; onClose: () => void; onSaved: (action: 'created' | 'updated' | 'disabled') => void }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = Boolean(templateId);
  const canDisableTemplate = userHasAnyRole(user, ['ADMIN', 'MANAGER']);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(!isEditing);
  const [form, setForm] = useState<TemplateEditorForm>({ ...EMPTY_TEMPLATE_EDITOR_FORM });
  const [checklists, setChecklists] = useState<TemplateChecklistDraft[]>([]);
  const initialSnapshot = useRef(templateEditorSnapshot(EMPTY_TEMPLATE_EDITOR_FORM, []));
  const { data: queues = [] } = useQuery({ queryKey: ['queues'], queryFn: () => api<Queue[]>('/queues') });
  const { data: template, isLoading, error: loadError } = useQuery({ queryKey: ['template', templateId], queryFn: () => api<Template>(`/templates/${templateId}`), enabled: isEditing });

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  useEffect(() => {
    if (!template) return;
    const nextForm: TemplateEditorForm = {
      name: template.name || '',
      description: template.description || '',
      subjectTemplate: template.subject_template || '',
      bodyTemplate: template.body_template || '',
      priority: template.priority || 'NORMAL',
      queueId: template.queue_id || '',
      category: template.category || '',
      isActive: template.is_active,
    };
    const nextChecklists = Array.isArray(template.checklist_template)
      ? template.checklist_template.map(group => ({ title: group.title || '', items: Array.isArray(group.items) ? [...group.items] : [] }))
      : [];
    setForm(nextForm);
    setChecklists(nextChecklists);
    initialSnapshot.current = templateEditorSnapshot(nextForm, nextChecklists);
    setReady(true);
  }, [template]);

  const currentSnapshot = useMemo(() => templateEditorSnapshot(form, checklists), [form, checklists]);
  const hasUnsavedChanges = ready && currentSnapshot !== initialSnapshot.current;
  const checklistItemCount = checklists.reduce((sum, group) => sum + group.items.filter(item => item.trim()).length, 0);

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api<Template>(isEditing ? `/templates/${templateId}` : '/templates', { method: isEditing ? 'PATCH' : 'POST', body: JSON.stringify(payload) }),
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.setQueryData(['template', result.id], result);
      initialSnapshot.current = currentSnapshot;
      onSaved(isEditing ? 'updated' : 'created');
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить шаблон'),
  });

  const disable = useMutation({
    mutationFn: () => api(`/templates/${templateId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.removeQueries({ queryKey: ['template', templateId] });
      onSaved('disabled');
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось отключить шаблон'),
  });

  function requestClose() {
    if (save.isPending || disable.isPending) return;
    if (hasUnsavedChanges && !window.confirm('Закрыть форму? Несохранённые изменения будут потеряны.')) return;
    onClose();
  }

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') requestClose();
    };
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const handlePopState = () => {
      if (!hasUnsavedChanges) return;
      if (!window.confirm('Закрыть форму? Несохранённые изменения будут потеряны.')) window.history.forward();
    };
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, save.isPending, disable.isPending]);

  function updateForm<K extends keyof TemplateEditorForm>(key: K, value: TemplateEditorForm[K]) {
    setForm(current => ({ ...current, [key]: value }));
  }

  function addChecklistGroup() {
    setChecklists(current => [...current, { title: '', items: [''] }]);
  }

  function updateChecklistGroup(groupIndex: number, title: string) {
    setChecklists(current => current.map((group, index) => index === groupIndex ? { ...group, title } : group));
  }

  function addChecklistItem(groupIndex: number) {
    setChecklists(current => current.map((group, index) => index === groupIndex ? { ...group, items: [...group.items, ''] } : group));
  }

  function updateChecklistItem(groupIndex: number, itemIndex: number, value: string) {
    setChecklists(current => current.map((group, index) => index === groupIndex ? { ...group, items: group.items.map((item, childIndex) => childIndex === itemIndex ? value : item) } : group));
  }

  function removeChecklistItem(groupIndex: number, itemIndex: number) {
    setChecklists(current => current.map((group, index) => index === groupIndex ? { ...group, items: group.items.filter((_item, childIndex) => childIndex !== itemIndex) } : group));
  }

  function removeChecklistGroup(groupIndex: number) {
    setChecklists(current => current.filter((_group, index) => index !== groupIndex));
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    const normalizedChecklists = checklists
      .map(group => ({ title: group.title.trim(), items: group.items.map(item => item.trim()).filter(Boolean) }))
      .filter(group => group.title || group.items.length)
      .map((group, index) => ({ title: group.title || `Чек-лист ${index + 1}`, items: group.items }));

    save.mutate({
      name: form.name.trim(),
      description: form.description.trim() || null,
      subjectTemplate: form.subjectTemplate.trim(),
      bodyTemplate: form.bodyTemplate.trim(),
      priority: form.priority,
      queueId: form.queueId || null,
      category: form.category.trim() || null,
      checklistTemplate: normalizedChecklists,
      isActive: form.isActive,
    });
  }

  const loadingError = loadError instanceof Error ? loadError.message : loadError ? 'Не удалось загрузить шаблон' : '';

  return <div className="modal-backdrop planned-editor-backdrop template-editor-backdrop-v3 template-editor-backdrop-v4" onMouseDown={event => { if (event.currentTarget === event.target) requestClose(); }}>
    <form className="modal-card planned-editor planned-editor-v4 template-editor-modal-v3 template-editor-modal-v4" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
      <div className="modal-head planned-editor-head template-editor-modal-head-v3 template-editor-modal-head-v4">
        <div>
          <span className="modal-kicker">ШАБЛОН ЗАЯВКИ</span>
          <div className="planned-editor-title-row"><h2>{isEditing ? 'Редактирование шаблона' : 'Новый шаблон'}</h2>{ready && <span className={`template-editor-status-v2 ${form.isActive ? 'active' : ''}`}><i />{form.isActive ? 'Активен' : 'Неактивен'}</span>}{hasUnsavedChanges && <span className="template-unsaved-badge-v3">Есть изменения</span>}</div>
          <p>{isEditing ? 'Измените сценарий и сохраните его, не покидая библиотеку шаблонов' : 'Создайте готовый сценарий для быстрого формирования заявок'}</p>
        </div>
        <button type="button" className="icon-button" onClick={requestClose} aria-label="Закрыть форму"><X /></button>
      </div>

      {isLoading ? <div className="template-editor-modal-loading-v3"><TableSkeleton /></div> : loadingError ? <div className="template-editor-modal-load-error-v3"><AlertTriangle /><b>Не удалось открыть шаблон</b><p>{loadingError}</p><button type="button" className="button ghost" onClick={requestClose}>Закрыть</button></div> : !ready ? <div className="template-editor-modal-loading-v3"><TableSkeleton /></div> : <>
        <div className="template-editor-modal-grid-v3">
          <section className="template-editor-modal-section-v3 template-modal-main-v3">
            <div className="planned-section-title"><span><FileText /></span><div><h3>1. Основная информация</h3><p>Название, категория и назначение шаблона</p></div></div>
            <div className="template-editor-grid-v2 name-category">
              <label>Название шаблона<input value={form.name} onChange={event => updateForm('name', event.target.value)} placeholder="Например: Новый сотрудник" maxLength={150} required /></label>
              <label>Категория<input value={form.category} onChange={event => updateForm('category', event.target.value)} placeholder="Например: Доступы" maxLength={100} /></label>
            </div>
            <label>Описание<textarea value={form.description} onChange={event => updateForm('description', event.target.value)} rows={3} placeholder="Кратко объясните, когда использовать этот шаблон" maxLength={500} /><small>{form.description.length} / 500</small></label>
          </section>

          <section className="template-editor-modal-section-v3 template-modal-content-v3">
            <div className="planned-section-title"><span><TicketIcon /></span><div><h3>2. Содержание заявки</h3><p>Поля, которые будут перенесены в новую заявку</p></div></div>
            <label>Тема заявки<input value={form.subjectTemplate} onChange={event => updateForm('subjectTemplate', event.target.value)} placeholder="Например: Подготовить рабочее место: {{ФИО}}" maxLength={250} required /><small>{form.subjectTemplate.length} / 250</small></label>
            <label>Текст заявки<textarea value={form.bodyTemplate} onChange={event => updateForm('bodyTemplate', event.target.value)} rows={9} placeholder="Опишите задачу, ожидаемый результат и данные, которые нужно заполнить" required /><small>{form.bodyTemplate.length} символов</small></label>
            <div className="template-variable-help-v2"><Sparkles /><div><b>Можно использовать подсказки-плейсхолдеры</b><p>Например: <code>{'{{ФИО}}'}</code>, <code>{'{{Система}}'}</code>, <code>{'{{Дата}}'}</code>. Сотрудник заменит их при создании заявки.</p></div></div>
          </section>

          <section className="template-editor-modal-section-v3 template-modal-settings-v3">
            <div className="planned-section-title"><span><Settings /></span><div><h3>3. Параметры заявки</h3><p>Приоритет, очередь и доступность сценария</p></div></div>
            <div className="template-editor-grid-v2">
              <label>Приоритет<select value={form.priority} onChange={event => updateForm('priority', event.target.value)}>{PRIORITY_OPTIONS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
              <label>Очередь<select value={form.queueId} onChange={event => updateForm('queueId', event.target.value)}><option value="">Без очереди</option>{queues.filter(item => item.is_active).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            </div>
            <label className="switch-setting template-editor-switch-v2"><span><b>Доступен сотрудникам</b><small>Неактивный шаблон останется в библиотеке, но его нельзя будет использовать</small></span><input type="checkbox" checked={form.isActive} onChange={event => updateForm('isActive', event.target.checked)} /></label>
            <div className="planned-editor-hint"><Bell /><span><b>Изменения применяются к будущим заявкам</b><small>Уже созданные по шаблону заявки не будут изменены.</small></span></div>
          </section>

          <section className="template-editor-modal-section-v3 template-modal-checklists-v3 checklist-template-editor-v2">
            <div className="planned-section-title"><span><ListChecks /></span><div><h3>4. Чек-листы заявки</h3><p>Стандартные этапы и контрольные шаги</p></div><em>{checklistItemCount ? `${checklistItemCount} пунктов` : 'Необязательно'}</em></div>
            {checklists.length ? <div className="checklist-template-groups-v2">{checklists.map((group, groupIndex) => <article key={groupIndex}>
              <div className="checklist-template-group-head-v2"><GripVertical /><input value={group.title} onChange={event => updateChecklistGroup(groupIndex, event.target.value)} placeholder={`Название чек-листа ${groupIndex + 1}`} /><button className="icon-button danger" type="button" title="Удалить чек-лист" onClick={() => removeChecklistGroup(groupIndex)}><Trash2 /></button></div>
              <div className="checklist-template-items-v2">{group.items.map((item, itemIndex) => <div key={itemIndex}><Circle /><input value={item} onChange={event => updateChecklistItem(groupIndex, itemIndex, event.target.value)} placeholder={`Пункт ${itemIndex + 1}`} /><button className="icon-button" type="button" title="Удалить пункт" onClick={() => removeChecklistItem(groupIndex, itemIndex)}><X /></button></div>)}</div>
              <button className="text-button" type="button" onClick={() => addChecklistItem(groupIndex)}><Plus /> Добавить пункт</button>
            </article>)}</div> : <div className="template-checklist-empty-v2"><ListChecks /><b>Чек-листы не добавлены</b><p>Шаблон можно сохранить без них или добавить стандартные шаги выполнения.</p></div>}
            <button className="soft-button planned-add-checklist template-add-checklist-v3" type="button" onClick={addChecklistGroup}><Plus /> Добавить чек-лист</button>
          </section>

          <section className="template-editor-modal-section-v3 template-modal-preview-v3">
            <div className="planned-section-title"><span><Sparkles /></span><div><h3>5. Предпросмотр и проверка</h3><p>Так сценарий будет выглядеть в библиотеке</p></div></div>
            <div className="template-preview-card-body-v2 template-preview-card-modal-v3">
              <header><span className="template-icon"><FileText /></span><div><h3>{form.name || 'Название шаблона'}</h3><p>{form.description || 'Краткое описание назначения шаблона'}</p></div></header>
              <div className="template-tags"><PriorityBadge priority={form.priority} />{form.category && <span>{form.category}</span>}{form.queueId && <span>{queues.find(item => item.id === form.queueId)?.name || 'Очередь'}</span>}</div>
              <small>Тема заявки</small><h4>{form.subjectTemplate || 'Тема будущей заявки'}</h4><p>{form.bodyTemplate || 'Здесь появится текст заявки, который сотрудники смогут уточнить перед созданием.'}</p>
            </div>
            <ul className="template-editor-check-v3">
              <li className={form.name.trim() ? 'done' : ''}><CheckCircle2 /> Понятное название</li>
              <li className={form.subjectTemplate.trim() ? 'done' : ''}><CheckCircle2 /> Заполнена тема заявки</li>
              <li className={form.bodyTemplate.trim() ? 'done' : ''}><CheckCircle2 /> Добавлен текст заявки</li>
              <li className={checklistItemCount ? 'done' : ''}><ListChecks /> {checklistItemCount ? `${checklistItemCount} пунктов чек-листа` : 'Чек-лист необязателен'}</li>
            </ul>
          </section>
        </div>

        {(error || save.error || disable.error) && <div className="form-error planned-editor-error template-editor-error-v3">{error || (save.error instanceof Error ? save.error.message : disable.error instanceof Error ? disable.error.message : 'Не удалось выполнить операцию')}</div>}

        <div className="form-actions planned-editor-actions template-editor-actions-v3">
          {isEditing && canDisableTemplate && form.isActive ? <button className="button ghost danger" type="button" disabled={disable.isPending || save.isPending} onClick={() => { if (window.confirm('Отключить шаблон? Он останется в библиотеке, но его нельзя будет использовать.')) disable.mutate(); }}><Ban /> {disable.isPending ? 'Отключение…' : 'Отключить'}</button> : <span />}
          <span />
          <button className="button ghost" type="button" onClick={requestClose} disabled={save.isPending || disable.isPending}>Отмена</button>
          <button className="button primary" disabled={save.isPending || disable.isPending || !ready}><Save /> {save.isPending ? 'Сохранение…' : isEditing ? 'Сохранить шаблон' : 'Создать шаблон'}</button>
        </div>
      </>}
    </form>
  </div>;
}

function notificationTarget(item: NotificationRow) {
  if (item.entity_type === 'TICKET' && item.entity_id) {
    return `/tickets/${item.entity_id}${/COMMENT|REPLY/.test(item.notification_type) ? '?tab=discussion' : ''}`;
  }
  if (item.entity_type === 'CONTACT_CONVERSATION' && item.entity_id) return `/contact-center?conversation=${item.entity_id}`;
  if (item.entity_type === 'PLANNED_WORK' && item.entity_id) return `/planned-works?work=${item.entity_id}`;
  return '/';
}

function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<'all' | 'unread'>('all');
  const [search, setSearch] = useState('');
  const hoverReadIds = useRef<Set<string>>(new Set());
  const { data: rows = [], isLoading } = useQuery({ queryKey: ['notifications', 'page'], queryFn: () => api<NotificationRow[]>('/notifications') });
  const markRead = useMutation({
    mutationFn: (id: string) => api(`/notifications/${id}/read`, { method: 'PATCH' }),
    onMutate: (id: string) => {
      hoverReadIds.current.add(id);
      const markRow = (items?: NotificationRow[]) => items?.map(item => item.id === id ? { ...item, is_read: true } : item);
      queryClient.setQueryData<NotificationRow[]>(['notifications', 'page'], markRow);
      queryClient.setQueryData<NotificationRow[]>(['notifications'], markRow);
      queryClient.setQueryData<NotificationRow[]>(['notifications', 'unread'], items => items?.filter(item => item.id !== id));
      queryClient.setQueryData<{ unread: number }>(['notification-count'], current => current ? { unread: Math.max(0, current.unread - 1) } : current);
    },
    onError: (_error, id) => { hoverReadIds.current.delete(id); },
    onSettled: () => { queryClient.invalidateQueries({ queryKey: ['notifications'] }); queryClient.invalidateQueries({ queryKey: ['notification-count'] }); },
  });
  const markAll = useMutation({ mutationFn: () => api('/notifications/read-all', { method: 'POST' }), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['notifications'] }); queryClient.invalidateQueries({ queryKey: ['notification-count'] }); } });
  const visible = rows.filter(item => (scope === 'all' || !item.is_read) && (!search.trim() || `${item.title} ${item.body || ''}`.toLowerCase().includes(search.trim().toLowerCase())));
  function markOnHover(item: NotificationRow) { if (!item.is_read && !hoverReadIds.current.has(item.id)) { hoverReadIds.current.add(item.id); markRead.mutate(item.id); } }
  function open(item: NotificationRow) { if (!item.is_read && !hoverReadIds.current.has(item.id)) markRead.mutate(item.id); navigate(notificationTarget(item)); }
  if (isLoading) return <PageSkeleton />;
  return <div className="notifications-page">
    <PageHeading eyebrow="УВЕДОМЛЕНИЯ" title="Все уведомления" description="История событий портала, заявок и Контакт-центра" actions={<button className="button ghost" onClick={() => markAll.mutate()} disabled={markAll.isPending}><CheckCircle2 /> Отметить всё прочитанным</button>} />
    <section className="surface notifications-page-toolbar">
      <div className="view-switch"><button className={scope === 'all' ? 'active' : ''} onClick={() => setScope('all')}>Все</button><button className={scope === 'unread' ? 'active' : ''} onClick={() => setScope('unread')}>Непрочитанные</button></div>
      <div className="search-field"><Search /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Поиск по уведомлениям" /></div>
    </section>
    <section className="surface notifications-page-list">
      {!visible.length && <EmptyState icon={<Bell />} title="Уведомлений нет" text="Измените фильтр или дождитесь новых событий" />}
      {visible.map(item => <button key={item.id} className={item.is_read ? '' : 'unread'} onMouseEnter={() => markOnHover(item)} onFocus={() => markOnHover(item)} onClick={() => open(item)}>
        <NotificationIcon item={item} />
        <span className="copy"><b>{item.title}</b>{item.body && <small>{item.body}</small>}<time>{formatDateTime(item.created_at)}</time></span>
        {!item.is_read && <em>Новое</em>}<ChevronRight />
      </button>)}
    </section>
  </div>;
}

function toDateTimeLocal(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function plannedWorkStatusName(status: PlannedWork['status']) {
  return ({ PLANNED: 'Запланировано', IN_PROGRESS: 'Выполняется', COMPLETED: 'Завершено', CANCELLED: 'Отменено' } as const)[status] || status;
}

function plannedWorkImpactName(impact: PlannedWork['impact_level']) {
  return ({ LOW: 'Низкое влияние', MEDIUM: 'Среднее влияние', HIGH: 'Высокое влияние', CRITICAL: 'Критическое влияние' } as const)[impact] || impact;
}

function plannedWorkEventName(event: PlannedWorkEvent['event_type']) {
  return ({ CREATED: 'Создание', UPDATED: 'Изменение', RESCHEDULED: 'Перенос сроков', RESPONSIBLE_CHANGED: 'Ответственный', STATUS_CHANGED: 'Статус' } as const)[event] || event;
}

function plannedWorkInitials(name?: string | null) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map(part => part[0]).join('').toUpperCase() || '—';
}

function plannedWorkDurationLabel(minutes?: number | null) {
  const value = Math.max(0, Number(minutes || 0));
  if (!value) return 'Без простоя';
  if (value < 60) return `${value} мин`;
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return rest ? `${hours} ч ${rest} мин` : `${hours} ч`;
}

function plannedWorkRelativeLabel(value: string) {
  const target = new Date(value).getTime();
  const diff = target - Date.now();
  const absolute = Math.abs(diff);
  if (absolute < 60 * 60 * 1000) {
    const minutes = Math.max(1, Math.round(absolute / 60_000));
    return diff >= 0 ? `через ${minutes} мин` : `${minutes} мин назад`;
  }
  if (absolute < 24 * 60 * 60 * 1000) {
    const hours = Math.max(1, Math.round(absolute / 3_600_000));
    return diff >= 0 ? `через ${hours} ч` : `${hours} ч назад`;
  }
  const days = Math.max(1, Math.round(absolute / 86_400_000));
  return diff >= 0 ? `через ${days} дн` : `${days} дн назад`;
}

function plannedWorkDateHeading(value: string) {
  const date = new Date(value);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const sameDay = (left: Date, right: Date) => left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
  if (sameDay(date, today)) return 'Сегодня';
  if (sameDay(date, tomorrow)) return 'Завтра';
  return date.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });
}

function plannedWorkIsOverdue(item: PlannedWork) {
  return !['COMPLETED', 'CANCELLED'].includes(item.status) && new Date(item.scheduled_end || item.scheduled_start).getTime() < Date.now();
}

function plannedWorkChecklistProgress(item?: PlannedWork | null) {
  if (!item) return 0;
  const total = Math.max(0, Number(item.checklist_total || 0));
  const completed = Math.min(total, Math.max(0, Number(item.checklist_completed || 0)));
  return total > 0 ? Math.round((completed * 100) / total) : Math.max(0, Math.min(100, Number(item.progress_percent || 0)));
}

function plannedWorkChecklistLabel(item?: PlannedWork | null) {
  if (!item) return '0 из 0';
  const total = Math.max(0, Number(item.checklist_total || 0));
  const completed = Math.min(total, Math.max(0, Number(item.checklist_completed || 0)));
  return total > 0 ? `${completed} из ${total}` : 'Без чек-листа';
}

function createPlannedChecklistDraft(item?: Partial<PlannedWorkChecklistItem>): PlannedWorkChecklistDraft {
  return {
    id: item?.id,
    client_key: item?.id || `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    title: item?.title || '',
    is_completed: Boolean(item?.is_completed),
    sort_order: Number(item?.sort_order || 0),
  };
}

function PlannedWorksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plannedSearchParams, setPlannedSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [queueId, setQueueId] = useState('');
  const [responsibleUserId, setResponsibleUserId] = useState('');
  const [impactLevel, setImpactLevel] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [view, setView] = useState<'timeline' | 'cards' | 'table'>(() => {
    const saved = localStorage.getItem('ihd-planned-view');
    return saved === 'cards' || saved === 'table' ? saved : 'timeline';
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<PlannedWork | null>(null);
  const [editorChecklist, setEditorChecklist] = useState<PlannedWorkChecklistDraft[]>([]);
  const [editorError, setEditorError] = useState('');
  const selectedId = plannedSearchParams.get('work');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const canEdit = userHasAnyRole(user, ['ADMIN', 'MANAGER', 'DISPATCHER', 'EXECUTOR']);

  useEffect(() => {
    localStorage.setItem('ihd-planned-view', view);
  }, [view]);


  function openDetail(id: string) {
    const next = new URLSearchParams(plannedSearchParams);
    next.set('work', id);
    setPlannedSearchParams(next, { replace: true });
  }

  function closeDetail() {
    const next = new URLSearchParams(plannedSearchParams);
    next.delete('work');
    setPlannedSearchParams(next, { replace: true });
  }

  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (search.trim()) params.set('search', search.trim());
  if (queueId) params.set('queueId', queueId);
  if (responsibleUserId) params.set('responsibleUserId', responsibleUserId);
  if (impactLevel) params.set('impactLevel', impactLevel);
  if (overdueOnly) params.set('overdue', 'true');
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const url = `/planned-works?${params.toString()}`;

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['planned-works', status, search, queueId, responsibleUserId, impactLevel, overdueOnly, from, to],
    queryFn: () => api<PlannedWork[]>(url),
  });
  const { data: stats } = useQuery({ queryKey: ['planned-works', 'stats'], queryFn: () => api<PlannedWorkStats>('/planned-works/stats') });
  const { data: queues = [] } = useQuery({ queryKey: ['queues'], queryFn: () => api<Queue[]>('/queues') });
  const { data: employees = [] } = useQuery({ queryKey: ['assignees'], queryFn: () => api<Assignee[]>('/users/assignees') });
  const { data: selected, isLoading: selectedLoading } = useQuery({
    queryKey: ['planned-works', 'detail', selectedId],
    queryFn: () => api<PlannedWork>(`/planned-works/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['planned-works'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const save = useMutation({
    mutationFn: async ({ id, payload, nextStatus, previousStatus }: { id?: string; payload: Record<string, unknown>; nextStatus?: PlannedWork['status']; previousStatus?: PlannedWork['status'] }) => {
      const result = id
        ? await api<PlannedWork>(`/planned-works/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
        : await api<PlannedWork>('/planned-works', { method: 'POST', body: JSON.stringify(payload) });
      if (id && nextStatus && previousStatus && nextStatus !== previousStatus) {
        await api(`/planned-works/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status: nextStatus, note: payload.completionNote }) });
      }
      return result;
    },
    onSuccess: result => {
      setEditorOpen(false);
      setEditing(null);
      openDetail(result.id);
      refresh();
    },
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, next, note }: { id: string; next: PlannedWork['status']; note?: string }) => api(`/planned-works/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status: next, note }) }),
    onSuccess: refresh,
  });

  const toggleChecklist = useMutation({
    mutationFn: ({ workId, itemId, isCompleted }: { workId: string; itemId: string; isCompleted: boolean }) => api<PlannedWork>(`/planned-works/${workId}/checklist/${itemId}`, { method: 'PATCH', body: JSON.stringify({ isCompleted }) }),
    onSuccess: result => {
      queryClient.setQueryData(['planned-works', 'detail', result.id], result);
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/planned-works/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setEditorOpen(false);
      setEditing(null);
      closeDetail();
      refresh();
    },
  });

  function openCreate() {
    save.reset();
    setEditing(null);
    setEditorChecklist([]);
    setEditorError('');
    setEditorOpen(true);
  }

  async function openEdit(item: PlannedWork) {
    save.reset();
    setEditorError('');
    try {
      const detail = item.checklist ? item : await api<PlannedWork>(`/planned-works/${item.id}`);
      setEditing(detail);
      setEditorChecklist((detail.checklist || []).map(checklistItem => createPlannedChecklistDraft(checklistItem)));
    } catch {
      setEditing(item);
      setEditorChecklist([]);
    }
    setEditorOpen(true);
  }

  function addChecklistItem() {
    if (editorChecklist.length >= 100) {
      setEditorError('В чек-листе может быть не более 100 пунктов');
      return;
    }
    setEditorError('');
    setEditorChecklist(items => [...items, { ...createPlannedChecklistDraft(), sort_order: items.length }]);
  }

  function updateChecklistItem(clientKey: string, patch: Partial<PlannedWorkChecklistDraft>) {
    setEditorChecklist(items => items.map(item => item.client_key === clientKey ? { ...item, ...patch } : item));
  }

  function removeChecklistItem(clientKey: string) {
    setEditorChecklist(items => items.filter(item => item.client_key !== clientKey).map((item, index) => ({ ...item, sort_order: index })));
  }

  function moveChecklistItem(clientKey: string, direction: -1 | 1) {
    setEditorChecklist(items => {
      const index = items.findIndex(item => item.client_key === clientKey);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= items.length) return items;
      const next = [...items];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((item, sortOrder) => ({ ...item, sort_order: sortOrder }));
    });
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const startValue = String(form.get('scheduledStart') || '');
    const endValue = String(form.get('scheduledEnd') || '');
    if (!startValue) return;
    const blankChecklistIndex = editorChecklist.findIndex(item => !item.title.trim());
    if (blankChecklistIndex >= 0) {
      setEditorError(`Заполните пункт чек-листа №${blankChecklistIndex + 1} или удалите его`);
      return;
    }
    const nextStatus = editing ? String(form.get('status') || editing.status) as PlannedWork['status'] : undefined;
    const checklistCompleted = editorChecklist.filter(item => item.is_completed).length;
    if (nextStatus === 'COMPLETED' && checklistCompleted < editorChecklist.length) {
      setEditorError(`Нельзя завершить работу: выполнено ${checklistCompleted} из ${editorChecklist.length} пунктов чек-листа`);
      return;
    }
    setEditorError('');
    const payload = {
      title: String(form.get('title') || '').trim(),
      description: String(form.get('description') || '').trim(),
      category: String(form.get('category') || '').trim(),
      scheduledStart: new Date(startValue).toISOString(),
      scheduledEnd: endValue ? new Date(endValue).toISOString() : undefined,
      recurrence: String(form.get('recurrence') || '').trim(),
      queueId: form.get('queueId') || undefined,
      responsibleUserId: form.get('responsibleUserId') || undefined,
      linkedTicketId: editing?.linked_ticket_id || undefined,
      impactLevel: String(form.get('impactLevel') || 'MEDIUM'),
      affectedServices: String(form.get('affectedServices') || '').trim(),
      expectedDowntimeMinutes: Number(form.get('expectedDowntimeMinutes') || 0),
      notifyBeforeMinutes: Number(form.get('notifyBeforeMinutes') || 0),
      completionNote: String(form.get('completionNote') || '').trim(),
      checklistItems: editorChecklist.map((item, index) => ({ id: item.id, title: item.title.trim(), isCompleted: item.is_completed, sortOrder: index })),
    };
    save.mutate({
      id: editing?.id,
      payload,
      nextStatus,
      previousStatus: editing?.status,
    });
  }

  function applyPeriodPreset(kind: 'TODAY' | 'WEEK' | 'MONTH' | 'ALL') {
    if (kind === 'ALL') {
      setFrom('');
      setTo('');
      return;
    }
    const start = new Date();
    const end = new Date();
    if (kind === 'WEEK') end.setDate(end.getDate() + 7);
    if (kind === 'MONTH') end.setMonth(end.getMonth() + 1);
    const iso = (value: Date) => value.toISOString().slice(0, 10);
    setFrom(iso(start));
    setTo(iso(end));
  }

  function resetFilters() {
    setStatus('');
    setSearch('');
    setQueueId('');
    setResponsibleUserId('');
    setImpactLevel('');
    setOverdueOnly(false);
    setFrom('');
    setTo('');
  }

  const activeFilterCount = [status, queueId, responsibleUserId, impactLevel, overdueOnly ? 'overdue' : '', from, to].filter(Boolean).length;
  const nextWork = rows.find(item => !['COMPLETED', 'CANCELLED'].includes(item.status)) || null;
  const timelineGroups = useMemo(() => {
    const groups = new Map<string, PlannedWork[]>();
    rows.forEach(item => {
      const date = new Date(item.scheduled_start);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      groups.set(key, [...(groups.get(key) || []), item]);
    });
    return Array.from(groups.entries()).map(([key, items]) => ({ key, title: plannedWorkDateHeading(items[0].scheduled_start), items }));
  }, [rows]);

  function actionButtons(item: PlannedWork, compact = false) {
    if (!canEdit) return null;
    const stop = (callback: () => void) => (event: MouseEvent<HTMLButtonElement>) => { event.stopPropagation(); callback(); };
    const checklistReady = Number(item.checklist_total || 0) === 0 || Number(item.checklist_completed || 0) >= Number(item.checklist_total || 0);
    return <div className={`planned-actions ${compact ? 'compact' : ''}`}>
      <button type="button" className="soft-button" onClick={stop(() => openEdit(item))}><Settings /> Изменить</button>
      {item.status === 'PLANNED' && <button type="button" className="soft-button primary" disabled={changeStatus.isPending} onClick={stop(() => changeStatus.mutate({ id: item.id, next: 'IN_PROGRESS' }))}><Activity /> Начать</button>}
      {item.status === 'IN_PROGRESS' && <button type="button" className="soft-button success" title={checklistReady ? 'Завершить работу' : `Сначала выполните чек-лист: ${plannedWorkChecklistLabel(item)}`} disabled={changeStatus.isPending || !checklistReady} onClick={stop(() => changeStatus.mutate({ id: item.id, next: 'COMPLETED' }))}><CheckCircle2 /> Завершить</button>}
      {['COMPLETED', 'CANCELLED'].includes(item.status) && <button type="button" className="soft-button" disabled={changeStatus.isPending} onClick={stop(() => changeStatus.mutate({ id: item.id, next: 'PLANNED' }))}><RefreshCw /> Вернуть</button>}
      {!['COMPLETED', 'CANCELLED'].includes(item.status) && <button type="button" className="soft-button danger icon-only" title="Отменить" disabled={changeStatus.isPending} onClick={stop(() => changeStatus.mutate({ id: item.id, next: 'CANCELLED' }))}><Ban /></button>}
    </div>;
  }

  if (isLoading) return <PageSkeleton />;
  return <div className="planned-works-page planned-workspace-v3 planned-workspace-v4 planned-workspace-v5">
    <PageHeading
      eyebrow="ПЛАНОВЫЕ РАБОТЫ"
      title="Плановые работы"
      description="Единый календарь обслуживания, обновлений и регламентных операций"
      actions={<>
        <div className="view-switch planned-view-switch" aria-label="Режим отображения">
          <button className={view === 'timeline' ? 'active' : ''} onClick={() => setView('timeline')}><CalendarClock /> Лента</button>
          <button className={view === 'cards' ? 'active' : ''} onClick={() => setView('cards')}><Columns3 /> Карточки</button>
          <button className={view === 'table' ? 'active' : ''} onClick={() => setView('table')}><List /> Таблица</button>
        </div>
        {canEdit && <button className="button primary" onClick={openCreate}><Plus /> Запланировать работу</button>}
      </>}
    />

    <section className={`planned-command-center ${nextWork ? '' : 'empty'}`}>
      <div className="planned-command-copy">
        <span className="planned-command-kicker"><CalendarClock /> {nextWork ? nextWork.status === 'IN_PROGRESS' ? 'Выполняется сейчас' : 'Ближайшее окно' : 'Расписание свободно'}</span>
        {nextWork ? <>
          <h2>{nextWork.title}</h2>
          <p>{nextWork.description || nextWork.affected_services || 'Описание и затрагиваемые сервисы пока не указаны.'}</p>
          <div className="planned-command-meta">
            <span><Clock3 /> {formatDateTime(nextWork.scheduled_start)}</span>
            <span><Users /> {nextWork.responsible_name || 'Ответственный не назначен'}</span>
            <span className={`impact ${nextWork.impact_level.toLowerCase()}`}><AlertTriangle /> {plannedWorkImpactName(nextWork.impact_level)}</span>
          </div>
        </> : <>
          <h2>План работ свободен</h2>
          <p>Нет активных или будущих регламентных операций. Добавьте работу, чтобы команда видела сроки и влияние.</p>
        </>}
      </div>
      {nextWork && <div className="planned-command-progress">
        <div className="planned-command-progress-head"><span>Чек-лист выполнения</span><b>{plannedWorkChecklistLabel(nextWork)}</b></div>
        <div className="planned-progress-track"><i style={{ width: `${plannedWorkChecklistProgress(nextWork)}%` }} /></div>
        <button className="button ghost compact" onClick={() => openDetail(nextWork.id)}>Открыть карточку <ChevronRight /></button>
      </div>}
    </section>

    <section className="surface planned-summary planned-summary-v3">
      <button type="button" onClick={() => { setOverdueOnly(false); applyPeriodPreset('TODAY'); }}><span className="planned-summary-icon blue"><CalendarClock /></span><b>{stats?.today ?? 0}</b><small>Сегодня</small><em>работ по плану</em></button>
      <button type="button" onClick={() => setStatus('IN_PROGRESS')}><span className="planned-summary-icon orange"><Activity /></span><b>{stats?.active ?? 0}</b><small>Выполняется</small><em>прямо сейчас</em></button>
      <button type="button" onClick={() => { setOverdueOnly(false); applyPeriodPreset('WEEK'); }}><span className="planned-summary-icon green"><RefreshCw /></span><b>{stats?.week ?? 0}</b><small>7 дней</small><em>ближайшие работы</em></button>
      <button type="button" className={overdueOnly ? 'active' : ''} onClick={() => { setStatus(''); setFrom(''); setTo(''); setOverdueOnly(true); }}><span className="planned-summary-icon red"><AlertTriangle /></span><b>{stats?.overdue ?? 0}</b><small>Просрочено</small><em>{stats?.high_impact ?? 0} с высоким влиянием</em></button>
    </section>

    <section className="surface planned-filter-panel planned-filter-panel-v3">
      <div className="planned-filter-main">
        <div className="search-field planned-search"><Search /><input aria-label="Поиск плановых работ" value={search} onChange={event => setSearch(event.target.value)} placeholder="Название, сервис, категория или описание" /></div>
        <div className="planned-status-chips" aria-label="Фильтр по статусу">
          <button className={!status && !overdueOnly ? 'active' : ''} onClick={() => { setOverdueOnly(false); setStatus(''); }}>Все</button>
          <button className={status === 'PLANNED' ? 'active blue' : ''} onClick={() => { setOverdueOnly(false); setStatus('PLANNED'); }}>План</button>
          <button className={status === 'IN_PROGRESS' ? 'active orange' : ''} onClick={() => setStatus('IN_PROGRESS')}>В работе</button>
          <button className={status === 'COMPLETED' ? 'active green' : ''} onClick={() => { setOverdueOnly(false); setStatus('COMPLETED'); }}>Завершено</button>
          <button className={status === 'CANCELLED' ? 'active' : ''} onClick={() => { setOverdueOnly(false); setStatus('CANCELLED'); }}>Отменено</button>
        </div>
        <button type="button" className={`button ghost planned-filter-toggle ${filtersOpen ? 'active' : ''}`} onClick={() => setFiltersOpen(value => !value)}><Filter /> Фильтры{activeFilterCount > 0 && <em>{activeFilterCount}</em>}<ChevronDown /></button>
      </div>
      <div className="planned-period-presets" aria-label="Период плановых работ">
        <span>Период</span>
        <button onClick={() => { setOverdueOnly(false); applyPeriodPreset('TODAY'); }}>Сегодня</button>
        <button onClick={() => { setOverdueOnly(false); applyPeriodPreset('WEEK'); }}>7 дней</button>
        <button onClick={() => { setOverdueOnly(false); applyPeriodPreset('MONTH'); }}>30 дней</button>
        <button className={!from && !to && !overdueOnly ? 'active' : ''} onClick={() => { setOverdueOnly(false); applyPeriodPreset('ALL'); }}>Весь период</button>
        {activeFilterCount > 0 && <button className="planned-reset-inline" onClick={resetFilters}><X /> Сбросить</button>}
      </div>
      {filtersOpen && <div className="planned-filter-advanced-v2 planned-filter-advanced-v3">
        <label><span>Очередь</span><select value={queueId} onChange={event => setQueueId(event.target.value)}><option value="">Все очереди</option>{queues.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label><span>Ответственный</span><select value={responsibleUserId} onChange={event => setResponsibleUserId(event.target.value)}><option value="">Все сотрудники</option>{employees.map(item => <option key={item.id} value={item.id}>{item.full_name}</option>)}</select></label>
        <label><span>Влияние</span><select value={impactLevel} onChange={event => setImpactLevel(event.target.value)}><option value="">Любое влияние</option><option value="LOW">Низкое</option><option value="MEDIUM">Среднее</option><option value="HIGH">Высокое</option><option value="CRITICAL">Критическое</option></select></label>
        <label><span>Начало периода</span><input type="date" value={from} onChange={event => setFrom(event.target.value)} /></label>
        <label><span>Конец периода</span><input type="date" value={to} onChange={event => setTo(event.target.value)} /></label>
      </div>}
    </section>

    {!rows.length && <section className="surface planned-empty"><EmptyState icon={<CalendarClock />} title="Работ по выбранным условиям нет" text={canEdit ? 'Измените фильтры или запланируйте новую работу' : 'Измените фильтры или дождитесь новых работ'} /></section>}

    {view === 'timeline' && Boolean(rows.length) && <section className="planned-agenda">
      {timelineGroups.map(group => <div className="planned-agenda-day" key={group.key}>
        <header><span>{group.title}</span><em>{new Date(group.items[0].scheduled_start).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}</em><b>{group.items.length}</b></header>
        <div className="planned-agenda-list">
          {group.items.map(item => <article className={`surface planned-agenda-item ${item.status.toLowerCase()} ${plannedWorkIsOverdue(item) ? 'overdue' : ''}`} key={item.id} tabIndex={0} onClick={() => openDetail(item.id)} onKeyDown={event => { if (event.key === 'Enter') openDetail(item.id); }}>
            <div className="planned-agenda-time"><b>{new Date(item.scheduled_start).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</b><small>{item.scheduled_end ? new Date(item.scheduled_end).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : 'без окончания'}</small></div>
            <span className={`planned-agenda-marker ${item.status.toLowerCase()}`} />
            <div className="planned-agenda-copy"><div><span className={`planned-status ${item.status.toLowerCase()}`}>{plannedWorkStatusName(item.status)}</span><span className={`planned-impact-pill ${item.impact_level.toLowerCase()}`}>{plannedWorkImpactName(item.impact_level)}</span>{plannedWorkIsOverdue(item) && <span className="planned-overdue-pill">Просрочено</span>}</div><h3>{item.title}</h3><p>{item.affected_services || item.description || 'Затрагиваемые сервисы не указаны'}</p></div>
            <div className="planned-agenda-owner"><span className="planned-avatar">{plannedWorkInitials(item.responsible_name)}</span><div><b>{item.responsible_name || 'Не назначен'}</b><small>{item.queue_name || item.category || 'Без очереди'}</small></div></div>
            <div className="planned-agenda-progress"><b>{plannedWorkChecklistLabel(item)}</b><span><i style={{ width: `${plannedWorkChecklistProgress(item)}%` }} /></span></div>
            <ChevronRight className="planned-agenda-chevron" />
          </article>)}
        </div>
      </div>)}
    </section>}

    {view === 'cards' && Boolean(rows.length) && <section className="planned-grid planned-grid-v3">
      {rows.map(item => <article className={`surface planned-work-card planned-work-card-v3 ${item.status.toLowerCase()} ${plannedWorkIsOverdue(item) ? 'overdue' : ''}`} key={item.id} onClick={() => openDetail(item.id)}>
        <div className="planned-card-date"><b>{new Date(item.scheduled_start).toLocaleDateString('ru-RU', { day: '2-digit' })}</b><span>{new Date(item.scheduled_start).toLocaleDateString('ru-RU', { month: 'short' })}</span><small>{new Date(item.scheduled_start).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</small></div>
        <div className="planned-card-content">
          <header><div><span className={`planned-status ${item.status.toLowerCase()}`}>{plannedWorkStatusName(item.status)}</span><span className={`planned-impact-pill ${item.impact_level.toLowerCase()}`}>{plannedWorkImpactName(item.impact_level)}</span></div><button type="button" className="icon-button" onClick={event => { event.stopPropagation(); openDetail(item.id); }}><ChevronRight /></button></header>
          <h2>{item.title}</h2><p>{item.description || 'Описание не указано'}</p>
          <div className="planned-service-box"><Tag /><span><small>Затрагивает</small><b>{item.affected_services || item.category || 'Не указано'}</b></span></div>
          <div className="planned-card-meta"><span><Timer /> {plannedWorkDurationLabel(item.expected_downtime_minutes)}</span><span><Bell /> за {item.notify_before_minutes || 0} мин</span>{item.recurrence && <span><RefreshCw /> {item.recurrence}</span>}</div>
          <div className="planned-card-progress"><div><span>Чек-лист</span><b>{plannedWorkChecklistLabel(item)}</b></div><span><i style={{ width: `${plannedWorkChecklistProgress(item)}%` }} /></span></div>
          <footer><div className="planned-owner-inline"><span className="planned-avatar">{plannedWorkInitials(item.responsible_name)}</span><div><b>{item.responsible_name || 'Ответственный не назначен'}</b><small>{item.queue_name || 'Без очереди'}</small></div></div>{actionButtons(item, true)}</footer>
        </div>
      </article>)}
    </section>}

    {view === 'table' && Boolean(rows.length) && <section className="surface planned-table planned-table-v3">
      <div className="planned-table-head"><span>Срок</span><span>Работа и влияние</span><span>Статус</span><span>Ответственный</span><span>Чек-лист</span><span /></div>
      {rows.map(item => <div className={`planned-table-row ${plannedWorkIsOverdue(item) ? 'overdue' : ''}`} key={item.id} onClick={() => openDetail(item.id)}>
        <span className="planned-table-date"><b>{new Date(item.scheduled_start).toLocaleDateString('ru-RU')}</b><small>{new Date(item.scheduled_start).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}{item.scheduled_end ? ` — ${new Date(item.scheduled_end).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}` : ''}</small></span>
        <span className="planned-table-title"><b>{item.title}</b><small>{item.affected_services || item.category || item.description || 'Без описания'}</small></span>
        <span><em className={`planned-status ${item.status.toLowerCase()}`}>{plannedWorkStatusName(item.status)}</em><em className={`planned-impact-pill ${item.impact_level.toLowerCase()}`}>{plannedWorkImpactName(item.impact_level)}</em></span>
        <span className="planned-table-owner"><span className="planned-avatar">{plannedWorkInitials(item.responsible_name)}</span><span>{item.responsible_name || 'Не назначен'}<small>{item.queue_name || 'Без очереди'}</small></span></span>
        <span className="planned-table-progress"><b>{plannedWorkChecklistLabel(item)}</b><span><i style={{ width: `${plannedWorkChecklistProgress(item)}%` }} /></span></span>
        <span><ChevronRight /></span>
      </div>)}
    </section>}

    {selectedId && <div className="modal-backdrop planned-detail-backdrop" onMouseDown={event => { if (event.currentTarget === event.target) closeDetail(); }}>
      <aside className="planned-detail-drawer">
        {selectedLoading || !selected ? <PageSkeleton /> : <>
          <header className="planned-detail-head"><div><span className={`planned-status ${selected.status.toLowerCase()}`}>{plannedWorkStatusName(selected.status)}</span><span className={`planned-impact-pill ${selected.impact_level.toLowerCase()}`}>{plannedWorkImpactName(selected.impact_level)}</span><h2>{selected.title}</h2><p>{selected.description || 'Описание не указано'}</p></div><button className="icon-button" onClick={closeDetail}><X /></button></header>
          <div className="planned-detail-body">
            <section className="planned-detail-schedule"><div><span>Плановое окно</span><b>{formatDateTime(selected.scheduled_start)}</b><small>{selected.scheduled_end ? `до ${formatDateTime(selected.scheduled_end)}` : 'Окончание не задано'}</small></div><div><span>Ожидаемый простой</span><b>{plannedWorkDurationLabel(selected.expected_downtime_minutes)}</b><small>Оповещение за {selected.notify_before_minutes || 0} мин</small></div></section>
            <section className="planned-detail-progress"><div><span>Чек-лист выполнения</span><b>{plannedWorkChecklistLabel(selected)}</b></div><span><i style={{ width: `${plannedWorkChecklistProgress(selected)}%` }} /></span>{selected.status === 'IN_PROGRESS' && selected.actual_start && <small>Начато {formatDateTime(selected.actual_start)}</small>}{selected.actual_end && <small>Завершено {formatDateTime(selected.actual_end)}</small>}</section>
            <section className="planned-detail-section"><h3>Организация</h3><div className="planned-detail-grid"><div><span><Users /></span><small>Ответственный</small><b>{selected.responsible_name || 'Не назначен'}</b></div><div><span><Inbox /></span><small>Очередь</small><b>{selected.queue_name || 'Без очереди'}</b></div><div><span><Tag /></span><small>Категория</small><b>{selected.category || 'Без категории'}</b></div><div><span><RefreshCw /></span><small>Повторение</small><b>{selected.recurrence || 'Не повторяется'}</b></div></div></section>
            <section className="planned-detail-section"><h3>Затрагиваемые сервисы</h3><div className="planned-affected-services">{selected.affected_services || 'Не указаны. Добавьте сервисы и компоненты, которые могут быть недоступны во время работ.'}</div></section>
            <section className="planned-detail-section planned-detail-checklist"><div className="planned-detail-section-head"><h3><ListChecks /> Чек-лист выполнения</h3><span>{plannedWorkChecklistLabel(selected)}</span></div>{selected.checklist?.length ? <div className="planned-detail-checklist-list">{selected.checklist.map(item => <button type="button" key={item.id} className={item.is_completed ? 'completed' : ''} disabled={!canEdit || toggleChecklist.isPending || ['COMPLETED','CANCELLED'].includes(selected.status)} onClick={() => toggleChecklist.mutate({ workId: selected.id, itemId: item.id, isCompleted: !item.is_completed })}><span className="planned-check-box">{item.is_completed && <Check />}</span><span><b>{item.title}</b>{item.is_completed && <small>{item.completed_by_name || 'Выполнено'}{item.completed_at ? ` · ${formatDateTime(item.completed_at)}` : ''}</small>}</span></button>)}</div> : <div className="planned-detail-checklist-empty"><ListChecks /><b>Чек-лист пока не добавлен</b><small>Откройте редактирование работы и добавьте контрольные пункты.</small></div>}{toggleChecklist.error && <div className="form-error planned-checklist-error">{toggleChecklist.error instanceof Error ? toggleChecklist.error.message : 'Не удалось обновить чек-лист'}</div>}</section>
            {selected.completion_note && <section className="planned-detail-section planned-completion-note"><h3><CheckCircle2 /> Результат выполнения</h3><p>{selected.completion_note}</p></section>}
            {selected.linked_ticket_id && <section className="planned-detail-section"><h3>Связанная заявка</h3><button className="planned-linked-ticket" onClick={() => navigate(`/tickets/${selected.linked_ticket_id}`)}><TicketIcon /><span><small>Заявка</small><b>{selected.ticket_display_number || 'Открыть'}</b></span><ChevronRight /></button></section>}
            <section className="planned-detail-section"><h3>История</h3><div className="planned-event-list">{selected.events?.map(event => <div key={event.id}><span className={`planned-event-icon ${event.event_type.toLowerCase()}`}><Activity /></span><div><b>{plannedWorkEventName(event.event_type)}</b><p>{event.description}</p><small>{event.actor_name || 'Система'} · {formatDateTime(event.created_at)}</small></div></div>)}{!selected.events?.length && <p className="muted">История пока пуста</p>}</div></section>
          </div>
          <footer className="planned-detail-actions"><span />{actionButtons(selected)}</footer>
        </>}
      </aside>
    </div>}

    {editorOpen && <div className="modal-backdrop planned-editor-backdrop" onMouseDown={event => { if (event.currentTarget === event.target) { setEditorOpen(false); setEditing(null); setEditorChecklist([]); setEditorError(''); } }}><form key={editing?.id || 'new'} className="modal-card planned-editor planned-editor-v4 planned-editor-v5" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
      <div className="modal-head planned-editor-head"><div><span className="modal-kicker">ПЛАНОВАЯ РАБОТА</span><div className="planned-editor-title-row"><h2>{editing ? 'Редактирование работы' : 'Новая плановая работа'}</h2>{editing && <span className={`planned-status ${editing.status.toLowerCase()}`}>{plannedWorkStatusName(editing.status)}</span>}</div><p>{editing ? 'Уточните данные, контрольные шаги и итоговый результат' : 'Создайте понятный план работ для команды и пользователей'}</p></div><button type="button" className="icon-button" onClick={() => { setEditorOpen(false); setEditing(null); setEditorChecklist([]); setEditorError(''); }}><X /></button></div>
      <div className="planned-editor-layout planned-editor-layout-v4">
        <section className="planned-editor-section planned-editor-main"><div className="planned-section-title"><span><FileText /></span><div><h3>Основная информация</h3><p>Что будет сделано и зачем это нужно</p></div></div><div className="form-grid planned-main-fields"><label>Название<input name="title" defaultValue={editing?.title || ''} required minLength={2} autoFocus placeholder="Например: Обновление серверов 1С" /></label><label>Категория<input name="category" defaultValue={editing?.category || ''} list="planned-categories" placeholder="Выберите или укажите категорию" /><datalist id="planned-categories"><option value="Инфраструктура" /><option value="1С" /><option value="Безопасность" /><option value="Серверы" /><option value="Сеть" /><option value="Рабочие станции" /><option value="Почта" /><option value="Резервное копирование" /></datalist></label></div><label>Описание<textarea name="description" rows={4} defaultValue={editing?.description || ''} placeholder="Опишите последовательность работ, ограничения и важные условия" maxLength={4000} /></label>{editing && <label className="planned-status-field">Статус<select name="status" defaultValue={editing.status}><option value="PLANNED">Запланировано</option><option value="IN_PROGRESS">Выполняется</option><option value="COMPLETED">Завершено</option><option value="CANCELLED">Отменено</option></select></label>}</section>

        <section className="planned-editor-section"><div className="planned-section-title"><span><AlertTriangle /></span><div><h3>Влияние на сервисы</h3><p>Что изменится и насколько это заметят пользователи</p></div></div><div className="form-grid two"><label>Уровень влияния<select name="impactLevel" defaultValue={editing?.impact_level || 'MEDIUM'}><option value="LOW">Низкое — без заметных ограничений</option><option value="MEDIUM">Среднее — возможны краткие перебои</option><option value="HIGH">Высокое — часть сервисов недоступна</option><option value="CRITICAL">Критическое — массовая недоступность</option></select></label><label>Ожидаемый простой, минут<input name="expectedDowntimeMinutes" type="number" min="0" max="10080" defaultValue={editing?.expected_downtime_minutes || 0} /></label></div><label>Затрагиваемые сервисы<textarea name="affectedServices" rows={4} defaultValue={editing?.affected_services || ''} placeholder="1С, файловый сервер, корпоративная почта, VPN…" maxLength={2000} /></label></section>

        <section className="planned-editor-section"><div className="planned-section-title"><span><CalendarClock /></span><div><h3>Сроки и оповещение</h3><p>Когда пройдут работы и когда предупредить команду</p></div></div><div className="form-grid two"><label>Начало<input name="scheduledStart" type="datetime-local" defaultValue={toDateTimeLocal(editing?.scheduled_start)} required /></label><label>Окончание<input name="scheduledEnd" type="datetime-local" defaultValue={toDateTimeLocal(editing?.scheduled_end)} /></label></div><div className="form-grid two"><label>Повторение<select name="recurrence" defaultValue={editing?.recurrence || ''}><option value="">Не повторять</option><option value="ежедневно">Ежедневно</option><option value="еженедельно">Еженедельно</option><option value="ежемесячно">Ежемесячно</option><option value="ежегодно">Ежегодно</option></select></label><label>Напомнить заранее<select name="notifyBeforeMinutes" defaultValue={editing?.notify_before_minutes ?? 60}><option value="0">Не напоминать</option><option value="15">За 15 минут</option><option value="30">За 30 минут</option><option value="60">За 1 час</option><option value="180">За 3 часа</option><option value="1440">За 1 день</option><option value="2880">За 2 дня</option><option value="10080">За неделю</option></select></label></div></section>

        <section className="planned-editor-section"><div className="planned-section-title"><span><Users /></span><div><h3>Ответственность</h3><p>Кто выполняет работу и где она контролируется</p></div></div><div className="form-grid two"><label>Очередь<select name="queueId" defaultValue={editing?.queue_id || ''}><option value="">Без очереди</option>{queues.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label><label>Ответственный<select name="responsibleUserId" defaultValue={editing?.responsible_user_id || ''}><option value="">Не назначен</option>{employees.map(item => <option key={item.id} value={item.id}>{item.full_name}</option>)}</select></label></div><div className="planned-editor-hint"><Bell /><span><b>Оповещение ответственного</b><small>Назначенный сотрудник получит уведомление о работе и изменении сроков.</small></span></div></section>

        <section className="planned-editor-section planned-editor-checklist"><div className="planned-section-title"><span><ListChecks /></span><div><h3>Чек-лист работ</h3><p>Шаги выполнения и контрольные точки</p></div><em>{editorChecklist.filter(item => item.is_completed).length} из {editorChecklist.length} выполнено</em></div>{editorChecklist.length ? <div className="planned-editor-checklist-list">{editorChecklist.map((item, index) => <div className={`planned-editor-checklist-row ${item.is_completed ? 'completed' : ''}`} key={item.client_key}><button type="button" className="planned-check-box" onClick={() => updateChecklistItem(item.client_key, { is_completed: !item.is_completed })} aria-label={item.is_completed ? 'Вернуть пункт в работу' : 'Отметить пункт выполненным'}>{item.is_completed && <Check />}</button><GripVertical className="planned-checklist-grip" /><input value={item.title} onChange={event => updateChecklistItem(item.client_key, { title: event.target.value })} placeholder={`Пункт ${index + 1}: опишите проверяемый шаг`} maxLength={500} /><div className="planned-checklist-row-actions"><button type="button" className="icon-button" disabled={index === 0} onClick={() => moveChecklistItem(item.client_key, -1)} title="Переместить выше"><ArrowUp /></button><button type="button" className="icon-button" disabled={index === editorChecklist.length - 1} onClick={() => moveChecklistItem(item.client_key, 1)} title="Переместить ниже"><ArrowDown /></button><button type="button" className="icon-button danger" onClick={() => removeChecklistItem(item.client_key)} title="Удалить пункт"><Trash2 /></button></div></div>)}</div> : <div className="planned-editor-checklist-empty"><ListChecks /><b>Добавьте контрольные шаги</b><small>Чек-лист заменяет ручной процент готовности и показывает реальный ход работ.</small></div>}<button type="button" className="soft-button planned-add-checklist" onClick={addChecklistItem}><Plus /> Добавить пункт</button></section>

        <section className="planned-editor-section planned-editor-result"><div className="planned-section-title"><span><CheckCircle2 /></span><div><h3>Результат и примечания</h3><p>{editing ? 'Фактический итог, отклонения и проверки после работ' : 'Какой результат команда должна получить по завершении'}</p></div></div><label>{editing ? 'Результат выполнения' : 'Ожидаемый результат'}<textarea name="completionNote" rows={5} defaultValue={editing?.completion_note || ''} placeholder={editing ? 'Что выполнено, были ли отклонения и что нужно проверить после работ' : 'Опишите ожидаемый результат и критерии успешного завершения'} maxLength={4000} /></label></section>
      </div>
      {(editorError || save.error) && <div className="form-error planned-editor-error">{editorError || (save.error instanceof Error ? save.error.message : 'Не удалось сохранить работу')}</div>}
      <div className="form-actions planned-editor-actions">{editing && <button type="button" className="button ghost danger" disabled={remove.isPending} onClick={() => { if (window.confirm(`Удалить плановую работу «${editing.title}»?`)) remove.mutate(editing.id); }}><Trash2 /> Удалить</button>}<span /><button type="button" className="button ghost" onClick={() => { setEditorOpen(false); setEditing(null); setEditorChecklist([]); setEditorError(''); }}>Отмена</button><button className="button primary" disabled={save.isPending}><Save /> {save.isPending ? 'Сохранение…' : editing ? 'Сохранить работу' : 'Создать работу'}</button></div>
    </form></div>}
  </div>;
}

function telephonyCallPhaseLabel(call?: TelephonyCall | null) {
  if (!call) return 'Готов к звонку';
  if (call.phase === 'TIMEOUT') return 'Истёк тайм-аут';
  return ({
    REQUESTED: 'Запрос отправлен',
    RINGING: 'Идёт вызов',
    CONNECTED: 'Разговор',
    COMPLETED: 'Завершён',
    MISSED: 'Нет ответа',
    FAILED: 'Ошибка звонка',
  } as Record<string, string>)[call.phase || ''] || telephonyStatusLabel(call.status);
}

function telephonyCallInstruction(call?: TelephonyCall | null) {
  if (!call) return 'Нажмите «Позвонить». Билайн сначала вызовет ваш привязанный телефон, а после ответа соединит вас с клиентом.';
  if (call.phase === 'TIMEOUT') return call.failure_reason || 'АТС не прислала следующее событие вовремя. Повторите звонок или проверьте подписку.';
  if (call.status === 'INITIATED') return 'Запрос принят. Ожидайте звонка на телефон сотрудника, привязанный к вашему пользователю портала.';
  if (call.status === 'RINGING') return 'Идёт вызов. Ответьте на своём рабочем телефоне и дождитесь соединения с клиентом.';
  if (call.status === 'ANSWERED') return 'Соединение установлено. Разговор и длительность обновляются автоматически.';
  if (call.status === 'COMPLETED') return 'Звонок успешно завершён и сохранён в журнале телефонии.';
  if (call.status === 'MISSED') return call.failure_reason || 'Одна из сторон не ответила на звонок.';
  return call.failure_reason || 'АТС не смогла выполнить звонок. Проверьте привязку абонента и журнал интеграции.';
}

function TelephonyDialModal({ target, onClose }: { target: TelephonyDialTarget; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedPhone, setSelectedPhone] = useState(target.phones[0]?.value || '');
  const [callId, setCallId] = useState('');
  const [resultMessage, setResultMessage] = useState('');
  const [clockNow, setClockNow] = useState(() => Date.now());
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['telephony-status'],
    queryFn: () => api<TelephonyStatus>('/telephony/status'),
    staleTime: 15_000,
  });
  const { data: browserPhoneConfig } = useQuery({
    queryKey: ['telephony-browser-config'],
    queryFn: () => api<any>('/telephony/browser/config'),
    staleTime: 15_000,
  });
  const { data: call } = useQuery({
    queryKey: ['telephony-call', callId],
    queryFn: () => api<TelephonyCall>(`/telephony/calls/${callId}`),
    enabled: Boolean(callId),
    refetchInterval: callId ? 2_000 : false,
  });
  const dial = useMutation({
    mutationFn: () => api<{ success: boolean; message: string; call: TelephonyCall }>('/telephony/dial', {
      method: 'POST',
      body: JSON.stringify({
        phone: selectedPhone,
        contactId: target.contactId,
        companyId: target.companyId,
        ticketId: target.ticketId,
      }),
    }),
    onSuccess: value => {
      setCallId(value.call.id);
      setResultMessage(value.message);
      queryClient.invalidateQueries({ queryKey: ['telephony-calls'] });
    },
  });
  const final = Boolean(call && ['COMPLETED', 'MISSED', 'FAILED'].includes(call.status));
  const mappedAbonent = settings?.dialAbonent;
  const mappedLabel = mappedAbonent
    ? [mappedAbonent.phone && formatPhoneInput(mappedAbonent.phone), mappedAbonent.extension && `доб. ${mappedAbonent.extension}`].filter(Boolean).join(' · ')
    : '';
  const browserReady = Boolean(browserPhoneConfig?.configured && browserPhoneConfig?.account?.allowOutbound);
  const canStartCall = browserReady || Boolean(settings?.canDial);
  const startPreferredCall = () => {
    if (browserReady) {
      window.dispatchEvent(new CustomEvent('intez:phone-dial', { detail: { phone: selectedPhone, contactId: target.contactId, companyId: target.companyId, ticketId: target.ticketId, title: target.contactName } }));
      onClose();
      return;
    }
    dial.mutate();
  };

  useEffect(() => {
    if (call) queryClient.invalidateQueries({ queryKey: ['telephony-calls'] });
  }, [call?.status, call?.phase, call?.duration_seconds]);

  useEffect(() => {
    if (call?.status !== 'ANSWERED') return;
    setClockNow(Date.now());
    const timer = window.setInterval(() => setClockNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [call?.status, call?.answered_at]);

  const liveDuration = call?.status === 'ANSWERED' && call.answered_at
    ? Math.max(Number(call.duration_seconds || 0), Math.floor((clockNow - new Date(call.answered_at).getTime()) / 1_000))
    : Number(call?.duration_seconds || 0);

  return <div className="modal-backdrop telephony-dial-backdrop" onMouseDown={event => { if (event.currentTarget === event.target && !dial.isPending) onClose(); }}>
    <section className="modal-card telephony-dial-modal" onMouseDown={event => event.stopPropagation()}>
      <div className="modal-head"><div><span className="modal-kicker">ИСХОДЯЩИЙ ЗВОНОК</span><h2>{target.contactName || 'Позвонить через АТС'}</h2><p>{target.companyName || 'Звонок будет выполнен через подключённую телефонию.'}</p></div><button type="button" className="icon-button" onClick={onClose}><X /></button></div>
      {!callId ? <div className="telephony-dial-content">
        <div className="telephony-dial-party"><span><Phone /></span><div><small>Кому звоним</small><b>{target.contactName || 'Номер телефона'}</b>{target.phones.length > 1 ? <select value={selectedPhone} onChange={event => setSelectedPhone(event.target.value)}>{target.phones.map(item => <option key={item.value} value={item.value}>{item.label ? `${item.label} · ` : ''}{formatPhoneInput(item.value) || item.value}</option>)}</select> : <strong>{formatPhoneInput(selectedPhone) || selectedPhone}</strong>}</div></div>
        <div className={`telephony-dial-readiness ${canStartCall ? 'ready' : 'blocked'}`}><span>{canStartCall ? <CheckCircle2 /> : <AlertTriangle />}</span><div><b>{settingsLoading ? 'Проверяем телефонию…' : canStartCall ? browserReady ? 'Гарнитура готова к звонку' : 'Готово к звонку через АТС' : 'Звонок недоступен'}</b><small>{browserReady ? `Разговор начнётся прямо в портале через SIP/WebRTC · доб. ${browserPhoneConfig?.account?.extension || '—'}` : settings?.canDial ? (mappedLabel ? `Резервный режим: Билайн сначала позвонит вам: ${mappedLabel}` : 'АТС сначала позвонит сотруднику, затем клиенту') : browserPhoneConfig?.unavailableReason || settings?.dialUnavailableReason || 'Проверьте настройки АТС'}</small></div></div>
        <div className="telephony-dial-how"><b>{browserReady ? 'Разговор в портале' : 'Как пройдёт звонок'}</b>{browserReady ? <ol><li><em>1</em><span>Браузер откроет SIP-сессию через защищённый WSS</span></li><li><em>2</em><span>Разрешите порталу использовать микрофон гарнитуры</span></li><li><em>3</em><span>Управляйте разговором во всплывающей карточке</span></li><li><em>4</em><span>Результат, длительность и связь с заявкой сохранятся</span></li></ol> : <ol><li><em>1</em><span>Портал отправит запрос в Билайн</span></li><li><em>2</em><span>Зазвонит ваш привязанный рабочий телефон</span></li><li><em>3</em><span>После вашего ответа Билайн позвонит клиенту</span></li><li><em>4</em><span>Статус и длительность сохранятся в журнале</span></li></ol>}</div>
        {dial.error && <div className="form-error">{dial.error instanceof Error ? dial.error.message : 'Не удалось запустить звонок'}</div>}
      </div> : <div className="telephony-call-progress">
        <div className={`telephony-call-progress-icon ${call?.status?.toLowerCase() || 'initiated'}`}><Phone /></div>
        <h3>{telephonyCallPhaseLabel(call)}</h3>
        <p>{telephonyCallInstruction(call)}</p>
        <strong>{formatPhoneInput(selectedPhone) || selectedPhone}</strong>
        <div className="telephony-call-progress-steps"><span className="done"><Check />Запрос</span><span className={call && ['RINGING','ANSWERED','COMPLETED','MISSED','FAILED'].includes(call.status) ? 'done' : ''}><Phone />Вызов</span><span className={call && ['ANSWERED','COMPLETED'].includes(call.status) ? 'done' : ''}><Activity />Разговор</span><span className={final ? 'done' : ''}><CheckCircle2 />Завершение</span></div>
        {call?.status === 'ANSWERED' && <div className="telephony-live-duration"><span className="telephony-live-dot" />Разговор: {callDurationLabel(liveDuration)}</div>}
        {call?.failure_reason && <div className="form-error telephony-call-failure">{call.failure_reason.replace(/^AUTO_TIMEOUT:\s*/, '')}</div>}
        {resultMessage && !call && <small>{resultMessage}</small>}
      </div>}
      <div className="form-actions telephony-dial-actions"><button type="button" className="button ghost" onClick={() => { onClose(); navigate('/calls'); }}><List /> Открыть журнал</button><span />{!callId ? <><button type="button" className="button ghost" onClick={onClose}>Отмена</button><button type="button" className="button primary" disabled={!canStartCall || !selectedPhone || dial.isPending} onClick={startPreferredCall}><Phone /> {dial.isPending ? 'Передаём в АТС…' : browserReady ? 'Позвонить в портале' : 'Позвонить через АТС'}</button></> : <button type="button" className={`button ${final ? 'primary' : 'ghost'}`} onClick={onClose}>{final ? 'Готово' : 'Скрыть окно'}</button>}</div>
    </section>
  </div>;
}

function CallsWorkspacePage() {
  const location = useLocation();
  const [dialTarget, setDialTarget] = useState<TelephonyDialTarget | null>(null);
  const requestedTab = String(new URLSearchParams(location.search).get('tab') || '').toUpperCase();
  const initialTab = requestedTab === 'MISSED' || requestedTab === 'WORKPLACE'
    ? 'WORKPLACE'
    : requestedTab === 'STATISTICS'
      ? 'STATISTICS'
      : 'JOURNAL';

  return <>
    <CallsPage
      initialTab={initialTab}
      onDial={target => setDialTarget({
        phones: [{ value: target.phone }],
        contactId: target.contactId,
        companyId: target.companyId,
        ticketId: target.ticketId,
        contactName: target.contactName,
        companyName: target.companyName,
      })}
    />
    {dialTarget && <TelephonyDialModal target={dialTarget} onClose={() => setDialTarget(null)} />}
  </>;
}

function telephonyStatusLabel(value: TelephonyCall['status']) {
  return ({ INITIATED: 'Запрос отправлен', RINGING: 'Вызов', ANSWERED: 'Разговор', COMPLETED: 'Завершён', MISSED: 'Пропущен', FAILED: 'Ошибка' } as Record<TelephonyCall['status'], string>)[value] || value;
}

function callDurationLabel(seconds: number) {
  const safe = Math.max(0, Number(seconds || 0));
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

function emailDeliveryStatusName(status: string) {
  return ({
    PENDING: 'В очереди',
    SENDING: 'Отправляется',
    RETRY: 'Повтор',
    SENT: 'Отправлено',
    FAILED: 'Ошибка',
    CANCELLED: 'Отменено',
  } as Record<string, string>)[status] || status;
}

function emailWorkerStatusName(status: string) {
  return ({
    STARTING: 'Запускается',
    RUNNING: 'Работает',
    DISABLED: 'Канал отключён',
    MISCONFIGURED: 'Не настроен',
    ERROR: 'Ошибка',
    UNKNOWN: 'Нет данных',
    STOPPED: 'Не запущена',
  } as Record<string, string>)[status] || status;
}

function NotificationSettings() {
  const queryClient = useQueryClient();
  const { data, isLoading, error: queryError } = useQuery({
    queryKey: ['email-notification-preferences'],
    queryFn: () => api<EmailNotificationPreferences>('/notifications/email-preferences'),
  });
  const [form, setForm] = useState<{ enabled: boolean; recipient: string; events: Record<string, boolean> } | null>(null);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!data) return;
    setForm({
      enabled: data.enabled,
      recipient: data.recipient,
      events: Object.fromEntries(data.events.map(event => [event.type, event.enabled])),
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      if (!form) throw new Error('Настройки ещё не загружены');
      return api<EmailNotificationPreferences>('/notifications/email-preferences', {
        method: 'PUT',
        body: JSON.stringify(form),
      });
    },
    onSuccess: value => {
      setForm({ enabled: value.enabled, recipient: value.recipient, events: Object.fromEntries(value.events.map(event => [event.type, event.enabled])) });
      setError('');
      setFeedback('Настройки почтовых уведомлений сохранены');
      queryClient.setQueryData(['email-notification-preferences'], value);
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить настройки'),
  });

  const test = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error('Настройки ещё не загружены');
      const saved = await api<EmailNotificationPreferences>('/notifications/email-preferences', {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      queryClient.setQueryData(['email-notification-preferences'], saved);
      return api<{ success: boolean; queued: boolean; status: string; recipient: string; sentAt?: string | null }>('/notifications/email-test', { method: 'POST' });
    },
    onSuccess: value => {
      setError('');
      setFeedback(value.status === 'SENT' ? `Тестовое письмо отправлено на ${value.recipient}` : `Тестовое письмо поставлено в очередь для ${value.recipient}`);
      window.setTimeout(() => queryClient.invalidateQueries({ queryKey: ['email-notification-preferences'] }), 1800);
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось отправить тестовое письмо'),
  });

  if (isLoading) return <PageSkeleton />;
  if (!data || !form) return <div className="admin-feedback error">{queryError instanceof Error ? queryError.message : 'Не удалось загрузить настройки уведомлений'}</div>;

  const groups = [...new Set(data.events.map(event => event.group))];
  return <div className="email-preferences-page">
    <PageHeading
      eyebrow="УВЕДОМЛЕНИЯ"
      title="Почтовые уведомления"
      description="Выберите события заявок, о которых портал должен сообщать вам по электронной почте"
      actions={<button className="button primary" onClick={() => save.mutate()} disabled={save.isPending}><Save /> {save.isPending ? 'Сохранение…' : 'Сохранить настройки'}</button>}
    />
    {feedback && <div className="admin-feedback success">{feedback}</div>}
    {error && <div className="admin-feedback error">{error}</div>}

    <section className={`surface email-personal-card ${form.enabled ? 'active' : ''}`}>
      <div className="email-personal-main">
        <span className="email-personal-icon"><Mail /></span>
        <div><h2>Получать письма по заявкам</h2><p>Адрес учётной записи: <b>{data.accountEmail}</b></p></div>
        <label className="compact-toggle"><input type="checkbox" checked={form.enabled} onChange={event => setForm(value => value ? ({ ...value, enabled: event.target.checked }) : value)} /><span /></label>
      </div>
      <div className="email-personal-address">
        <label>Email для уведомлений<input type="email" value={form.recipient} disabled={!form.enabled} onChange={event => setForm(value => value ? ({ ...value, recipient: event.target.value }) : value)} placeholder={data.accountEmail} /></label>
        <button className="button ghost" type="button" onClick={() => test.mutate()} disabled={!form.enabled || !data.smtpAvailable || test.isPending}>{test.isPending ? 'Постановка в очередь…' : 'Отправить тестовое письмо'}</button>
      </div>
      <div className={`smtp-user-state ${data.smtpAvailable ? 'ready' : 'not-ready'}`}>
        {data.smtpAvailable ? <CheckCircle2 /> : <AlertTriangle />}
        <span><b>{data.smtpAvailable ? 'Почтовый сервер готов' : 'Почтовый сервер не настроен'}</b><small>{data.smtpAvailable ? 'Письма отправляются через SMTP, настроенный администратором.' : 'Сохранить предпочтения можно сейчас, отправка начнётся после настройки SMTP.'}</small></span>
      </div>
    </section>

    <section className="surface email-event-settings">
      <div className="surface-head"><div><h2>Какие письма получать</h2><p>Отключённые события останутся видны в колокольчике портала, но не будут отправляться на почту.</p></div></div>
      {groups.map(group => <div className="email-event-group" key={group}>
        <h3>{group}</h3>
        {data.events.filter(event => event.group === group).map(event => <label className="email-event-row" key={event.type}>
          <span><b>{event.label}</b><small>{event.description}</small></span>
          <input
            type="checkbox"
            checked={Boolean(form.events[event.type])}
            disabled={!form.enabled}
            onChange={change => setForm(value => value ? ({ ...value, events: { ...value.events, [event.type]: change.target.checked } }) : value)}
          />
        </label>)}
      </div>)}
      <div className="email-mention-note"><MessageSquare /><span><b>Упоминания в комментариях</b><small>Чтобы адресно уведомить сотрудника, используйте его логин: <code>@login</code>.</small></span></div>
    </section>

    <section className="surface email-personal-history">
      <div className="surface-head"><div><h2>Последние письма</h2><p>Статусы отправки и сообщения SMTP для вашей учётной записи</p></div></div>
      {!data.deliveries.length ? <EmptyState compact icon={<Mail />} title="Писем пока не было" text="После событий заявок здесь появится история отправки." /> : <div className="email-delivery-list">
        {data.deliveries.map(item => <div className="email-delivery-row" key={item.id}>
          <span className={`email-delivery-icon ${item.status.toLowerCase()}`}><Mail /></span>
          <span><b>{item.title}</b><small>{item.recipient} · {formatDateTime(item.created_at)}</small>{item.last_error && <em>{item.last_error}</em>}</span>
          <strong className={`delivery-status ${item.status.toLowerCase()}`}>{emailDeliveryStatusName(item.status)}</strong>
        </div>)}
      </div>}
    </section>
  </div>;
}

function EmailNotificationAdmin({ mode, onOpenNotifications }: { mode: 'notifications' | 'mail'; onOpenNotifications?: () => void }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error: queryError } = useQuery({
    queryKey: ['email-notification-settings'],
    queryFn: () => api<EmailNotificationSettings>('/integrations/email-notifications'),
    refetchInterval: mode === 'notifications' ? 5_000 : false,
  });
  const { data: deliveries = [] } = useQuery({
    queryKey: ['email-notification-deliveries'],
    queryFn: () => api<EmailDeliveryAdmin[]>('/integrations/email-notifications/deliveries'),
    refetchInterval: 5_000,
    enabled: mode === 'notifications',
  });
  const [form, setForm] = useState({
    enabled: false,
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: false,
    smtpTlsRejectUnauthorized: true,
    smtpUsername: '',
    smtpPassword: '',
    clearSmtpPassword: false,
    fromName: 'Intez Helpdesk',
    fromEmail: '',
    replyTo: '',
    imapEnabled: false,
    imapHost: '',
    imapPort: 993,
    imapSecure: true,
    imapTlsRejectUnauthorized: true,
    imapUsername: '',
    imapPassword: '',
    clearImapPassword: false,
  });
  const [testRecipient, setTestRecipient] = useState('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const formInitialized = useRef(false);

  useEffect(() => {
    setFeedback('');
    setError('');
  }, [mode]);

  useEffect(() => {
    if (!data || formInitialized.current) return;
    formInitialized.current = true;
    setForm({
      enabled: data.enabled,
      smtpHost: data.smtpHost,
      smtpPort: data.smtpPort,
      smtpSecure: data.smtpSecure,
      smtpTlsRejectUnauthorized: data.smtpTlsRejectUnauthorized,
      smtpUsername: data.smtpUsername,
      smtpPassword: '',
      clearSmtpPassword: false,
      fromName: data.fromName,
      fromEmail: data.fromEmail,
      replyTo: data.replyTo,
      imapEnabled: data.imapEnabled,
      imapHost: data.imapHost,
      imapPort: data.imapPort,
      imapSecure: data.imapSecure,
      imapTlsRejectUnauthorized: data.imapTlsRejectUnauthorized,
      imapUsername: data.imapUsername,
      imapPassword: '',
      clearImapPassword: false,
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () => api<EmailNotificationSettings>('/integrations/email-notifications', { method: 'PUT', body: JSON.stringify(form) }),
    onSuccess: value => {
      setError('');
      setFeedback(mode === 'notifications' ? 'Настройки уведомлений о заявках сохранены' : 'Настройки почтового клиента сохранены');
      queryClient.setQueryData(['email-notification-settings'], value);
      setForm(current => ({ ...current, smtpPassword: '', clearSmtpPassword: false, imapPassword: '', clearImapPassword: false }));
    },
    onError: reason => setError(reason instanceof Error ? reason.message : mode === 'notifications' ? 'Не удалось сохранить уведомления' : 'Не удалось сохранить почтовый клиент'),
  });
  const test = useMutation({
    mutationFn: async () => {
      const saved = await api<EmailNotificationSettings>('/integrations/email-notifications', { method: 'PUT', body: JSON.stringify(form) });
      queryClient.setQueryData(['email-notification-settings'], saved);
      return api<{ success: boolean; message: string }>('/integrations/email-notifications/test', { method: 'POST', body: JSON.stringify({ recipient: testRecipient || undefined }) });
    },
    onSuccess: value => { setError(''); setFeedback(value.message); setForm(current => ({ ...current, smtpPassword: '', clearSmtpPassword: false, imapPassword: '', clearImapPassword: false })); queryClient.invalidateQueries({ queryKey: ['email-notification-settings'] }); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Проверка SMTP не пройдена'),
  });
  const retry = useMutation({
    mutationFn: (id: string) => api(`/integrations/email-notifications/deliveries/${id}/retry`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['email-notification-deliveries'] }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api(`/integrations/email-notifications/deliveries/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['email-notification-deliveries'] }),
  });
  const repair = useMutation({
    mutationFn: () => api<{ success: boolean; inserted: number; requeued: number; cancelled: number }>('/integrations/email-notifications/repair?days=14', { method: 'POST' }),
    onSuccess: value => {
      setError('');
      setFeedback(`Очередь проверена: добавлено ${value.inserted}, возвращено в очередь ${value.requeued}, отменено ${value.cancelled}`);
      queryClient.invalidateQueries({ queryKey: ['email-notification-settings'] });
      queryClient.invalidateQueries({ queryKey: ['email-notification-deliveries'] });
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось восстановить очередь уведомлений'),
  });

  if (isLoading) return <PageSkeleton />;
  if (!data) return <div className="admin-feedback error">{queryError instanceof Error ? queryError.message : 'Не удалось загрузить почтовые настройки'}</div>;

  const isNotifications = mode === 'notifications';

  return <div className="admin-section-stack email-notification-admin">
    <section className="surface integration-header-card">
      <div><span className="integration-icon">{isNotifications ? <Bell /> : <Mail />}</span><div><h2>{isNotifications ? 'Уведомления о заявках' : 'Почтовый клиент'}</h2><p>{isNotifications ? 'SMTP-уведомления сотрудникам и автоматические письма клиентам по заявкам из почты' : 'IMAP-папки, входящие письма, поиск и создание заявок из писем'}</p></div></div>
      <span className={`ad-connection-badge ${(isNotifications ? data.enabled : data.imapEnabled) ? 'connected' : ''}`}>{(isNotifications ? data.enabled : data.imapEnabled) ? 'Активно' : 'Не подключено'}</span>
    </section>
    {feedback && <div className="admin-feedback success">{feedback}</div>}
    {error && <div className="admin-feedback error">{error}</div>}

    {isNotifications && <section className={`surface email-worker-card ${data.worker.status.toLowerCase()}`}>
      <div className="email-worker-main">
        <span className="email-worker-icon"><Activity /></span>
        <div>
          <h2>Служба отправки уведомлений</h2>
          <p>{emailWorkerStatusName(data.worker.status)} · версия {data.worker.version || 'не определена'}</p>
          <small>{data.worker.heartbeatAt ? `Последняя активность: ${formatDateTime(data.worker.heartbeatAt)}` : 'Служба ещё не передала состояние'}</small>
          {data.configurationError && <em>{data.configurationError}</em>}
          {!data.configurationError && data.worker.lastError && <em>{data.worker.lastError}</em>}
        </div>
        <strong>{emailWorkerStatusName(data.worker.status)}</strong>
      </div>
      <div className="email-worker-metrics">
        <span><b>{data.queue.pending}</b><small>в очереди</small></span>
        <span><b>{data.queue.retry}</b><small>на повторе</small></span>
        <span><b>{data.queue.failed}</b><small>с ошибкой</small></span>
        <span><b>{data.queue.sent24h}</b><small>отправлено за 24 ч</small></span>
      </div>
      <div className="email-worker-actions">
        <button className="button ghost" type="button" onClick={() => repair.mutate()} disabled={repair.isPending}><RefreshCw /> {repair.isPending ? 'Проверка…' : 'Проверить и восстановить очередь'}</button>
      </div>
    </section>}

    {isNotifications ? <>
      <section className="surface compact-form email-smtp-card">
        <div className="surface-head"><div><h2>Отправка уведомлений по заявкам</h2><p>Эти параметры используются для служебных уведомлений, а также для писем клиенту, когда заявка из входящего письма принята в работу или выполнена.</p></div></div>
        <label className="switch-setting"><span><b>Включить уведомления о заявках</b><small>Сотрудники получают письма по личным настройкам. Автор входящего письма получает отдельное уведомление о начале и завершении работ по своей заявке.</small></span><input type="checkbox" checked={form.enabled} onChange={event => setForm(value => ({ ...value, enabled: event.target.checked }))} /></label>
        <div className="form-grid three">
          <label>SMTP-сервер<input value={form.smtpHost} onChange={event => setForm(value => ({ ...value, smtpHost: event.target.value }))} placeholder="mail.example.ru" /></label>
          <label>Порт<input type="number" min={1} max={65535} value={form.smtpPort} onChange={event => setForm(value => ({ ...value, smtpPort: Number(event.target.value) }))} /></label>
          <label className="switch-setting compact"><span><b>SSL/TLS сразу</b><small>Обычно порт 465. Для STARTTLS 587 оставьте выключенным.</small></span><input type="checkbox" checked={form.smtpSecure} onChange={event => setForm(value => ({ ...value, smtpSecure: event.target.checked }))} /></label>
        </div>
        <div className="form-grid two">
          <label>Логин SMTP<input value={form.smtpUsername} onChange={event => setForm(value => ({ ...value, smtpUsername: event.target.value }))} autoComplete="username" /></label>
          <label>Пароль SMTP<input type="password" value={form.smtpPassword} onChange={event => setForm(value => ({ ...value, smtpPassword: event.target.value, clearSmtpPassword: false }))} placeholder={data.hasSmtpPassword ? 'Пароль сохранён. Введите новый только для замены.' : 'Пароль'} autoComplete="new-password" /></label>
        </div>
        <div className="form-grid two">
          <label className="switch-setting compact"><span><b>Проверять TLS-сертификат</b><small>Отключать только для внутреннего SMTP с самоподписанным сертификатом.</small></span><input type="checkbox" checked={form.smtpTlsRejectUnauthorized} onChange={event => setForm(value => ({ ...value, smtpTlsRejectUnauthorized: event.target.checked }))} /></label>
          {data.hasSmtpPassword && <label className="switch-setting compact"><span><b>Удалить сохранённый пароль</b><small>Применится после сохранения.</small></span><input type="checkbox" checked={form.clearSmtpPassword} onChange={event => setForm(value => ({ ...value, clearSmtpPassword: event.target.checked, smtpPassword: event.target.checked ? '' : value.smtpPassword }))} /></label>}
        </div>
        <div className="form-grid three">
          <label>Имя отправителя<input value={form.fromName} onChange={event => setForm(value => ({ ...value, fromName: event.target.value }))} /></label>
          <label>Email отправителя<input type="email" value={form.fromEmail} onChange={event => setForm(value => ({ ...value, fromEmail: event.target.value }))} placeholder="helpdesk@example.ru" /></label>
          <label>Reply-To<input type="email" value={form.replyTo} onChange={event => setForm(value => ({ ...value, replyTo: event.target.value }))} placeholder="Необязательно" /></label>
        </div>
        <div className="integration-actions"><button className="button primary" type="button" onClick={() => save.mutate()} disabled={save.isPending}><Save /> {save.isPending ? 'Сохранение…' : 'Сохранить уведомления'}</button></div>
      </section>

      <section className="surface compact-form email-test-card">
        <div className="surface-head"><div><h2>Проверка уведомлений</h2><p>Портал проверит SMTP-авторизацию и отправит настоящее тестовое письмо.</p></div></div>
        <div className="email-test-line"><label>Email для теста<input type="email" value={testRecipient} onChange={event => setTestRecipient(event.target.value)} placeholder="Если пусто — email администратора" /></label><button className="button ghost" type="button" onClick={() => test.mutate()} disabled={test.isPending || !form.enabled}>{test.isPending ? 'Отправка…' : 'Отправить тестовое письмо'}</button></div>
        <p className={data.lastTestOk ? 'test-ok' : 'test-error'}>{data.lastTestAt ? `${formatDateTime(data.lastTestAt)} · ${data.lastTestMessage || 'Проверка выполнена'}` : 'Проверок ещё не было'}</p>
      </section>

      <section className="surface email-admin-history">
        <div className="surface-head"><div><h2>Журнал уведомлений о заявках</h2><p>Последние 200 автоматических писем, количество попыток и ответы SMTP</p></div></div>
        {!deliveries.length ? <EmptyState compact icon={<Bell />} title="Уведомлений пока нет" text="После событий заявок здесь появится журнал отправок." /> : <div className="email-admin-list">
          {deliveries.map(item => <div className="email-admin-row" key={item.id}>
            <span className={`email-delivery-icon ${item.status.toLowerCase()}`}><Mail /></span>
            <span><b>{item.title}</b><small>{item.user_full_name} · {item.recipient} · {formatDateTime(item.created_at)}</small>{item.last_error && <em>{item.last_error}</em>}</span>
            <span className="email-admin-attempts">Попыток: {item.attempts}</span>
            <strong className={`delivery-status ${item.status.toLowerCase()}`}>{emailDeliveryStatusName(item.status)}</strong>
            <div className="email-admin-actions">
              {['FAILED','CANCELLED','RETRY'].includes(item.status) && <button className="icon-button" title="Повторить отправку" onClick={() => retry.mutate(item.id)}><RefreshCw /></button>}
              {['PENDING','RETRY'].includes(item.status) && <button className="icon-button danger" title="Отменить" onClick={() => cancel.mutate(item.id)}><X /></button>}
            </div>
          </div>)}
        </div>}
      </section>
    </> : <>
      <section className="surface mail-outbound-summary">
        <span className={`mail-outbound-summary-icon ${data.enabled && data.hasSmtpPassword ? 'ready' : ''}`}><Send /></span>
        <div><h2>Исходящая почта</h2><p>Для написания и ответа на письма почтовый клиент использует общий SMTP, настроенный в отдельном разделе «Уведомления заявок».</p><small>{data.smtpHost ? `${data.smtpHost}:${data.smtpPort} · ${data.fromEmail || data.smtpUsername || 'отправитель не указан'}` : 'SMTP ещё не настроен'}</small></div>
        {onOpenNotifications && <button className="button ghost" type="button" onClick={onOpenNotifications}><Settings /> Настроить SMTP</button>}
      </section>

      <section className="surface compact-form email-imap-card">
        <div className="surface-head"><div><h2>Входящая почта и папки</h2><p>IMAP синхронизирует входящие, отправленные, архив, спам, корзину и остальные серверные папки.</p></div></div>
        <label className="switch-setting"><span><b>Включить встроенный почтовый клиент</b><small>Сотрудники смогут читать письма, отвечать на них и создавать связанные заявки.</small></span><input type="checkbox" checked={form.imapEnabled} onChange={event => setForm(value => ({ ...value, imapEnabled: event.target.checked }))} /></label>
        <div className="form-grid three"><label>IMAP-сервер<input value={form.imapHost} onChange={event => setForm(value => ({ ...value, imapHost: event.target.value }))} placeholder="mail.example.ru" /></label><label>Порт<input type="number" min={1} max={65535} value={form.imapPort} onChange={event => setForm(value => ({ ...value, imapPort: Number(event.target.value) }))} /></label><label className="switch-setting compact"><span><b>SSL/TLS сразу</b><small>Обычно порт 993.</small></span><input type="checkbox" checked={form.imapSecure} onChange={event => setForm(value => ({ ...value, imapSecure: event.target.checked }))} /></label></div>
        <div className="form-grid two"><label>Логин IMAP<input value={form.imapUsername} onChange={event => setForm(value => ({ ...value, imapUsername: event.target.value }))} autoComplete="username" /></label><label>Пароль IMAP<input type="password" value={form.imapPassword} onChange={event => setForm(value => ({ ...value, imapPassword: event.target.value, clearImapPassword: false }))} placeholder={data.hasImapPassword ? 'Пароль сохранён. Введите новый для замены.' : 'Пароль'} autoComplete="new-password" /></label></div>
        <div className="form-grid two"><label className="switch-setting compact"><span><b>Проверять TLS-сертификат IMAP</b><small>Отключать только для внутреннего сервера с самоподписанным сертификатом.</small></span><input type="checkbox" checked={form.imapTlsRejectUnauthorized} onChange={event => setForm(value => ({ ...value, imapTlsRejectUnauthorized: event.target.checked }))} /></label>{data.hasImapPassword && <label className="switch-setting compact"><span><b>Удалить пароль IMAP</b><small>Почтовый клиент перестанет подключаться после сохранения.</small></span><input type="checkbox" checked={form.clearImapPassword} onChange={event => setForm(value => ({ ...value, clearImapPassword: event.target.checked, imapPassword: event.target.checked ? '' : value.imapPassword }))} /></label>}</div>
        <div className="integration-actions"><button className="button primary" type="button" onClick={() => save.mutate()} disabled={save.isPending}><Save /> {save.isPending ? 'Сохранение…' : 'Сохранить почтовый клиент'}</button><button className="button ghost" type="button" disabled={!form.imapEnabled} onClick={async () => { try { await save.mutateAsync(); const result = await api<{synced:number}>('/mail/sync?folder=INBOX',{method:'POST'}); setFeedback(`IMAP подключён. Синхронизировано писем: ${result.synced}`); queryClient.invalidateQueries({queryKey:['email-notification-settings']}); } catch (reason) { setError(reason instanceof Error ? reason.message : 'Проверка IMAP не пройдена'); } }}><RefreshCw /> Проверить IMAP</button></div>
        <p className={data.imapLastTestOk ? 'test-ok' : 'test-error'}>{data.imapLastTestAt ? `${formatDateTime(data.imapLastTestAt)} · ${data.imapLastTestMessage || 'Проверка выполнена'}` : 'IMAP ещё не проверялся'}</p>
      </section>
    </>}
  </div>;
}

function MailClientAdmin() {
  const queryClient = useQueryClient();
  const { data, isLoading, error: queryError } = useQuery({
    queryKey: ['mail-client-settings'],
    queryFn: () => api<MailClientSettings>('/integrations/mail-client'),
  });
  const [form, setForm] = useState({
    smtpEnabled: false,
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: false,
    smtpTlsRejectUnauthorized: true,
    smtpUsername: '',
    smtpPassword: '',
    clearSmtpPassword: false,
    fromName: 'Intez Helpdesk',
    fromEmail: '',
    replyTo: '',
    imapEnabled: false,
    imapHost: '',
    imapPort: 993,
    imapSecure: true,
    imapTlsRejectUnauthorized: true,
    imapUsername: '',
    imapPassword: '',
    clearImapPassword: false,
  });
  const [testRecipient, setTestRecipient] = useState('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!data) return;
    setForm({
      smtpEnabled: data.smtpEnabled,
      smtpHost: data.smtpHost,
      smtpPort: data.smtpPort,
      smtpSecure: data.smtpSecure,
      smtpTlsRejectUnauthorized: data.smtpTlsRejectUnauthorized,
      smtpUsername: data.smtpUsername,
      smtpPassword: '',
      clearSmtpPassword: false,
      fromName: data.fromName,
      fromEmail: data.fromEmail,
      replyTo: data.replyTo,
      imapEnabled: data.imapEnabled,
      imapHost: data.imapHost,
      imapPort: data.imapPort,
      imapSecure: data.imapSecure,
      imapTlsRejectUnauthorized: data.imapTlsRejectUnauthorized,
      imapUsername: data.imapUsername,
      imapPassword: '',
      clearImapPassword: false,
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () => api<MailClientSettings>('/integrations/mail-client', { method: 'PUT', body: JSON.stringify(form) }),
    onSuccess: value => {
      setError('');
      setFeedback('Настройки отправки и получения обычной почты сохранены');
      queryClient.setQueryData(['mail-client-settings'], value);
      queryClient.invalidateQueries({ queryKey: ['mail-status'] });
      setForm(current => ({ ...current, smtpPassword: '', clearSmtpPassword: false, imapPassword: '', clearImapPassword: false }));
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить почтовый клиент'),
  });

  const testSmtp = useMutation({
    mutationFn: async () => {
      const saved = await api<MailClientSettings>('/integrations/mail-client', { method: 'PUT', body: JSON.stringify(form) });
      queryClient.setQueryData(['mail-client-settings'], saved);
      return api<{ success: boolean; message: string }>('/integrations/mail-client/test-smtp', { method: 'POST', body: JSON.stringify({ recipient: testRecipient || undefined }) });
    },
    onSuccess: value => {
      setError('');
      setFeedback(value.message);
      setForm(current => ({ ...current, smtpPassword: '', clearSmtpPassword: false, imapPassword: '', clearImapPassword: false }));
      queryClient.invalidateQueries({ queryKey: ['mail-client-settings'] });
      queryClient.invalidateQueries({ queryKey: ['mail-status'] });
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Проверка исходящей почты не пройдена'),
  });

  const testImap = useMutation({
    mutationFn: async () => {
      const saved = await api<MailClientSettings>('/integrations/mail-client', { method: 'PUT', body: JSON.stringify(form) });
      queryClient.setQueryData(['mail-client-settings'], saved);
      return api<{ synced: number; foldersSynced: number }>('/mail/sync?folder=INBOX&all=false', { method: 'POST' });
    },
    onSuccess: value => {
      setError('');
      setFeedback(`IMAP подключён. Синхронизировано писем: ${value.synced}`);
      setForm(current => ({ ...current, smtpPassword: '', clearSmtpPassword: false, imapPassword: '', clearImapPassword: false }));
      queryClient.invalidateQueries({ queryKey: ['mail-client-settings'] });
      queryClient.invalidateQueries({ queryKey: ['mail-status'] });
      queryClient.invalidateQueries({ queryKey: ['mail-folders'] });
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Проверка входящей почты не пройдена'),
  });

  if (isLoading) return <PageSkeleton />;
  if (!data) return <div className="admin-feedback error">{queryError instanceof Error ? queryError.message : 'Не удалось загрузить настройки почтового клиента'}</div>;

  return <div className="admin-section-stack email-notification-admin">
    <section className="surface integration-header-card">
      <div><span className="integration-icon"><Mail /></span><div><h2>Почта</h2><p>Отдельная учётная запись для написания, ответа и получения обычных писем</p></div></div>
      <span className={`ad-connection-badge ${(data.smtpEnabled || data.imapEnabled) ? 'connected' : ''}`}>{(data.smtpEnabled || data.imapEnabled) ? 'Настроено' : 'Не подключено'}</span>
    </section>

    <div className="admin-feedback mail-settings-separation-note">
      <b>Настройки разделены.</b> Адрес ниже используется только в разделе «Почта». Автоматические уведомления по заявкам продолжают отправляться с адреса, указанного во вкладке «Уведомления заявок».
    </div>
    {feedback && <div className="admin-feedback success">{feedback}</div>}
    {error && <div className="admin-feedback error">{error}</div>}

    <section className="surface compact-form email-smtp-card">
      <div className="surface-head"><div><h2>Исходящая почта</h2><p>Эти реквизиты используются при нажатии «Написать» и «Ответить». Например, здесь можно указать help@intezgroup.ru.</p></div></div>
      <label className="switch-setting"><span><b>Разрешить отправку писем из портала</b><small>Не влияет на автоматические уведомления о заявках.</small></span><input type="checkbox" checked={form.smtpEnabled} onChange={event => setForm(value => ({ ...value, smtpEnabled: event.target.checked }))} /></label>
      <div className="form-grid three">
        <label>SMTP-сервер<input value={form.smtpHost} onChange={event => setForm(value => ({ ...value, smtpHost: event.target.value }))} placeholder="mail.intezgroup.ru" /></label>
        <label>Порт<input type="number" min={1} max={65535} value={form.smtpPort} onChange={event => setForm(value => ({ ...value, smtpPort: Number(event.target.value) }))} /></label>
        <label className="switch-setting compact"><span><b>SSL/TLS сразу</b><small>Обычно порт 465. Для STARTTLS 587 оставьте выключенным.</small></span><input type="checkbox" checked={form.smtpSecure} onChange={event => setForm(value => ({ ...value, smtpSecure: event.target.checked }))} /></label>
      </div>
      <div className="form-grid two">
        <label>Логин SMTP<input value={form.smtpUsername} onChange={event => setForm(value => ({ ...value, smtpUsername: event.target.value }))} placeholder="help@intezgroup.ru" autoComplete="username" /></label>
        <label>Пароль SMTP<input type="password" value={form.smtpPassword} onChange={event => setForm(value => ({ ...value, smtpPassword: event.target.value, clearSmtpPassword: false }))} placeholder={data.hasSmtpPassword ? 'Пароль сохранён. Введите новый только для замены.' : 'Пароль'} autoComplete="new-password" /></label>
      </div>
      <div className="form-grid two">
        <label className="switch-setting compact"><span><b>Проверять TLS-сертификат</b><small>Отключать только для внутреннего сервера с самоподписанным сертификатом.</small></span><input type="checkbox" checked={form.smtpTlsRejectUnauthorized} onChange={event => setForm(value => ({ ...value, smtpTlsRejectUnauthorized: event.target.checked }))} /></label>
        {data.hasSmtpPassword && <label className="switch-setting compact"><span><b>Удалить сохранённый пароль SMTP</b><small>Исходящая почта перестанет работать после сохранения.</small></span><input type="checkbox" checked={form.clearSmtpPassword} onChange={event => setForm(value => ({ ...value, clearSmtpPassword: event.target.checked, smtpPassword: event.target.checked ? '' : value.smtpPassword }))} /></label>}
      </div>
      <div className="form-grid three">
        <label>Имя отправителя<input value={form.fromName} onChange={event => setForm(value => ({ ...value, fromName: event.target.value }))} placeholder="Intez Help" /></label>
        <label>Email отправителя<input type="email" value={form.fromEmail} onChange={event => setForm(value => ({ ...value, fromEmail: event.target.value }))} placeholder="help@intezgroup.ru" /></label>
        <label>Reply-To<input type="email" value={form.replyTo} onChange={event => setForm(value => ({ ...value, replyTo: event.target.value }))} placeholder="Необязательно" /></label>
      </div>
      <div className="email-test-line"><label>Email для проверки<input type="email" value={testRecipient} onChange={event => setTestRecipient(event.target.value)} placeholder="Если пусто — email администратора" /></label><button className="button ghost" type="button" onClick={() => testSmtp.mutate()} disabled={!form.smtpEnabled || testSmtp.isPending}><Send /> {testSmtp.isPending ? 'Проверка…' : 'Проверить отправку'}</button></div>
      <p className={data.smtpLastTestOk ? 'test-ok' : 'test-error'}>{data.smtpLastTestAt ? `${formatDateTime(data.smtpLastTestAt)} · ${data.smtpLastTestMessage || 'Проверка выполнена'}` : 'SMTP почтового клиента ещё не проверялся'}</p>
    </section>

    <section className="surface compact-form email-imap-card">
      <div className="surface-head"><div><h2>Входящая почта и папки</h2><p>IMAP синхронизирует входящие, отправленные, черновики, архив, спам, корзину и пользовательские папки.</p></div></div>
      <label className="switch-setting"><span><b>Включить получение почты</b><small>Обычно используется та же учётная запись, что и для исходящей почты, например help@intezgroup.ru.</small></span><input type="checkbox" checked={form.imapEnabled} onChange={event => setForm(value => ({ ...value, imapEnabled: event.target.checked }))} /></label>
      <div className="form-grid three">
        <label>IMAP-сервер<input value={form.imapHost} onChange={event => setForm(value => ({ ...value, imapHost: event.target.value }))} placeholder="mail.intezgroup.ru" /></label>
        <label>Порт<input type="number" min={1} max={65535} value={form.imapPort} onChange={event => setForm(value => ({ ...value, imapPort: Number(event.target.value) }))} /></label>
        <label className="switch-setting compact"><span><b>SSL/TLS сразу</b><small>Обычно порт 993.</small></span><input type="checkbox" checked={form.imapSecure} onChange={event => setForm(value => ({ ...value, imapSecure: event.target.checked }))} /></label>
      </div>
      <div className="form-grid two">
        <label>Логин IMAP<input value={form.imapUsername} onChange={event => setForm(value => ({ ...value, imapUsername: event.target.value }))} placeholder="help@intezgroup.ru" autoComplete="username" /></label>
        <label>Пароль IMAP<input type="password" value={form.imapPassword} onChange={event => setForm(value => ({ ...value, imapPassword: event.target.value, clearImapPassword: false }))} placeholder={data.hasImapPassword ? 'Пароль сохранён. Введите новый только для замены.' : 'Пароль'} autoComplete="new-password" /></label>
      </div>
      <div className="form-grid two">
        <label className="switch-setting compact"><span><b>Проверять TLS-сертификат IMAP</b><small>Отключать только для внутреннего сервера с самоподписанным сертификатом.</small></span><input type="checkbox" checked={form.imapTlsRejectUnauthorized} onChange={event => setForm(value => ({ ...value, imapTlsRejectUnauthorized: event.target.checked }))} /></label>
        {data.hasImapPassword && <label className="switch-setting compact"><span><b>Удалить сохранённый пароль IMAP</b><small>Получение писем перестанет работать после сохранения.</small></span><input type="checkbox" checked={form.clearImapPassword} onChange={event => setForm(value => ({ ...value, clearImapPassword: event.target.checked, imapPassword: event.target.checked ? '' : value.imapPassword }))} /></label>}
      </div>
      <div className="integration-actions"><button className="button primary" type="button" onClick={() => save.mutate()} disabled={save.isPending}><Save /> {save.isPending ? 'Сохранение…' : 'Сохранить почту'}</button><button className="button ghost" type="button" onClick={() => testImap.mutate()} disabled={!form.imapEnabled || testImap.isPending}><RefreshCw /> {testImap.isPending ? 'Проверка…' : 'Проверить IMAP'}</button></div>
      <p className={data.imapLastTestOk ? 'test-ok' : 'test-error'}>{data.imapLastTestAt ? `${formatDateTime(data.imapLastTestAt)} · ${data.imapLastTestMessage || 'Проверка выполнена'}` : 'IMAP почтового клиента ещё не проверялся'}</p>
    </section>
  </div>;
}

function Admin() {
  const [tab, setTab] = useState<'users' | 'queues' | 'telephony' | 'security' | 'integrations' | 'backups'>('users');
  return <div className="admin-page-v2">
    <PageHeading eyebrow="СИСТЕМА" title="Администрирование" description="Пользователи, очереди, телефония, безопасность, резервные копии и интеграции" />
    <div className="admin-tabs">
      <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}><Users /> Пользователи</button>
      <button className={tab === 'queues' ? 'active' : ''} onClick={() => setTab('queues')}><Inbox /> Очереди</button>
      <button className={tab === 'telephony' ? 'active' : ''} onClick={() => setTab('telephony')}><Phone /> Телефония</button>
      <button className={tab === 'security' ? 'active' : ''} onClick={() => setTab('security')}><ShieldCheck /> Безопасность</button>
      <button className={tab === 'integrations' ? 'active' : ''} onClick={() => setTab('integrations')}><Zap /> Интеграции</button>
      <button className={tab === 'backups' ? 'active' : ''} onClick={() => setTab('backups')}><HardDrive /> Резервные копии</button>
    </div>
    {tab === 'users' && <UsersAdmin />}
    {tab === 'queues' && <QueuesAdmin />}
    {tab === 'telephony' && <TelephonyAdministration />}
    {tab === 'security' && <SecurityAdmin />}
    {tab === 'integrations' && <IntegrationsAdmin />}
    {tab === 'backups' && <BackupAdminPanel />}
  </div>;
}

function UsersAdmin() {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const { data: users = [], isLoading } = useQuery({ queryKey: ['users'], queryFn: () => api<UserRow[]>('/users') });
  const { data: companies = [] } = useQuery({ queryKey: ['companies'], queryFn: () => api<Company[]>('/companies') });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['users'] });
  const editing = users.find(item => item.id === editingId) ?? null;

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api('/users', { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: () => { setError(''); setMessage('Локальный пользователь создан'); refresh(); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось создать пользователя'),
  });
  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) => api(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
    onSuccess: () => { setError(''); setMessage('Данные, роль и права пользователя сохранены'); setEditingId(null); refresh(); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить пользователя'),
  });
  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => api(`/users/${id}/status`, { method: 'PATCH', body: JSON.stringify({ isActive }) }),
    onSuccess: () => { setError(''); setMessage('Статус пользователя изменён'); refresh(); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось изменить статус'),
  });
  const resetPassword = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => api(`/users/${id}/reset-password`, { method: 'POST', body: JSON.stringify({ password }) }),
    onSuccess: () => { setError(''); setMessage('Локальный пароль установлен'); refresh(); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось изменить пароль'),
  });
  const removeLocalPassword = useMutation({
    mutationFn: (id: string) => api(`/users/${id}/local-password`, { method: 'DELETE' }),
    onSuccess: () => { setError(''); setMessage('Локальный пароль удалён, оставлен вход через Active Directory'); refresh(); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось удалить локальный пароль'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => { setError(''); setMessage('Пользователь удалён из портала'); setEditingId(null); refresh(); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось удалить пользователя'),
  });

  function submitCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage('');
    setError('');
    const form = new FormData(event.currentTarget);
    const userType = String(form.get('userType'));
    create.mutate({
      fullName: form.get('fullName'),
      email: form.get('email'),
      loginName: userType === 'EMPLOYEE' ? form.get('loginName') || undefined : undefined,
      password: form.get('password'),
      phone: form.get('phone') || undefined,
      userType,
      roles: userType === 'CLIENT' ? ['CLIENT'] : form.getAll('roles').map(String),
      companyId: userType === 'CLIENT' ? form.get('companyId') : undefined,
    });
  }

  function submitEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editing) return;
    setMessage('');
    setError('');
    const form = new FormData(event.currentTarget);
    update.mutate({
      id: editing.id,
      payload: {
        fullName: form.get('fullName'),
        email: editing.auth_source === 'LOCAL' ? form.get('email') : undefined,
        loginName: editing.user_type === 'EMPLOYEE' ? form.get('loginName') : undefined,
        phone: form.get('phone') || '',
        roles: editing.user_type === 'CLIENT' ? ['CLIENT'] : form.getAll('roles').map(String),
        companyId: editing.user_type === 'CLIENT' ? form.get('companyId') : undefined,
        directorySyncEnabled: form.get('directorySyncEnabled') === 'on',
      },
    });
  }

  function askPassword(item: UserRow) {
    const password = window.prompt(`Новый локальный пароль для ${item.full_name}. Не менее 12 символов:`);
    if (!password) return;
    if (password.length < 12) { setError('Пароль должен содержать не менее 12 символов'); return; }
    resetPassword.mutate({ id: item.id, password });
  }

  function askRemove(item: UserRow) {
    if (!window.confirm(`Удалить пользователя «${item.full_name}» из портала? История заявок сохранится.`)) return;
    remove.mutate(item.id);
  }

  const normalizedSearch = search.trim().toLowerCase();
  const filteredUsers = users.filter(item => {
    const matchesSearch = !normalizedSearch || [item.full_name, item.email, item.login_name, item.company_name].some(value => value?.toLowerCase().includes(normalizedSearch));
    const matchesSource = !sourceFilter || item.auth_source === sourceFilter || (sourceFilter === 'DIRECTORY' && ['ACTIVE_DIRECTORY', 'HYBRID'].includes(item.auth_source));
    const matchesStatus = !statusFilter || (statusFilter === 'ACTIVE' ? item.is_active : !item.is_active);
    return matchesSearch && matchesSource && matchesStatus;
  });

  const active = users.filter(item => item.is_active).length;
  const local = users.filter(item => item.auth_source === 'LOCAL' || item.auth_source === 'HYBRID').length;
  const directoryUsers = users.filter(item => item.auth_source === 'ACTIVE_DIRECTORY' || item.auth_source === 'HYBRID').length;
  return <div className="admin-section-stack">
    <div className="admin-stat-grid">
      <MetricCard icon={<Users />} label="Всего пользователей" value={users.length} note={`${active} активных`} tone="blue" />
      <MetricCard icon={<KeyRound />} label="Локальные учётные записи" value={local} note="Вход по email и паролю" tone="green" />
      <MetricCard icon={<Network />} label="Active Directory" value={directoryUsers} note="Вход по короткому доменному логину" tone="red" />
    </div>
    {(message || error) && <div className={`admin-feedback ${error ? 'error' : 'success'}`}>{error || message}</div>}
    <div className="user-admin-filters surface">
      <div className="search-field"><Search /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Поиск по ФИО, логину, email или компании" /></div>
      <select value={sourceFilter} onChange={event => setSourceFilter(event.target.value)}><option value="">Все источники</option><option value="LOCAL">Локальные</option><option value="DIRECTORY">Active Directory</option></select>
      <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}><option value="">Все статусы</option><option value="ACTIVE">Активные</option><option value="DISABLED">Отключённые</option></select>
      <button className="soft-button" type="button" onClick={() => setEditingId(null)}><UserPlus /> Новый пользователь</button>
    </div>
    <div className="management-layout wide-form admin-users-layout">
      <section className="surface">
        <div className="surface-head"><div><h2>Пользователи</h2><p>Показано {filteredUsers.length} из {users.length}. Выбранные роли вместе определяют права и виджеты.</p></div><button className="icon-button" onClick={() => refresh()} title="Обновить"><RefreshCw /></button></div>
        {isLoading ? <TableSkeleton /> : <div className="user-list admin-user-list">
          {filteredUsers.map(item => <div className={`user-row admin-user-row ${editingId === item.id ? 'selected' : ''}`} key={item.id}>
            <span className="avatar light">{initials(item.full_name)}</span>
            <div className="user-main"><b>{item.full_name}</b><small>{item.login_name ? `${item.login_name} · ` : ''}{item.email}</small></div>
            <div><small>Источник</small><b>{authSourceName(item.auth_source)}</b></div>
            <div><small>Роли и права</small><b>{(item.roles?.length ? item.roles : [item.role]).map(roleName).join(' · ')}</b></div>
            <div><small>Последний вход</small><b>{formatShort(item.last_login_at)}</b></div>
            <StatusDot active={item.is_active} />
            <div className="user-actions">
              <button className="soft-button compact" type="button" onClick={() => setEditingId(item.id)}><Settings /> Изменить</button>
              <button className="soft-button compact" type="button" onClick={() => askPassword(item)}><KeyRound /> {item.has_local_password ? 'Пароль' : 'Добавить пароль'}</button>
              <button className={`soft-button compact ${item.is_active ? 'danger' : ''}`} type="button" disabled={item.id === currentUser?.id && item.is_active} onClick={() => setStatus.mutate({ id: item.id, isActive: !item.is_active })}>{item.is_active ? 'Отключить' : 'Включить'}</button>
            </div>
          </div>)}
          {!filteredUsers.length && <EmptyState compact icon={<Users />} title="Пользователи не найдены" text="Измените параметры поиска или фильтры." />}
        </div>}
      </section>

      {editing ? <form className="surface compact-form user-editor" onSubmit={submitEdit} key={editing.id}>
        <div className="editor-title"><div><h2>Редактирование</h2><p className="form-description">{authSourceName(editing.auth_source)}</p></div><button type="button" className="icon-button" onClick={() => setEditingId(null)}><X /></button></div>
        <label>ФИО<input name="fullName" defaultValue={editing.full_name} required /></label>
        <label>Email<input name="email" type="email" defaultValue={editing.email} disabled={editing.auth_source !== 'LOCAL'} required /></label>
        {editing.user_type === 'EMPLOYEE' && <label>Логин<input name="loginName" defaultValue={editing.login_name ?? ''} placeholder="Например, aas" /></label>}
        <label>Телефон<input name="phone" type="tel" defaultValue={editing.phone ?? ''} /></label>
        <label>Тип<input value={editing.user_type === 'CLIENT' ? 'Клиент' : 'Сотрудник'} disabled /></label>
        {editing.user_type === 'EMPLOYEE' && <UserRolesEditor defaultRoles={editing.roles?.length ? editing.roles : [editing.role]} />}
        {editing.user_type === 'CLIENT' && <label>Компания клиента<select name="companyId" defaultValue={editing.company_id ?? ''} required><option value="">Не выбрана</option>{companies.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>}
        {editing.auth_source !== 'LOCAL' && <label className="checkbox-row"><input type="checkbox" name="directorySyncEnabled" defaultChecked={editing.directory_sync_enabled} /><span>Обновлять ФИО и телефон из Active Directory</span></label>}
        <button className="button primary wide" disabled={update.isPending}><Save /> {update.isPending ? 'Сохранение…' : 'Сохранить изменения'}</button>
        {editing.auth_source === 'HYBRID' && <button className="soft-button wide" type="button" onClick={() => removeLocalPassword.mutate(editing.id)}><KeyRound /> Оставить только вход через AD</button>}
        <button className="soft-button danger wide" type="button" disabled={editing.id === currentUser?.id || remove.isPending} onClick={() => askRemove(editing)}><Trash2 /> Удалить из портала</button>
      </form> : <form className="surface compact-form" onSubmit={submitCreate}>
        <h2>Новый локальный пользователь</h2>
        <p className="form-description">Клиенты входят по email, локальные сотрудники — по логину или email.</p>
        <label>ФИО<input name="fullName" required /></label>
        <label>Email<input name="email" type="email" required /></label>
        <label>Логин сотрудника<input name="loginName" placeholder="Заполнится из email автоматически" /></label>
        <label>Телефон<input name="phone" type="tel" /></label>
        <label>Временный пароль<input name="password" type="password" minLength={12} required /></label>
        <label>Тип<select name="userType" defaultValue="EMPLOYEE"><option value="EMPLOYEE">Сотрудник</option><option value="CLIENT">Клиент</option></select></label>
        <UserRolesEditor defaultRoles={['EXECUTOR']} />
        <label>Компания клиента<select name="companyId"><option value="">Не выбрана</option>{companies.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <button className="button primary wide" disabled={create.isPending}><UserPlus /> {create.isPending ? 'Создание…' : 'Создать пользователя'}</button>
      </form>}
    </div>
  </div>;
}

function SecurityAdmin() {
  const queryClient = useQueryClient();
  const { data, isLoading, error: queryError } = useQuery({
    queryKey: ['security-overview'],
    queryFn: () => api<SecurityOverview>('/security/overview'),
    refetchInterval: 30_000,
  });
  const [form, setForm] = useState({ enabled: true, maxRetry: 5, findTimeSeconds: 600, banTimeSeconds: 3600, trustedIps: '127.0.0.1/8\n::1', notifyAdmins: true });
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!data) return;
    setForm({
      enabled: data.settings.enabled,
      maxRetry: data.settings.max_retry,
      findTimeSeconds: data.settings.find_time_seconds,
      banTimeSeconds: data.settings.ban_time_seconds,
      trustedIps: data.settings.trusted_ips.join('\n'),
      notifyAdmins: data.settings.notify_admins,
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () => api('/security/settings', {
      method: 'PUT',
      body: JSON.stringify({ ...form, trustedIps: form.trustedIps.split(/[\n,]+/).map(value => value.trim()).filter(Boolean) }),
    }),
    onSuccess: () => { setError(''); setFeedback('Настройки Fail2ban применены'); queryClient.invalidateQueries({ queryKey: ['security-overview'] }); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить настройки'),
  });
  const ban = useMutation({
    mutationFn: (payload: { ip: string; reason?: string }) => api('/security/ban', { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: () => { setError(''); setFeedback('IP-адрес заблокирован'); queryClient.invalidateQueries({ queryKey: ['security-overview'] }); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось заблокировать IP'),
  });
  const unban = useMutation({
    mutationFn: (ip: string) => api('/security/unban', { method: 'POST', body: JSON.stringify({ ip, reason: 'Разблокировано в веб-интерфейсе' }) }),
    onSuccess: () => { setError(''); setFeedback('IP-адрес разблокирован'); queryClient.invalidateQueries({ queryKey: ['security-overview'] }); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось разблокировать IP'),
  });

  function submitBan(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    ban.mutate({ ip: String(values.get('ip') ?? ''), reason: String(values.get('reason') ?? '') });
    event.currentTarget.reset();
  }

  if (isLoading) return <PageSkeleton />;
  if (!data) return <div className="admin-feedback error">{queryError instanceof Error ? queryError.message : 'Не удалось получить состояние безопасности'}</div>;

  return <div className="admin-section-stack">
    {(feedback || error) && <div className={`admin-feedback ${error ? 'error' : 'success'}`}>{error || feedback}</div>}
    <div className="admin-stat-grid security-stats">
      <MetricCard icon={<ShieldCheck />} label="Fail2ban" value={data.status.running && data.status.jailEnabled ? 'Активен' : 'Недоступен'} note={data.status.message || 'Состояние агента'} tone={data.status.running ? 'green' : 'red'} />
      <MetricCard icon={<Ban />} label="Заблокировано сейчас" value={data.status.currentBanned} note={`${data.status.totalBanned} всего за время работы jail`} tone="red" />
      <MetricCard icon={<AlertTriangle />} label="Ошибок входа за 24 ч" value={data.stats.failed_24h} note={`${data.stats.unique_ips_24h} уникальных IP`} tone="blue" />
    </div>

    <div className="security-layout">
      <section className="surface security-banned-panel">
        <div className="surface-head"><div><h2>Заблокированные IP-адреса</h2><p>Текущее состояние jail «intez-helpdesk»</p></div><button className="icon-button" onClick={() => queryClient.invalidateQueries({ queryKey: ['security-overview'] })}><RefreshCw /></button></div>
        {data.status.bannedIps.length ? <div className="security-ip-list">
          {data.status.bannedIps.map(ip => <div className="security-ip-row" key={ip}><div><b>{ip}</b><small>Блокировка активна на уровне сервера</small></div><button className="soft-button" onClick={() => unban.mutate(ip)} disabled={unban.isPending}><Unlock /> Разблокировать</button></div>)}
        </div> : <EmptyState compact icon={<ShieldCheck />} title="Активных блокировок нет" text="Новые блокировки появятся здесь автоматически." />}
      </section>

      <form className="surface compact-form security-manual-ban" onSubmit={submitBan}>
        <h2>Ручная блокировка</h2>
        <label>IP-адрес<input name="ip" placeholder="203.0.113.25" required /></label>
        <label>Причина<textarea name="reason" rows={3} placeholder="Подозрительная активность" /></label>
        <button className="button primary wide" disabled={ban.isPending}><Ban /> Заблокировать</button>
      </form>
    </div>

    <section className="surface security-settings-panel">
      <div className="surface-head"><div><h2>Параметры Fail2ban</h2><p>Защита формы входа от перебора паролей</p></div></div>
      <div className="security-settings-grid">
        <label className="switch-setting"><span><b>Защита включена</b><small>Отслеживать неудачные попытки входа</small></span><input type="checkbox" checked={form.enabled} onChange={event => setForm(value => ({ ...value, enabled: event.target.checked }))} /></label>
        <label>Максимум попыток<input type="number" min={2} max={100} value={form.maxRetry} onChange={event => setForm(value => ({ ...value, maxRetry: Number(event.target.value) }))} /></label>
        <label>Окно анализа, секунд<input type="number" min={60} value={form.findTimeSeconds} onChange={event => setForm(value => ({ ...value, findTimeSeconds: Number(event.target.value) }))} /></label>
        <label>Время блокировки, секунд<input type="number" min={60} value={form.banTimeSeconds} onChange={event => setForm(value => ({ ...value, banTimeSeconds: Number(event.target.value) }))} /></label>
        <label className="trusted-ips-field">Доверенные IP и подсети<textarea rows={4} value={form.trustedIps} onChange={event => setForm(value => ({ ...value, trustedIps: event.target.value }))} placeholder={'127.0.0.1/8\n192.168.1.0/24'} /></label>
        <label className="switch-setting"><span><b>Уведомлять администраторов</b><small>Создавать уведомление при новой блокировке</small></span><input type="checkbox" checked={form.notifyAdmins} onChange={event => setForm(value => ({ ...value, notifyAdmins: event.target.checked }))} /></label>
      </div>
      <div className="settings-actions"><button className="button primary" type="button" onClick={() => save.mutate()} disabled={save.isPending}><Save /> {save.isPending ? 'Применение…' : 'Сохранить и применить'}</button></div>
    </section>

    <section className="surface security-history-panel">
      <div className="surface-head"><div><h2>История блокировок</h2><p>Автоматические и ручные действия</p></div></div>
      <div className="security-history-head"><span>IP-адрес</span><span>Действие</span><span>Причина</span><span>Источник</span><span>Время</span></div>
      {data.events.slice(0, 20).map(item => <div className="security-history-row" key={item.id}><b>{item.ip_address}</b><span className={`event-action ${item.action.toLowerCase()}`}>{item.action === 'BAN' ? 'Блокировка' : 'Разблокировка'}</span><span>{item.reason || '—'}</span><span>{item.actor_name || item.source}</span><span>{formatDateTime(item.created_at)}</span></div>)}
    </section>
  </div>;
}


function IntegrationsAdmin() {
  const [tab, setTab] = useState<'ad' | 'bitrix24' | 'max' | 'telegram' | 'notifications' | 'mail'>('ad');
  return <div className="admin-section-stack">
    <div className="integration-switcher">
      <button className={tab === 'ad' ? 'active' : ''} onClick={() => setTab('ad')}><Network /> Active Directory</button>
      <button className={tab === 'bitrix24' ? 'active' : ''} onClick={() => setTab('bitrix24')}><Building2 /> Битрикс24</button>
      <button className={tab === 'max' ? 'active' : ''} onClick={() => setTab('max')}><MessageSquare /> MAX</button>
      <button className={tab === 'telegram' ? 'active' : ''} onClick={() => setTab('telegram')}><Send /> Telegram</button>
      <button className={tab === 'notifications' ? 'active' : ''} onClick={() => setTab('notifications')}><Bell /> Уведомления заявок</button>
      <button className={tab === 'mail' ? 'active' : ''} onClick={() => setTab('mail')}><Mail /> Почта</button>
    </div>
    {tab === 'ad' ? <ActiveDirectoryAdmin /> : tab === 'bitrix24' ? <Bitrix24Administration /> : tab === 'max' ? <MaxIntegrationAdmin /> : tab === 'telegram' ? <TelegramIntegrationAdmin /> : tab === 'notifications' ? <EmailNotificationAdmin mode="notifications" /> : <MailClientAdmin />}
  </div>;
}

function MaxIntegrationAdmin() {
  const queryClient = useQueryClient();
  const { data, isLoading, error: queryError } = useQuery({ queryKey: ['max-settings'], queryFn: () => api<MaxIntegrationSettings>('/integrations/max') });
  const [form, setForm] = useState({ botEnabled: false, botName: 'MAX Bot', botToken: '' });
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!data) return;
    setForm({
      botEnabled: data.botEnabled,
      botName: data.botName || 'MAX Bot',
      botToken: '',
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () => api<MaxIntegrationSettings>('/integrations/max', { method: 'PUT', body: JSON.stringify(form) }),
    onSuccess: () => { setError(''); setFeedback('Настройки MAX Bot сохранены'); queryClient.invalidateQueries({ queryKey: ['max-settings'] }); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить MAX Bot'),
  });
  const rotate = useMutation({
    mutationFn: () => api<MaxIntegrationSettings>('/integrations/max/rotate-webhook-secret', { method: 'POST' }),
    onSuccess: () => { setError(''); setFeedback('Секрет MAX обновлён'); queryClient.invalidateQueries({ queryKey: ['max-settings'] }); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось обновить секрет'),
  });
  const enablePolling = useMutation({
    mutationFn: () => api<MaxIntegrationSettings>('/integrations/max/configure-webhook', { method: 'POST', body: JSON.stringify({ origin: window.location.origin }) }),
    onSuccess: () => { setError(''); setFeedback('Long Polling для MAX Bot включён'); queryClient.invalidateQueries({ queryKey: ['max-settings'] }); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось включить приём сообщений MAX'),
  });

  if (isLoading) return <PageSkeleton />;
  if (!data) return <div className="admin-feedback error">{queryError instanceof Error ? queryError.message : 'Не удалось загрузить MAX'}</div>;

  return <div className="admin-section-stack max-admin-page">
    <section className="surface integration-header-card">
      <div><span className="integration-icon"><MessageSquare /></span><div><h2>MAX Bot</h2><p>Подключение официального бота MAX к контакт-центру</p></div></div>
      <span className={`ad-connection-badge ${data.botEnabled ? 'connected' : ''}`}>{data.botEnabled ? 'Активно' : 'Не подключено'}</span>
    </section>

    {feedback && <div className="admin-feedback success">{feedback}</div>}
    {error && <div className="admin-feedback error">{error}</div>}

    <section className="surface compact-form max-settings-card max-bot-only-card">
      <h2>Подключение MAX Bot</h2>
      <label className="switch-setting"><span><b>Включить канал MAX Bot</b><small>Входящие сообщения от бота будут попадать в «Контакт-центр» отдельными диалогами.</small></span><input type="checkbox" checked={form.botEnabled} onChange={event => setForm(value => ({ ...value, botEnabled: event.target.checked }))} /></label>
      <label>Название канала<input value={form.botName} onChange={event => setForm(value => ({ ...value, botName: event.target.value }))} placeholder="MAX Bot" /></label>
      <label>Токен бота<input type="password" value={form.botToken} onChange={event => setForm(value => ({ ...value, botToken: event.target.value }))} placeholder={data.hasBotToken ? 'Токен сохранён. Введите новый только для замены.' : 'Вставьте токен бота MAX'} /></label>
      <div className="info-note"><ShieldCheck /><span><b>Нужен только токен бота</b><small>Токен хранится зашифрованным. Сообщения забирает сервер портала через Long Polling, без входящего webhook и без отдельного MAX-коннектора.</small></span></div>
      <div className="integration-actions">
        <button className="button primary" type="button" onClick={() => save.mutate()} disabled={save.isPending}><Save /> {save.isPending ? 'Сохранение…' : 'Сохранить MAX Bot'}</button>
        <button className="button ghost" type="button" onClick={() => enablePolling.mutate()} disabled={enablePolling.isPending || !form.botEnabled || !data.hasBotToken}>{enablePolling.isPending ? 'Запуск…' : 'Запустить приём сообщений'}</button>
      </div>
    </section>

    <section className="surface max-webhook-card">
      <div className="surface-head"><div><h2>Приём сообщений</h2><p>Рабочий режим — Long Polling. Портал сам забирает события MAX Bot через scheduler, поэтому входящий webhook настраивать не нужно.</p></div><button className="button ghost" onClick={() => rotate.mutate()} disabled={rotate.isPending}><RefreshCw /> Новый внутренний секрет</button></div>
      <label>Режим<code>{data.deliveryMode === 'WEBHOOK' ? 'Webhook' : 'Long Polling'}</code></label>
      <label>Последняя проверка<code>{data.pollingLastAt ? formatDateTime(data.pollingLastAt) : 'Пока не было'}</code></label>
      <p>{data.lastTestMessage ? data.lastTestMessage : 'После сохранения токена нажмите «Запустить приём сообщений», затем напишите боту из MAX.'}{data.pollingLastError ? ` Ошибка: ${data.pollingLastError}` : ''}</p>
    </section>

    <section className="surface max-channel-status">
      <div className="surface-head"><div><h2>Состояние канала</h2><p>MAX Bot в контакт-центре</p></div></div>
      <div className="max-channel-grid">{data.channels.filter(channel => channel.connection_type === 'BOT').map(channel => <div key={channel.connection_type}><b>Бот</b><span className={`event-action ${channel.status === 'ACTIVE' ? 'unban' : 'ban'}`}>{channel.status === 'ACTIVE' ? 'Активен' : 'Отключён'}</span><small>{channel.last_event_at ? `Последнее сообщение: ${formatDateTime(channel.last_event_at)}` : 'Сообщений ещё не было'}{channel.last_error ? ` · Ошибка: ${channel.last_error}` : ''}</small></div>)}</div>
    </section>
  </div>;
}

function TelegramIntegrationAdmin() {
  const queryClient = useQueryClient();
  const { data, isLoading, error: queryError } = useQuery({ queryKey: ['telegram-settings'], queryFn: () => api<TelegramIntegrationSettings>('/integrations/telegram') });
  const [form, setForm] = useState({
    botEnabled: false,
    botName: 'Telegram Bot',
    botToken: '',
    proxyEnabled: true,
    proxyProtocol: 'SOCKS5' as 'HTTP' | 'HTTPS' | 'SOCKS5',
    proxyHost: '',
    proxyPort: 1080,
    proxyUsername: '',
    proxyPassword: '',
    clearProxyPassword: false,
    mtprotoEnabled: false,
    mtprotoApiId: 0,
    mtprotoApiHash: '',
    clearMtprotoApiHash: false,
  });
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!data) return;
    setForm({
      botEnabled: data.botEnabled,
      botName: data.botName || 'Telegram Bot',
      botToken: '',
      proxyEnabled: data.proxyEnabled,
      proxyProtocol: data.proxyProtocol || 'SOCKS5',
      proxyHost: data.proxyHost || '',
      proxyPort: data.proxyPort || (data.proxyProtocol === 'SOCKS5' ? 1080 : 8080),
      proxyUsername: data.proxyUsername || '',
      proxyPassword: '',
      clearProxyPassword: false,
      mtprotoEnabled: data.mtprotoEnabled,
      mtprotoApiId: data.mtprotoApiId || 0,
      mtprotoApiHash: '',
      clearMtprotoApiHash: false,
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () => api<TelegramIntegrationSettings>('/integrations/telegram', { method: 'PUT', body: JSON.stringify(form) }),
    onSuccess: () => {
      setError('');
      setFeedback('Настройки Telegram Bot и прокси сохранены');
      setForm(value => ({ ...value, botToken: '', proxyPassword: '', clearProxyPassword: false, mtprotoApiHash: '', clearMtprotoApiHash: false }));
      queryClient.invalidateQueries({ queryKey: ['telegram-settings'] });
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить Telegram Bot'),
  });
  const test = useMutation({
    mutationFn: () => api<TelegramIntegrationSettings>('/integrations/telegram/test', { method: 'POST' }),
    onSuccess: result => { setError(''); setFeedback(result.lastTestMessage || 'Подключение к Telegram успешно'); queryClient.invalidateQueries({ queryKey: ['telegram-settings'] }); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось проверить Telegram'),
  });
  const startPolling = useMutation({
    mutationFn: () => api<TelegramIntegrationSettings>('/integrations/telegram/start-polling', { method: 'POST' }),
    onSuccess: result => { setError(''); setFeedback(result.lastTestMessage || 'Приём сообщений Telegram включён'); queryClient.invalidateQueries({ queryKey: ['telegram-settings'] }); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось запустить приём сообщений Telegram'),
  });
  const rotate = useMutation({
    mutationFn: () => api<TelegramIntegrationSettings>('/integrations/telegram/rotate-webhook-secret', { method: 'POST' }),
    onSuccess: () => { setError(''); setFeedback('Внутренний секрет Telegram обновлён'); queryClient.invalidateQueries({ queryKey: ['telegram-settings'] }); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось обновить внутренний секрет'),
  });

  if (isLoading) return <PageSkeleton />;
  if (!data) return <div className="admin-feedback error">{queryError instanceof Error ? queryError.message : 'Не удалось загрузить Telegram'}</div>;

  return <div className="admin-section-stack telegram-admin-page">
    <section className="surface integration-header-card">
      <div><span className="integration-icon telegram"><Send /></span><div><h2>Telegram Bot</h2><p>Официальный Telegram Bot API, контакт-центр и отдельный прокси только для Telegram</p></div></div>
      <span className={`ad-connection-badge ${data.botEnabled ? 'connected' : ''}`}>{data.botEnabled ? 'Активно' : 'Не подключено'}</span>
    </section>

    {feedback && <div className="admin-feedback success">{feedback}</div>}
    {error && <div className="admin-feedback error">{error}</div>}

    <section className="surface compact-form max-settings-card telegram-settings-card">
      <h2>Подключение Telegram Bot</h2>
      <label className="switch-setting"><span><b>Включить канал Telegram Bot</b><small>Новые сообщения будут создавать отдельные диалоги и связанные заявки в Контакт-центре.</small></span><input type="checkbox" checked={form.botEnabled} onChange={event => setForm(value => ({ ...value, botEnabled: event.target.checked }))} /></label>
      <div className="telegram-settings-grid">
        <label>Название канала<input value={form.botName} onChange={event => setForm(value => ({ ...value, botName: event.target.value }))} placeholder="Telegram Bot" /></label>
        <label>Токен бота<input type="password" value={form.botToken} onChange={event => setForm(value => ({ ...value, botToken: event.target.value }))} placeholder={data.hasBotToken ? 'Токен сохранён. Введите новый только для замены.' : 'Вставьте токен от BotFather'} /></label>
      </div>
      <div className="info-note"><ShieldCheck /><span><b>Токен хранится зашифрованным</b><small>Портал получает сообщения через Long Polling. Входящий webhook и открытый порт для Telegram не требуются.</small></span></div>
    </section>

    <section className="surface compact-form telegram-proxy-card">
      <div className="surface-head"><div><h2>Прокси только для Telegram</h2><p>MAX, Active Directory и остальные запросы портала продолжают работать напрямую.</p></div><span className={`ad-connection-badge ${form.proxyEnabled ? 'connected' : ''}`}>{form.proxyEnabled ? 'Включён' : 'Выключен'}</span></div>
      <label className="switch-setting"><span><b>Использовать прокси</b><small>Через него выполняются getUpdates, отправка сообщений и передача файлов Telegram.</small></span><input type="checkbox" checked={form.proxyEnabled} onChange={event => setForm(value => ({ ...value, proxyEnabled: event.target.checked }))} /></label>
      <div className={`telegram-proxy-grid ${form.proxyEnabled ? '' : 'disabled'}`}>
        <label>Тип прокси<select disabled={!form.proxyEnabled} value={form.proxyProtocol} onChange={event => setForm(value => ({ ...value, proxyProtocol: event.target.value as 'HTTP' | 'HTTPS' | 'SOCKS5', proxyPort: event.target.value === 'SOCKS5' && value.proxyPort === 8080 ? 1080 : value.proxyPort }))}><option value="SOCKS5">SOCKS5</option><option value="HTTP">HTTP</option><option value="HTTPS">HTTPS</option></select></label>
        <label>Адрес прокси<input disabled={!form.proxyEnabled} value={form.proxyHost} onChange={event => setForm(value => ({ ...value, proxyHost: event.target.value }))} placeholder="proxy.example.ru или 10.0.0.5" /></label>
        <label>Порт<input disabled={!form.proxyEnabled} type="number" min={1} max={65535} value={form.proxyPort} onChange={event => setForm(value => ({ ...value, proxyPort: Number(event.target.value) }))} /></label>
        <label>Логин<input disabled={!form.proxyEnabled} value={form.proxyUsername} onChange={event => setForm(value => ({ ...value, proxyUsername: event.target.value }))} placeholder="Необязательно" /></label>
        <label>Пароль<input disabled={!form.proxyEnabled} type="password" value={form.proxyPassword} onChange={event => setForm(value => ({ ...value, proxyPassword: event.target.value, clearProxyPassword: false }))} placeholder={data.hasProxyPassword ? 'Пароль сохранён. Введите новый для замены.' : 'Необязательно'} /></label>
        {data.hasProxyPassword && <label className="switch-setting compact"><span><b>Удалить сохранённый пароль</b><small>Применится после сохранения.</small></span><input disabled={!form.proxyEnabled} type="checkbox" checked={form.clearProxyPassword} onChange={event => setForm(value => ({ ...value, clearProxyPassword: event.target.checked, proxyPassword: event.target.checked ? '' : value.proxyPassword }))} /></label>}
      </div>
      <div className="telegram-large-files">
        <div className="surface-head"><div><h2>Большие файлы Telegram</h2><p>Файлы сохраняются на сервере портала в MinIO и после этого открываются, просматриваются и скачиваются без повторного обращения к Telegram.</p></div><span className={`ad-connection-badge ${form.mtprotoEnabled ? 'connected' : ''}`}>{form.mtprotoEnabled ? 'Включено' : 'Выключено'}</span></div>
        <label className="switch-setting"><span><b>Загружать большие файлы через MTProto</b><small>Нужно для файлов, которые облачный Bot API не отдаёт с ошибкой «file is too big». API ID и API Hash создаются в кабинете Telegram.</small></span><input type="checkbox" checked={form.mtprotoEnabled} onChange={event => setForm(value => ({ ...value, mtprotoEnabled: event.target.checked }))} /></label>
        <div className={`telegram-mtproto-grid ${form.mtprotoEnabled ? '' : 'disabled'}`}>
          <label>API ID<input disabled={!form.mtprotoEnabled} type="number" min={1} value={form.mtprotoApiId || ''} onChange={event => setForm(value => ({ ...value, mtprotoApiId: Number(event.target.value) || 0 }))} placeholder="12345678" /></label>
          <label>API Hash<input disabled={!form.mtprotoEnabled} type="password" value={form.mtprotoApiHash} onChange={event => setForm(value => ({ ...value, mtprotoApiHash: event.target.value, clearMtprotoApiHash: false }))} placeholder={data.hasMtprotoApiHash ? 'API Hash сохранён. Введите новый только для замены.' : 'Введите API Hash'} /></label>
          {data.hasMtprotoApiHash && <label className="switch-setting compact"><span><b>Удалить сохранённый API Hash</b><small>Применится после сохранения.</small></span><input disabled={!form.mtprotoEnabled} type="checkbox" checked={form.clearMtprotoApiHash} onChange={event => setForm(value => ({ ...value, clearMtprotoApiHash: event.target.checked, mtprotoApiHash: event.target.checked ? '' : value.mtprotoApiHash }))} /></label>}
        </div>
        <div className="info-note"><ShieldCheck /><span><b>Файл сохраняется один раз</b><small>Новые вложения сохраняются при получении. Старое вложение без локальной копии будет скачано и сохранено при первом нажатии «Открыть» или «Скачать».</small></span></div>
      </div>
      <div className="integration-actions telegram-actions">
        <button className="button primary" type="button" onClick={() => save.mutate()} disabled={save.isPending}><Save /> {save.isPending ? 'Сохранение…' : 'Сохранить Telegram'}</button>
        <button className="button ghost" type="button" onClick={() => test.mutate()} disabled={test.isPending || !data.hasBotToken}>{test.isPending ? 'Проверка…' : 'Проверить подключение'}</button>
        <button className="button ghost" type="button" onClick={() => startPolling.mutate()} disabled={startPolling.isPending || !form.botEnabled || !data.hasBotToken}>{startPolling.isPending ? 'Запуск…' : 'Запустить приём сообщений'}</button>
      </div>
    </section>

    <section className="surface max-webhook-card telegram-polling-card">
      <div className="surface-head"><div><h2>Приём сообщений</h2><p>Режим Long Polling. Scheduler проверяет Telegram каждые 5 секунд и передаёт события во внутренний API портала.</p></div><button className="button ghost" onClick={() => rotate.mutate()} disabled={rotate.isPending}><RefreshCw /> Новый внутренний секрет</button></div>
      <label>Режим<code>Long Polling</code></label>
      <label>Подключение<code>{data.proxyEnabled ? `${data.proxyProtocol} · ${data.proxyHost}:${data.proxyPort || '—'}` : 'Напрямую'}</code></label>
      <label>Последняя проверка<code>{data.pollingLastAt ? formatDateTime(data.pollingLastAt) : 'Пока не было'}</code></label>
      <p>{data.lastTestMessage || 'Сохраните токен и параметры прокси, затем проверьте подключение.'}{data.pollingLastError ? ` Ошибка: ${data.pollingLastError}` : ''}</p>
    </section>

    <section className="surface max-channel-status">
      <div className="surface-head"><div><h2>Состояние канала</h2><p>Telegram Bot в Контакт-центре</p></div></div>
      <div className="max-channel-grid">{data.channels.map(channel => <div key={channel.connection_type}><b>Бот</b><span className={`event-action ${channel.status === 'ACTIVE' ? 'unban' : 'ban'}`}>{channel.status === 'ACTIVE' ? 'Активен' : channel.status === 'ERROR' ? 'Ошибка' : 'Отключён'}</span><small>{channel.last_event_at ? `Последнее сообщение: ${formatDateTime(channel.last_event_at)}` : 'Сообщений ещё не было'}{channel.last_error ? ` · Ошибка: ${channel.last_error}` : ''}</small></div>)}</div>
    </section>
  </div>;
}

function ActiveDirectoryAdmin() {
  const queryClient = useQueryClient();
  const { data, isLoading, error: queryError } = useQuery({ queryKey: ['active-directory-settings'], queryFn: () => api<ActiveDirectorySettings>('/integrations/active-directory') });
  const [server, setServer] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [usersVisible, setUsersVisible] = useState(false);
  const [directoryUsers, setDirectoryUsers] = useState<Array<{ dn: string; login: string; upn: string; email: string; fullName: string; disabled: boolean }>>([]);
  const [syncSettings, setSyncSettings] = useState({ enabled: true, intervalMinutes: 60 });

  useEffect(() => {
    if (!data) return;
    const configured = data.serverUrls[0] || '';
    setServer(configured.replace(/^ldaps?:\/\//i, '').replace(/:(389|636)$/i, ''));
    setUsername(data.bindDn || '');
    setSyncSettings({ enabled: data.adUserSyncEnabled, intervalMinutes: data.syncIntervalMinutes || 60 });
  }, [data]);

  const connect = useMutation({
    mutationFn: () => api<{ success: boolean; serverUrl: string; transport: string; bindIdentity: string; baseDn: string; domainName: string; dnsDomain: string; users: Array<{ dn: string; login: string; upn: string; email: string; fullName: string; disabled: boolean }> }>('/integrations/active-directory/connect', {
      method: 'POST',
      body: JSON.stringify({ server: server.trim(), username: username.trim(), password }),
    }),
    onSuccess: result => {
      setError('');
      setPassword('');
      setDirectoryUsers(result.users);
      setUsersVisible(true);
      setFeedback(`Active Directory подключен через ${result.transport}. Домен: ${result.dnsDomain || result.domainName}. Найдено пользователей: ${result.users.length}.`);
      queryClient.invalidateQueries({ queryKey: ['active-directory-settings'] });
      queryClient.invalidateQueries({ queryKey: ['directory-public-status'] });
    },
    onError: reason => { setError(reason instanceof Error ? reason.message : 'Не удалось подключиться к Active Directory'); queryClient.invalidateQueries({ queryKey: ['active-directory-settings'] }); },
  });

  const preview = useMutation({
    mutationFn: () => api<Array<{ dn: string; login: string; upn: string; email: string; fullName: string; disabled: boolean }>>('/integrations/active-directory/preview', { method: 'POST' }),
    onSuccess: rows => { setError(''); setDirectoryUsers(rows); setUsersVisible(true); setFeedback(`Загружено пользователей Active Directory: ${rows.length}`); },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось получить пользователей Active Directory'),
  });

  const sync = useMutation({
    mutationFn: () => api<{ success: boolean; synced: number; skipped: number }>('/integrations/active-directory/sync', { method: 'POST' }),
    onSuccess: result => {
      setError('');
      setFeedback(`Пользователи загружены и обновлены из AD: ${result.synced}. Пропущено: ${result.skipped}.`);
      queryClient.invalidateQueries({ queryKey: ['active-directory-settings'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось загрузить пользователей в портал'),
  });

  const refreshExisting = useMutation({
    mutationFn: () => api<{ success: boolean; synced: number; disabled: number; skipped: number; message?: string }>('/integrations/active-directory/sync-existing', { method: 'POST' }),
    onSuccess: result => {
      setError('');
      setFeedback(result.message || `Обновлено добавленных пользователей: ${result.synced}. Отключено по AD: ${result.disabled}. Пропущено: ${result.skipped}.`);
      queryClient.invalidateQueries({ queryKey: ['active-directory-settings'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось обновить добавленных пользователей AD'),
  });

  const saveSyncSettings = useMutation({
    mutationFn: () => api<ActiveDirectorySettings>('/integrations/active-directory/sync-settings', {
      method: 'PUT',
      body: JSON.stringify(syncSettings),
    }),
    onSuccess: () => {
      setError('');
      setFeedback('Параметры автообновления AD сохранены');
      queryClient.invalidateQueries({ queryKey: ['active-directory-settings'] });
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить интервал обновления AD'),
  });

  if (isLoading) return <PageSkeleton />;
  if (!data) return <div className="admin-feedback error">{queryError instanceof Error ? queryError.message : 'Не удалось загрузить интеграцию'}</div>;

  return <div className="admin-section-stack ad-simple-page">
    <section className="surface integration-header-card">
      <div><span className="integration-icon"><Network /></span><div><h2>Active Directory</h2><p>Подключение домена и автоматическая загрузка пользователей</p></div></div>
      <span className={`ad-connection-badge ${data.lastTestOk ? 'connected' : ''}`}>{data.lastTestOk ? 'Подключено' : 'Не подключено'}</span>
    </section>

    {feedback && <div className="admin-feedback success">{feedback}</div>}
    {error && <div className="admin-feedback error">{error}</div>}

    <section className="surface ad-simple-card">
      <div className="surface-head"><div><h2>Подключение к домену</h2><p>Укажите контроллер домена и учётную запись с правом чтения каталога. Остальные параметры портал определит автоматически.</p></div></div>
      <div className="ad-simple-form">
        <label>Адрес сервера Active Directory<input value={server} onChange={event => setServer(event.target.value)} placeholder="dc01.company.local или 192.168.1.10" autoComplete="off" /></label>
        <label>Логин администратора<input value={username} onChange={event => setUsername(event.target.value)} placeholder="DOMAIN\administrator или administrator@company.local" autoComplete="username" /></label>
        <label>Пароль<input type="password" value={password} onChange={event => setPassword(event.target.value)} placeholder={data.hasBindPassword ? 'Введите пароль для повторного подключения' : 'Пароль администратора'} autoComplete="current-password" /></label>
        <button className="button primary ad-connect-button" onClick={() => connect.mutate()} disabled={connect.isPending || !server.trim() || !username.trim() || !password}><Network /> {connect.isPending ? 'Подключение…' : 'Подключить Active Directory'}</button>
      </div>
      <div className="ad-auto-note"><ShieldCheck /><span><b>Автоматическая настройка</b>Портал автоматически проверит LDAP + StartTLS 389, LDAPS 636 и обычный LDAP 389, затем определит Base DN, домен и пользователей.</span></div>
    </section>

    {data.lastTestOk && <section className="surface ad-domain-summary">
      <div><span>Сервер</span><b>{data.serverUrls[0] || '—'}</b></div>
      <div><span>Домен</span><b>{data.domainName || '—'}</b></div>
      <div><span>Base DN</span><b>{data.baseDn || '—'}</b></div>
      <div><span>Последнее обновление</span><b>{data.lastSyncAt ? formatDateTime(data.lastSyncAt) : '—'}</b></div>
    </section>}

    {data.lastTestOk && <section className="surface ad-sync-panel">
      <div className="surface-head"><div><h2>Автообновление пользователей AD</h2><p>Обновляет ФИО, телефон, email, статус активности и роли уже добавленных доменных пользователей.</p></div></div>
      <div className="ad-sync-grid">
        <label className="switch-setting"><span><b>Автообновление включено</b><small>{data.nextSyncAt ? `Следующий запуск: ${formatDateTime(data.nextSyncAt)}` : 'Автоматический запуск отключён'}</small></span><input type="checkbox" checked={syncSettings.enabled} onChange={event => setSyncSettings(value => ({ ...value, enabled: event.target.checked }))} /></label>
        <label>Интервал, минут<input type="number" min={5} max={10080} value={syncSettings.intervalMinutes} onChange={event => setSyncSettings(value => ({ ...value, intervalMinutes: Number(event.target.value) }))} /></label>
        <button className="button ghost" type="button" onClick={() => saveSyncSettings.mutate()} disabled={saveSyncSettings.isPending}><Save /> {saveSyncSettings.isPending ? 'Сохранение…' : 'Сохранить интервал'}</button>
        <button className="button primary" type="button" onClick={() => refreshExisting.mutate()} disabled={refreshExisting.isPending}><RefreshCw /> {refreshExisting.isPending ? 'Обновление…' : 'Обновить добавленных пользователей'}</button>
      </div>
      <p className="ad-sync-note">{data.lastSyncMessage || 'Пароли из AD не копируются в портал: при каждом входе пароль проверяется напрямую в Active Directory.'}</p>
    </section>}

    {data.lastTestOk && <section className="surface ad-users-toolbar">
      <div><h2>Пользователи Active Directory</h2><p>Просмотр каталога, импорт новых и обновление уже добавленных учётных записей</p></div>
      <div>
        <button className="button ghost" onClick={() => preview.mutate()} disabled={preview.isPending}><Users /> {preview.isPending ? 'Загрузка…' : 'Список пользователей AD'}</button>
        <button className="button primary" onClick={() => sync.mutate()} disabled={sync.isPending}><RefreshCw /> {sync.isPending ? 'Импорт…' : 'Импортировать и обновить всех'}</button>
      </div>
    </section>}

    {usersVisible && <section className="surface ad-directory-users">
      <div className="surface-head"><div><h2>Список пользователей AD</h2><p>Показаны первые {directoryUsers.length} найденных учётных записей</p></div><button className="icon-button" onClick={() => setUsersVisible(false)}>×</button></div>
      <div className="ad-user-table-head"><span>Логин</span><span>ФИО</span><span>Email</span><span>Состояние</span><span>Расположение в AD</span></div>
      {directoryUsers.length ? directoryUsers.map(item => <div className="ad-user-table-row" key={item.dn}>
        <b>{item.login || item.upn || '—'}</b><span>{item.fullName || '—'}</span><span>{item.email || '—'}</span><span className={`event-action ${item.disabled ? 'ban' : 'unban'}`}>{item.disabled ? 'Отключён' : 'Активен'}</span><span title={item.dn}>{item.dn}</span>
      </div>) : <EmptyState compact icon={<Users />} title="Пользователи не найдены" text="Проверьте права учётной записи на чтение каталога." />}
    </section>}

    <button className="ad-advanced-toggle" type="button" onClick={() => setShowAdvanced(value => !value)}>{showAdvanced ? 'Скрыть технические параметры' : 'Показать технические параметры'}</button>
    {showAdvanced && <section className="surface ad-advanced-summary">
      <div><span>LDAP URL</span><code>{data.serverUrls.join(', ') || '—'}</code></div>
      <div><span>Сервисная учётная запись</span><code>{data.bindDn || '—'}</code></div>
      <div><span>Фильтр пользователей</span><code>{data.userFilter || '—'}</code></div>
      <div><span>Шифрование</span><code>{data.serverUrls[0]?.startsWith('ldaps://') ? 'LDAPS' : data.startTls ? 'LDAP + StartTLS' : 'LDAP'}</code></div>
      <div><span>Последняя диагностика</span><code>{data.lastTestMessage || '—'}</code></div>
      <div><span>Автообновление</span><code>{data.adUserSyncEnabled ? `Каждые ${data.syncIntervalMinutes} мин.` : 'Отключено'}</code></div>
      <p>Расширенные значения определяются автоматически и доступны здесь только для диагностики.</p>
    </section>}
  </div>;
}

function QueuesAdmin() {
  return <QueueRoutingAdmin />;
}

function EmptyState({ icon, title, text, compact = false }: { icon: ReactNode; title: string; text: string; compact?: boolean }) {
  return <div className={`empty-state ${compact ? 'compact' : ''}`}><span>{icon}</span><b>{title}</b><p>{text}</p></div>;
}

function PageSkeleton() { return <div className="page-skeleton"><span /><span /><span /><span /></div>; }
function TableSkeleton() { return <div className="table-skeleton">{Array.from({ length: 5 }, (_, index) => <span key={index} />)}</div>; }

export function App({ uiShellVersion = 'legacy' }: { uiShellVersion?: UiShellVersion }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="screen-loader"><span /></div>;
  return <SipPhoneProvider key={user?.id ?? 'anonymous'} enabled={user?.userType === 'EMPLOYEE'}><Routes><Route path="/login" element={<Login />} /><Route path="/*" element={user ? <Layout uiShellVersion={uiShellVersion} /> : <Navigate to="/login" replace />} /></Routes></SipPhoneProvider>;
}
